import React, { useState, useEffect } from 'react';
import { X, Download, Plus, BarChart3, Users, Film, Trash2, ChevronDown, ChevronUp, Heart, Home, Gift, Star, MessageSquare, Calendar, ArrowLeft, HeartHandshake, Search, Eye, Send, Play, Edit2, RefreshCw, Copy, Check, Droplets, BookOpen, Trophy } from 'lucide-react';
import { PrimaryButton, VideoPlayer } from './ui';
import { CalendarView } from './CalendarView';
import { UpcomingActivities } from './UpcomingActivities';
import { ScheduleActivityModal } from './ScheduleActivityModal';
import { CATEGORIES, COLORS, TAGS, getTriggeredQuestions } from '@/data';
import { HIS_SIZE_SCALE, HIS_GIRTH_SCALE, HIS_CUM_SCALE, HIS_CONSISTENCY_SCALE, HIS_TASTE_SCALE, HIS_PRECUM_SCALE, HER_WETNESS_SCALE, HER_SQUIRT_SCALE, HER_SENSITIVITY_SCALE, HER_ORGASM_EASE_SCALE } from '@/data/profile-scales';
import { generateId, now, generateCoupleCode, generateAccessCode } from '@/lib/utils';
import { saveCoupleByCode } from '@/lib/storage';
import { d1List, d1Upsert, d1Delete, d1DeleteWhere, d1BatchUpsert } from '@/lib/d1-storage';
import { getClipCategoryIds } from '@/types';
import type { Clip, Creator, Reviewer, SubmittedClip, SubmittedClipReview, CoupleRecommendation, ScheduledActivity, ActivityActor, CoupleData, ClipView, WishlistItem, ReviewerFollowUp, CoupleFollowUp, CoupleQuestion, TagTutorial, ResearchQuestionTemplate, UserPresence, SuggestedEdit, ClipSuggestionRequest, ClipSuggestionItem, FemaleSurveyConfig, CoupleRequest, CoupleTabData, ConsolidatedQAViewData, AdminPeople, TriedClip } from '@/types';
import { TAG_TUTORIAL_DEFINITIONS } from '@/data/tag-tutorials';
import { ConsolidatedQAView } from './ConsolidatedQAView';
import { PeopleOverview } from './admin/PeopleOverview';
import { CoupleRecord } from './admin/CoupleRecord';
import { SubmissionsBoard } from './admin/SubmissionsBoard';
import { fetchAdminPeople } from '@/lib/admin-api';
import { CopyLinkButton, reviewerLoginLink } from '@/components/CopyLinkButton';

// Admin password - in production, this should come from environment variables
const ADMIN_PASSWORD = import.meta.env.VITE_ADMIN_PASSWORD || 'VBHealth2024';

interface AdminModalProps {
  clips: SubmittedClip[];
  creators: Creator[];
  reviewers: Reviewer[];
  dateNiteClips: Clip[];
  saveDateNiteClips: (clips: Clip[]) => Promise<void>;
  auth: boolean;
  setAuth: (auth: boolean) => void;
  onClose: () => void;
  // Clip view tracking
  clipViews?: ClipView[];
  // Couple wishlist
  wishlist?: WishlistItem[];
  // Reviewer follow-ups
  reviewerFollowUps?: ReviewerFollowUp[];
  saveReviewerFollowUps?: (followUps: ReviewerFollowUp[]) => Promise<void>;
  // Couple follow-ups (AI questions to couples)
  coupleFollowUps?: CoupleFollowUp[];
  saveCoupleFollowUps?: (followUps: CoupleFollowUp[]) => Promise<void>;
  // Couple questions (from reviewers)
  coupleQuestions?: CoupleQuestion[];
  saveCoupleQuestions?: (questions: CoupleQuestion[]) => Promise<void>;
  // Tag tutorials
  tagTutorials?: TagTutorial[];
  saveTagTutorials?: (tutorials: TagTutorial[]) => Promise<void>;
  // Suggested clip edits (from reviewers, pending approval)
  suggestedEdits?: SuggestedEdit[];
  saveSuggestedEdits?: (edits: SuggestedEdit[]) => Promise<void>;
  // Clip suggestions (curated clips from admin to couples for rating)
  clipSuggestions?: ClipSuggestionRequest[];
  saveClipSuggestions?: (suggestions: ClipSuggestionRequest[]) => Promise<void>;
  // Refresh data
  onRefresh?: () => Promise<void>;
  // Save functions for submissions and reviewers
  saveClips?: (clips: SubmittedClip[]) => Promise<void>;
  saveReviewers?: (reviewers: Reviewer[]) => Promise<void>;
}

export const AdminModal: React.FC<AdminModalProps> = ({
  clips,
  creators,
  reviewers,
  dateNiteClips,
  saveDateNiteClips,
  auth,
  setAuth,
  onClose,
  clipViews = [],
  wishlist = [],
  reviewerFollowUps = [],
  saveReviewerFollowUps,
  coupleFollowUps = [],
  saveCoupleFollowUps,
  coupleQuestions = [],
  saveCoupleQuestions,
  tagTutorials = [],
  saveTagTutorials,
  suggestedEdits = [],
  saveSuggestedEdits,
  clipSuggestions = [],
  saveClipSuggestions,
  onRefresh,
  saveClips,
  saveReviewers
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [tab, setTab] = useState<'people' | 'stats' | 'clips' | 'addclip' | 'reviewers' | 'couples' | 'questions' | 'edits' | 'survey' | 'submissions' | 'tools'>('people');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [expandedReviewer, setExpandedReviewer] = useState<string | null>(null);
  const [previewClip, setPreviewClip] = useState<{ title: string; link: string } | null>(null);

  // Edit clip state
  const [editingClip, setEditingClip] = useState<Clip | null>(null);
  const [editClipForm, setEditClipForm] = useState<{ categoryIds: string[]; tags: string[]; title: string; description: string; link: string }>({
    categoryIds: [],
    tags: [],
    title: '',
    description: '',
    link: ''
  });
  const [reviewerRecommendations, setReviewerRecommendations] = useState<Record<string, CoupleRecommendation[]>>({});

  // Couple schedule management state
  const [selectedCoupleSchedule, setSelectedCoupleSchedule] = useState<{ coupleId: string; partnerName?: string; reviewerId?: string } | null>(null);
  const [coupleScheduledActivities, setCoupleScheduledActivities] = useState<ScheduledActivity[]>([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | undefined>();
  const [rescheduleActivityId, setRescheduleActivityId] = useState<string | null>(null);

  // All couples from global storage (not just from reviewers)
  const [allCouples, setAllCouples] = useState<CoupleData[]>([]);
  // Access code index (maps VBA codes to VBC coupleIds) - for reverse lookup
  const [accessCodeIndex, setAccessCodeIndex] = useState<Record<string, string>>({});

  // Expanded couple for detailed view (like reviewer expansion)
  const [expandedCouple, setExpandedCouple] = useState<string | null>(null);

  // Consolidated Q&A slide-over
  const [consolidatedQAReviewerId, setConsolidatedQAReviewerId] = useState<string | null>(null);

  // Couple requests per couple (for clip feedback badge)
  const [allCoupleRequests, setAllCoupleRequests] = useState<Record<string, CoupleRequest[]>>({});

  const [toolConfirm, setToolConfirm] = useState<string | null>(null);
  const [toolMessage, setToolMessage] = useState<string | null>(null);

  // Duplicate couple result modal
  const [duplicatedCoupleResult, setDuplicatedCoupleResult] = useState<{ coupleId: string; accessCode: string; hisName: string; herName: string } | null>(null);
  // Tab state for couple cards: inbox | activity | profile
  const [coupleCardTab, setCoupleCardTab] = useState<Record<string, 'inbox' | 'activity' | 'profile'>>({});

  // Online presence tracking
  const [userPresences, setUserPresences] = useState<UserPresence[]>([]);

  // Clip search for admin recommendations
  const [clipSearchTerm, setClipSearchTerm] = useState('');
  const [clipCategoryFilter, setClipCategoryFilter] = useState('all');

  const getSubmittedClipReviews = (submission: SubmittedClip): SubmittedClipReview[] => {
    const reviews = [...(submission.reviews || [])];
    if (submission.review && !reviews.some(r => r.reviewerId === submission.review!.reviewerId)) {
      reviews.push(submission.review);
    }
    return reviews;
  };

  const submittedReviewBelongsToReviewer = (review: SubmittedClipReview | undefined, reviewer: Reviewer) => {
    if (!review?.reviewerId) return false;
    return review.reviewerId === reviewer.id || review.reviewerId === reviewer.code;
  };

  // Custom follow-up question form
  const [askingFollowUp, setAskingFollowUp] = useState<string | null>(null); // reviewer ID
  const [newFollowUpQuestion, setNewFollowUpQuestion] = useState('');
  const [replyingToQuestionId, setReplyingToQuestionId] = useState<string | null>(null); // parentId for threading
  const [replyingToCoupleQuestionId, setReplyingToCoupleQuestionId] = useState<string | null>(null); // parentId for couple Q&A threading
  const [newCoupleQuestionReply, setNewCoupleQuestionReply] = useState('');
  const [replyingToCoupleFollowUpId, setReplyingToCoupleFollowUpId] = useState<string | null>(null); // parentId for couple follow-up threading
  const [newCoupleFollowUpReply, setNewCoupleFollowUpReply] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; type: 'reviewer' | 'couple' } | null>(null);
  // Inline follow-up from submission review cards
  const [submissionFollowUp, setSubmissionFollowUp] = useState<{ reviewerId: string; subId: string; clipIds?: string[] } | null>(null);
  const [submissionFollowUpText, setSubmissionFollowUpText] = useState('');
  // Inline follow-up from DateNite clip review cards
  const [clipReviewFollowUp, setClipReviewFollowUp] = useState<{ reviewerId: string; clipId: string; extraClipIds?: string[] } | null>(null);
  const [clipReviewFollowUpText, setClipReviewFollowUpText] = useState('');
  // Quick-view clip from matchup history
  const [matchupPreviewClip, setMatchupPreviewClip] = useState<string | null>(null);
  // Sent confirmation flash
  const [followUpSentFlash, setFollowUpSentFlash] = useState<string | null>(null); // shows subId or clipId briefly

  // Clip picker for Q&A replies (supports multiple clips)
  const [replyClipIds, setReplyClipIds] = useState<string[]>([]);
  const [showReplyClipPicker, setShowReplyClipPicker] = useState(false);
  const [replyClipSearch, setReplyClipSearch] = useState('');

  // Edit Q&A state
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [editQuestionText, setEditQuestionText] = useState('');
  const [editQuestionClipIds, setEditQuestionClipIds] = useState<string[]>([]);

  // Clip suggestion request form (curated clips for couples/reviewers to rate)
  const [creatingClipSuggestion, setCreatingClipSuggestion] = useState<{ coupleId?: string; reviewerId?: string } | null>(null);
  const [editingClipSuggestionId, setEditingClipSuggestionId] = useState<string | null>(null);
  const [clipSuggestionContext, setClipSuggestionContext] = useState('');
  const [clipSuggestionItems, setClipSuggestionItems] = useState<ClipSuggestionItem[]>([]);
  const [newClipUrl, setNewClipUrl] = useState('');
  const [newClipTitle, setNewClipTitle] = useState('');
  const [clipSuggestionLinkedQuestion, setClipSuggestionLinkedQuestion] = useState<string | null>(null); // links to Q&A thread

  // Expanded section states for reviewer detail
  const [expandedWatchHistory, setExpandedWatchHistory] = useState<string | null>(null);
  const [expandedReviews, setExpandedReviews] = useState<string | null>(null);
  const [expandedWishlist, setExpandedWishlist] = useState<string | null>(null);
  const [expandedSchedule, setExpandedSchedule] = useState<string | null>(null);

  // Reviewer detail tabs (for future use)
  const [_reviewerDetailTab, _setReviewerDetailTab] = useState<Record<string, 'stats' | 'questions' | 'couple' | 'profile' | 'activity'>>({});

  // Solo Q&A generator
  const [soloQuestions, setSoloQuestions] = useState<{ reviewerId: string; questions: string[] } | null>(null);
  const [soloQuestionsEditing, setSoloQuestionsEditing] = useState<string[]>([]);

  // Confirmation before sending follow-up (supports queue of multiple questions)
  const [pendingFollowUp, setPendingFollowUp] = useState<{
    reviewerId: string;
    reviewerName: string;
    questions: { question: string; context?: string }[]; // Queue of questions to send
  } | null>(null);

  // Confirmation before sending couple follow-up (same pattern as reviewer)
  const [pendingCoupleFollowUp, setPendingCoupleFollowUp] = useState<{
    coupleId: string;
    coupleName: string;
    questions: { question: string; context?: string; clipId?: string }[];
  } | null>(null);

  // Research question templates (editable)
  const [researchQuestions, setResearchQuestions] = useState<ResearchQuestionTemplate[]>([]);
  const [editingQuestion, setEditingQuestion] = useState<ResearchQuestionTemplate | null>(null);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);

  // Suggested edit rejection modal
  const [rejectingEdit, setRejectingEdit] = useState<SuggestedEdit | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Female survey config
  const [femaleSurveyConfig, setFemaleSurveyConfig] = useState<FemaleSurveyConfig>({
    wetness: [
      { level: 1, label: 'Light moisture', description: 'Minimal visible wetness, needs lube for comfort' },
      { level: 2, label: 'Naturally wet', description: 'Some glistening, comfortable without lube' },
      { level: 3, label: 'Very wet', description: 'Visible wetness, some stringing between fingers' },
      { level: 4, label: 'Dripping', description: 'Wetness running down, very slick' },
      { level: 5, label: 'Soaking', description: 'Puddle-forming wet, extremely slick' }
    ],
    squirting: [
      { level: 0, label: 'Never squirted', description: "Haven't experienced squirting yet" },
      { level: 1, label: 'Small release', description: 'Slight trickle or dribble when orgasming' },
      { level: 2, label: 'Modest squirt', description: 'Short spray, noticeable but contained' },
      { level: 3, label: 'Good squirt', description: 'Clear projection, needs a towel' },
      { level: 4, label: 'Strong squirter', description: 'Multiple bursts, significant volume' },
      { level: 5, label: 'Gushing', description: 'Full fountain, soaking the bed' }
    ]
  });

  // Default research questions (used for seeding)
  const DEFAULT_RESEARCH_QUESTIONS: Omit<ResearchQuestionTemplate, 'id' | 'createdAt'>[] = [
    {
      label: 'Group content (MFM/gangbang)',
      question: "We've been getting feedback about group content - specifically multiple men with one woman (MFM, gangbang scenarios). Is this something you've fantasized about or explored? Have you experienced anything like this? If yes to either, we'd love to hear more details. And would you be interested in seeing this type of content? If so, what specifically would appeal to you?",
      context: 'Research: Group content interest',
      active: true
    },
    {
      label: 'Cum volume & finish preferences',
      question: "We're curious about preferences around cum volume and finishing. Does a bigger load make the experience better for you? Where do you (or your partner) prefer to finish - inside, on body, face, etc? Has this preference changed over time?",
      context: 'Research: Finish preferences',
      active: true
    },
    {
      label: 'Missing content types',
      question: "Is there any type of content you've been looking for but haven't found in our library? Any specific scenarios, positions, or dynamics you'd love to see more of?",
      context: 'Research: Content gaps',
      active: true
    },
    {
      label: 'Product experience & curiosity',
      question: "Have you or your partner tried any VB products (Load Boost, Soaking Wet)? If yes, what was your experience? If not, what would make you want to try them?",
      context: 'Research: Product feedback',
      active: true
    },
    {
      label: 'Roleplay & scenarios',
      question: "Are you into roleplay or specific scenarios during intimacy? Things like stranger fantasy, power dynamics, specific settings? What scenarios turn you on most?",
      context: 'Research: Roleplay interest',
      active: true
    },
    {
      label: 'Boundaries & comfort zones',
      question: "Everyone has different comfort zones. Are there things you're curious about but haven't tried yet? What would help you feel more comfortable exploring new things with your partner?",
      context: 'Research: Boundaries exploration',
      active: true
    },
    {
      label: 'Naughtiest fantasy',
      question: "Let's go there... What's your naughtiest fantasy? The one that really gets you going when you think about it. No judgment here - we want to understand what truly excites people so we can find or create content that hits the spot.",
      context: 'Research: Naughtiest fantasy',
      active: true
    },
    {
      label: 'Naughtiest thing done',
      question: "What's the naughtiest or wildest thing you've actually done sexually? Could be with your current partner, a past experience, a one-time thing... We'd love to hear the details if you're comfortable sharing!",
      context: 'Research: Naughtiest experience',
      active: true
    },
    {
      label: 'Handjob preferences',
      question: "Let's talk handjobs - do you give them? Do you enjoy receiving them? What makes a handjob really good vs just okay? Any specific techniques that drive you (or your partner) wild?",
      context: 'Research: Handjob preferences',
      active: true
    },
    {
      label: 'Solo play habits',
      question: "We'd love to know about your solo play habits. Do you masturbate? How often? What's your go-to method or technique? Do you use toys or just hands? What do you typically think about or watch when you do?",
      context: 'Research: Masturbation habits',
      active: true
    },
    {
      label: 'Porn consumption',
      question: "What's your relationship with porn? What types do you watch most? How often? Do you watch alone or with a partner? Any specific categories or scenarios that always get you going? Anything you avoid?",
      context: 'Research: Porn preferences',
      active: true
    },
    {
      label: 'Clips that made you act',
      question: "Have any of our clips (or other content you've watched) ever turned you on so much that you had to do something about it right then? If so, which ones and what happened? Did you stop to handle it yourself, initiate with your partner, or something else?",
      context: 'Research: Content impact',
      active: true
    },
    {
      label: 'Blowjob experience',
      question: "Let's talk oral - if you give blowjobs, what do you enjoy about it? What makes it hot for you? Any techniques you love or things partners have done that drive you crazy? If you receive, same questions - what makes a great blowjob?",
      context: 'Research: Oral preferences',
      active: true
    },
    {
      label: 'Toys & accessories',
      question: "Do you use any toys or accessories during sex or solo play? Vibrators, dildos, cock rings, restraints, etc? What's your favorite and why? Anything you've been curious to try?",
      context: 'Research: Toy usage',
      active: true
    },
    {
      label: 'Dirty talk & sexting',
      question: "Are you into dirty talk? What kind of things do you like to say or hear during sex? Do you sext with your partner? What about phone sex or video calls - have you done those, and do you enjoy them?",
      context: 'Research: Verbal intimacy',
      active: true
    },
    {
      label: 'Sexual frequency & drive',
      question: "How often do you have sex? How does that compare to how often you'd ideally like to? Is your drive higher or lower than your partner's? How do you handle mismatched libido?",
      context: 'Research: Sexual frequency',
      active: true
    },
    {
      label: 'Cum on body experiences',
      question: "Has your partner cum on your breasts, stomach, back, or other body parts? How do you feel about it - do you enjoy it, find it hot, or not your thing? Any specific spots you love or prefer to avoid?",
      context: 'Research: Cum placement',
      active: true
    },
    {
      label: 'Cum play & snowballing',
      question: "Let's talk cum play - have you ever spit cum into your partner's mouth, or had them lick it off you? Would you be into that? What about 'snowballing' (passing cum back and forth)? Have you done it or fantasized about it?",
      context: 'Research: Cum play/snowballing',
      active: true
    },
    {
      label: 'Cum feeding & sharing',
      question: "Some couples enjoy 'feeding' cum - having him lick his own cum off you, or you feeding it to him from your fingers or mouth. Is this something you've tried or would try? What's the appeal (or turn-off) for you?",
      context: 'Research: Cum feeding',
      active: true
    },
    {
      label: 'Taste & swallowing details',
      question: "If you swallow, what's your honest take on the taste? Does it vary? Have you noticed differences based on diet, hydration, or supplements? Any tips you'd share? If you don't swallow, is taste the reason or something else?",
      context: 'Research: Taste/swallowing details',
      active: true
    },
    {
      label: 'Creampie preferences',
      question: "How do you feel about creampies (finishing inside)? Is it something you enjoy, prefer, or avoid? Do you like the feeling of it? What about watching it come out after - hot or not your thing?",
      context: 'Research: Creampie preferences',
      active: true
    },
    {
      label: 'Precum - general',
      question: "Let's talk about precum - does your partner produce much? Do you notice it during foreplay or when he's turned on? How do you feel about it - do you find it hot, useful for lubrication, or not something you pay attention to?",
      context: 'Research: Precum general',
      active: true
    },
    {
      label: 'Precum - taste & play',
      question: "Have you tasted your partner's precum? How does it compare to his cum? Do you ever lick it off during foreplay or use it in any way? Is precum something that turns you on visually or not really?",
      context: 'Research: Precum taste',
      active: true
    },
    {
      label: 'Precum - amount',
      question: "Some guys produce a lot of precum, others barely any. Where does your partner fall? Would you prefer more or less? Have you noticed it changing based on how turned on he is or how long you've been teasing?",
      context: 'Research: Precum amount',
      active: true
    },
    {
      label: 'Precum - in content',
      question: "In adult content, do you notice or enjoy seeing precum? Is it something that adds to clips for you, or not something you pay attention to? Would you want to see more clips featuring visible precum/dripping?",
      context: 'Research: Precum in content',
      active: true
    },
    {
      label: 'Public sex experiences',
      question: "Have you ever had sex or done sexual things in public or semi-public places? Car, park, beach, bathroom, etc? What happened and how did it feel? Is the risk of getting caught exciting or anxiety-inducing?",
      context: 'Research: Public sex',
      active: true
    },
    {
      label: 'Outdoor sex preferences',
      question: "What about outdoor sex specifically - backyard, camping, nature, balcony? Have you done it? Is fresh air and nature a turn-on, or do you prefer the comfort of indoors? Any memorable outdoor experiences?",
      context: 'Research: Outdoor sex',
      active: true
    },
    {
      label: 'Female POV swallowing',
      question: "We have a new clip showing the female POV of a swallowing scene (her perspective looking up). Do you like it? Did it turn you on? As a woman, is this angle hotter for you than the typical male POV? Would you like to see more content from this perspective?",
      context: 'Research: Female POV content',
      active: true
    },
    {
      label: 'POV preferences',
      question: "When watching adult content, which POV (point of view) do you prefer? Male POV, female POV, third-person/voyeur view? Does it depend on the act? What makes a certain angle hotter for you?",
      context: 'Research: POV preferences',
      active: true
    },
    {
      label: 'Content that made you act',
      question: "Have you ever watched a clip that turned you on so much you had to pause and do something about it? What was it about that clip? Did you finish yourself, initiate with your partner, or something else? We'd love to know what content really hits different.",
      context: 'Research: High-impact content',
      active: true
    },
    {
      label: 'Anal exploration',
      question: "Have you explored anal play together? What's been your experience - giving, receiving, or both? What makes it enjoyable or challenging? Is it something you'd want more of, or not really your thing?",
      context: 'Research: Anal play',
      active: true
    },
    {
      label: 'Power dynamics (Dom/Sub)',
      question: "Do either of you enjoy being dominant or submissive during intimacy? How do power dynamics play into your sex life? Is one of you naturally more dominant? Have you experimented with role reversal, restraints, or control play?",
      context: 'Research: Power dynamics',
      active: true
    },
    {
      label: 'Aftercare & aftermath',
      question: "What does aftercare look like for you two after sex? How do you handle cleanup and connection? Do you have rituals - cuddling, shower together, pillow talk? Is aftercare something you prioritize or does it just happen naturally?",
      context: 'Research: Aftercare practices',
      active: true
    }
  ];

  // Load research questions from storage (or seed defaults)
  // Runs on mount AND whenever modal opens (tab changes trigger re-render)
  useEffect(() => {
    const loadResearchQuestions = async () => {
      try {
        const stored = await d1List<ResearchQuestionTemplate>('research_questions');

        if (stored.length > 0) {
          // Check for new defaults that aren't in stored (by label)
          const storedLabels = stored.map(q => q.label.toLowerCase());
          const missingDefaults = DEFAULT_RESEARCH_QUESTIONS.filter(
            dq => !storedLabels.includes(dq.label.toLowerCase())
          );

          if (missingDefaults.length > 0) {
            console.log(`Adding ${missingDefaults.length} new research questions:`, missingDefaults.map(d => d.label));
            const newQuestions: ResearchQuestionTemplate[] = missingDefaults.map((q, i) => ({
              ...q,
              id: `rq-${Date.now()}-${i}`,
              createdAt: now()
            }));
            await d1BatchUpsert('research_questions', newQuestions);
            setResearchQuestions([...stored, ...newQuestions]);
          } else {
            setResearchQuestions(stored);
          }
        } else {
          // Seed with defaults
          const seeded: ResearchQuestionTemplate[] = DEFAULT_RESEARCH_QUESTIONS.map((q, i) => ({
            ...q,
            id: `rq-${Date.now()}-${i}`,
            createdAt: now()
          }));
          await d1BatchUpsert('research_questions', seeded);
          setResearchQuestions(seeded);
        }
      } catch {
        setResearchQuestions([]);
      }
    };
    if (auth) loadResearchQuestions();
  }, [auth]);

  // Save research questions
  const saveResearchQuestions = async (questions: ResearchQuestionTemplate[]) => {
    // Diff: find changed items and upsert them
    for (const q of questions) {
      const prev = researchQuestions.find(p => p.id === q.id);
      if (!prev || JSON.stringify(prev) !== JSON.stringify(q)) {
        await d1Upsert('research_questions', q);
      }
    }
    // Delete removed questions
    for (const prev of researchQuestions) {
      if (!questions.some(q => q.id === prev.id)) {
        await d1Delete('research_questions', prev.id);
      }
    }
    setResearchQuestions(questions);
  };

  // Load female survey config from storage
  useEffect(() => {
    const loadFemaleSurveyConfig = async () => {
      try {
        const configs = await d1List<FemaleSurveyConfig>('female_survey_config');
        if (configs.length > 0 && configs[0]) {
          setFemaleSurveyConfig(configs[0]);
        }
      } catch {
        // Keep defaults
      }
    };
    if (auth) loadFemaleSurveyConfig();
  }, [auth]);

  // Save female survey config
  const saveFemaleSurveyConfig = async (config: FemaleSurveyConfig) => {
    const updated = { ...config, updatedAt: now() };
    await d1Upsert('female_survey_config', updated);
    setFemaleSurveyConfig(updated);
  };

  // Load and refresh user presences (online status)
  useEffect(() => {
    const loadPresences = async () => {
      try {
        const presences = await d1List<UserPresence>('user_presence');
        // Filter to only show active in last 2 minutes
        const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000).toISOString();
        setUserPresences(presences.filter(p => p.lastSeen > twoMinutesAgo));
      } catch {
        setUserPresences([]);
      }
    };

    if (auth) {
      loadPresences();
      // Refresh every 15 seconds
      const interval = setInterval(loadPresences, 15000);
      return () => clearInterval(interval);
    }
  }, [auth]);

  // Helper to check if a user is online
  const isUserOnline = (userType: 'reviewer' | 'couple', userId: string): UserPresence | undefined => {
    return userPresences.find(p => p.userType === userType && p.userId === userId);
  };

  // People overview: one aggregated request for activity, relationships and submission counts
  const [people, setPeople] = useState<AdminPeople | null>(null);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleError, setPeopleError] = useState<string | null>(null);

  const loadPeople = async () => {
    setPeopleLoading(true);
    try {
      setPeople(await fetchAdminPeople());
      setPeopleError(null);
    } catch (err) {
      setPeopleError(err instanceof Error ? err.message : String(err));
    } finally {
      setPeopleLoading(false);
    }
  };

  useEffect(() => {
    if (!auth || !['people', 'couples', 'reviewers'].includes(tab)) return;
    loadPeople();
    if (tab !== 'people') return;
    // Skip polls while the admin tab is in the background (each poll scans the child tables)
    const interval = setInterval(() => { if (!document.hidden) loadPeople(); }, 30000);
    return () => clearInterval(interval);
  }, [auth, tab]);

  const peopleCouples = new Map((people?.couples ?? []).map(c => [c.coupleId, c]));
  const peopleReviewers = new Map((people?.reviewers ?? []).map(r => [r.id, r]));

  // Jump from the People table to the existing expandable record
  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null);
  const [recordCoupleId, setRecordCoupleId] = useState<string | null>(null);
  const recordCouple = recordCoupleId ? people?.couples.find(c => c.coupleId === recordCoupleId) : undefined;

  const openFromPeople = (target: 'couples' | 'reviewers', id: string) => {
    setTab(target);
    if (target === 'couples') setExpandedCouple(id);
    else setExpandedReviewer(id);
    setPendingScrollId(`admin-${target}-${id}`);
  };

  const formatLastActive = (iso?: string) => {
    if (!iso) return null;
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (days < 1) return 'today';
    if (days === 1) return 'yesterday';
    return `${days}d ago`;
  };

  // Load all couples from global index when viewing couples tab
  useEffect(() => {
    const loadAllCouples = async () => {
      if (tab === 'couples') {
        try {
          // Load all couples directly from D1, with their tried clips (couples rows don't carry them)
          const [coupleRows, triedRows] = await Promise.all([
            d1List<CoupleData>('couples'),
            d1List<TriedClip & { coupleId: string }>('tried_clips'),
          ]);
          const triedByCouple = new Map<string, TriedClip[]>();
          for (const t of triedRows) triedByCouple.set(t.coupleId, [...(triedByCouple.get(t.coupleId) || []), t]);
          const couplesData = coupleRows.map(c => ({ ...c, tried: triedByCouple.get(c.coupleId) || [] }));

          // Build access code index from couple data (for reverse lookup: VBA -> VBC)
          const accessIndex: Record<string, string> = {};
          for (const c of couplesData) {
            if (c.accessCode) {
              accessIndex[c.accessCode] = c.coupleId;
            }
          }
          setAccessCodeIndex(accessIndex);
          setAllCouples(couplesData);
        } catch {
          setAllCouples([]);
        }
      }
    };
    loadAllCouples();
  }, [tab]);

  // Finish a People → record jump once the target card exists
  useEffect(() => {
    if (!pendingScrollId) return;
    const el = document.getElementById(pendingScrollId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setPendingScrollId(null);
  }, [pendingScrollId, tab, allCouples, reviewers]);

  // Load couple requests for all couples (for pending clip badge)
  useEffect(() => {
    const loadCoupleRequests = async () => {
      if (allCouples.length === 0) return;
      const result: Record<string, CoupleRequest[]> = {};
      for (const couple of allCouples) {
        try {
          const reqs = await d1List<CoupleRequest>('couple_requests', { coupleId: couple.coupleId });
          if (reqs.length > 0) {
            result[couple.coupleId] = reqs;
          }
        } catch {
          // Skip couples that fail to load
        }
      }
      setAllCoupleRequests(result);
    };
    loadCoupleRequests();
  }, [allCouples]);

  // Load recommendations for a reviewer when expanded
  useEffect(() => {
    const loadRecommendations = async () => {
      if (expandedReviewer) {
        const reviewer = reviewers.find(r => r.id === expandedReviewer);
        if (reviewer?.coupleId && !reviewerRecommendations[expandedReviewer]) {
          try {
            const recs = await d1List<CoupleRecommendation>('couple_recommendations', { coupleId: reviewer.coupleId });
            setReviewerRecommendations(prev => ({ ...prev, [expandedReviewer]: recs }));
          } catch {
            setReviewerRecommendations(prev => ({ ...prev, [expandedReviewer]: [] }));
          }
        }
      }
    };
    loadRecommendations();
  }, [expandedReviewer, reviewers, reviewerRecommendations]);

  // Load couple schedule when viewing
  useEffect(() => {
    const loadCoupleSchedule = async () => {
      if (selectedCoupleSchedule?.coupleId) {
        try {
          const activities = await d1List<ScheduledActivity>('scheduled_activities', { coupleId: selectedCoupleSchedule.coupleId });
          setCoupleScheduledActivities(activities);
        } catch {
          setCoupleScheduledActivities([]);
        }
      }
    };
    loadCoupleSchedule();
  }, [selectedCoupleSchedule?.coupleId]);

  // Admin context for activity modifications
  const getAdminContext = (): ActivityActor => ({
    type: 'admin',
    id: 'admin',
    name: 'Admin',
  });

  // Search clips for admin recommendations
  const searchedClips = dateNiteClips.filter(clip => {
    if (clipCategoryFilter !== 'all' && clip.categoryId !== clipCategoryFilter) return false;
    if (clipSearchTerm.trim()) {
      const term = clipSearchTerm.toLowerCase();
      const matchesTitle = clip.title.toLowerCase().includes(term);
      const matchesDescription = clip.description?.toLowerCase().includes(term);
      const matchesTags = clip.tags?.some(t => t.toLowerCase().includes(term));
      if (!matchesTitle && !matchesDescription && !matchesTags) return false;
    }
    return true;
  });

  // Save couple scheduled activities
  const saveCoupleScheduledActivities = async (activities: ScheduledActivity[]) => {
    if (!selectedCoupleSchedule?.coupleId) return;
    const activitiesWithCoupleId = activities.map(a => ({
      ...a,
      coupleId: a.coupleId || selectedCoupleSchedule.coupleId,
    }));
    const prevIds = new Set(coupleScheduledActivities.map(a => a.id));
    const nextIds = new Set(activitiesWithCoupleId.map(a => a.id));
    for (const a of activitiesWithCoupleId) {
      const prev = coupleScheduledActivities.find(p => p.id === a.id);
      if (!prev || JSON.stringify(prev) !== JSON.stringify(a)) {
        await d1Upsert('scheduled_activities', a);
      }
    }
    for (const id of prevIds) {
      if (!nextIds.has(id)) {
        await d1Delete('scheduled_activities', id);
      }
    }
    setCoupleScheduledActivities(activitiesWithCoupleId);
  };

  // Admin handlers for couple schedule management
  const handleAdminScheduleActivity = async (activity: ScheduledActivity) => {
    const activityWithCreator: ScheduledActivity = {
      ...activity,
      coupleId: activity.coupleId || selectedCoupleSchedule?.coupleId,
      createdBy: activity.createdBy || getAdminContext(),
    };
    // If rescheduling, remove the old activity first
    let baseActivities = coupleScheduledActivities;
    if (rescheduleActivityId) {
      baseActivities = coupleScheduledActivities.filter(a => a.id !== rescheduleActivityId);
      setRescheduleActivityId(null);
    }
    const updated = [...baseActivities, activityWithCreator];
    await saveCoupleScheduledActivities(updated);
    setShowScheduleModal(false);
    setSelectedCalendarDate(undefined);
  };

  const handleAdminMarkComplete = async (activityId: string, note?: string, userClipUrl?: string) => {
    const updated = coupleScheduledActivities.map(a =>
      a.id === activityId ? {
        ...a,
        completedAt: now(),
        notes: note || a.notes,
        userClipUrl: userClipUrl || a.userClipUrl,
        modifiedBy: getAdminContext(),
        modifiedAt: now()
      } : a
    );
    await saveCoupleScheduledActivities(updated);
  };

  const handleAdminDeleteActivity = async (activityId: string) => {
    const updated = coupleScheduledActivities.filter(a => a.id !== activityId);
    await saveCoupleScheduledActivities(updated);
  };

  const handleAdminRescheduleActivity = (activityId: string) => {
    // Store the ID - the old activity will be removed when admin confirms new schedule
    setRescheduleActivityId(activityId);
    setShowScheduleModal(true);
  };

  const handleAdminCalendarDateSelect = (date: Date) => {
    setSelectedCalendarDate(date);
    setShowScheduleModal(true);
  };

  // Add clip form
  const [newClip, setNewClip] = useState<{ categoryIds: string[]; title: string; link: string; description: string; tags: string[] }>({
    categoryIds: [CATEGORIES[0]?.id || ''],
    title: '',
    link: '',
    description: '',
    tags: []
  });

  const login = () => {
    if (password === ADMIN_PASSWORD) {
      setAuth(true);
      setError('');
    } else {
      setError('Incorrect');
    }
  };

  const exportData = () => {
    const data = {
      clips,
      creators,
      reviewers,
      dateNiteClips,
      exportedAt: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `vb-export-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  // Get category stats
  const getCategoryStats = () => {
    return CATEGORIES.map(cat => {
      const catClips = dateNiteClips.filter(c => getClipCategoryIds(c).includes(cat.id));
      const totalReviews = catClips.reduce((sum, c) => sum + (c.reviews?.length || 0), 0);
      const totalPicks = catClips.reduce((sum, c) => sum + (c.picks?.length || 0), 0);
      const totalTried = catClips.reduce((sum, c) => sum + (c.couplesTried || 0), 0);
      return {
        ...cat,
        clipCount: catClips.length,
        totalReviews,
        totalPicks,
        totalTried
      };
    }).sort((a, b) => b.clipCount - a.clipCount);
  };

  const addClip = async () => {
    if (!newClip.title.trim() || !newClip.link.trim() || newClip.categoryIds.length === 0) return;

    // First category is primary, rest are additional
    const [primaryCategory, ...additionalCategories] = newClip.categoryIds;
    if (!primaryCategory) return;

    const clip: Clip = {
      id: generateId('clip'),
      categoryId: primaryCategory,
      additionalCategoryIds: additionalCategories.length > 0 ? additionalCategories : undefined,
      title: newClip.title.trim(),
      link: newClip.link.trim(),
      description: newClip.description.trim(),
      tags: newClip.tags,
      rating: '',
      notesForHim: '',
      notesForHer: '',
      reviews: [],
      picks: []
    };

    await saveDateNiteClips([...dateNiteClips, clip]);
    setNewClip({ categoryIds: [CATEGORIES[0]?.id || ''], title: '', link: '', description: '', tags: [] });
    setTab('clips');
  };

  const deleteClip = async (clipId: string) => {
    if (confirm('Delete this clip?')) {
      await saveDateNiteClips(dateNiteClips.filter(c => c.id !== clipId));
    }
  };

  const startEditClip = (clip: Clip) => {
    setEditingClip(clip);
    setEditClipForm({
      categoryIds: getClipCategoryIds(clip),
      tags: clip.tags || [],
      title: clip.title,
      description: clip.description || '',
      link: clip.link || ''
    });
  };

  const saveEditClip = async () => {
    if (!editingClip || editClipForm.categoryIds.length === 0) return;

    const [primaryCategory, ...additionalCategories] = editClipForm.categoryIds;
    if (!primaryCategory) return;

    const updatedClip: Clip = {
      ...editingClip,
      categoryId: primaryCategory,
      additionalCategoryIds: additionalCategories.length > 0 ? additionalCategories : undefined,
      tags: editClipForm.tags,
      title: editClipForm.title.trim() || editingClip.title,
      description: editClipForm.description.trim(),
      link: editClipForm.link.trim() || editingClip.link
    };

    await saveDateNiteClips(dateNiteClips.map(c => c.id === editingClip.id ? updatedClip : c));
    setEditingClip(null);
  };

  // Delete a reviewer
  const deleteReviewer = async (reviewerId: string) => {
    const reviewer = reviewers.find(r => r.id === reviewerId);
    if (!confirm(`Delete reviewer "${reviewerId}"? This cannot be undone.`)) return;

    try {
      await d1Delete('reviewers', reviewerId);

      // Also clean up related data if they have a couple
      if (reviewer?.coupleId) {
        await d1DeleteWhere('couple_recommendations', { coupleId: reviewer.coupleId });
        await d1DeleteWhere('scheduled_activities', { coupleId: reviewer.coupleId });
        await d1DeleteWhere('couple_messages', { coupleId: reviewer.coupleId });
        await d1DeleteWhere('couple_submissions', { coupleId: reviewer.coupleId });
        await d1DeleteWhere('couple_requests', { coupleId: reviewer.coupleId });
      }

      alert('Reviewer deleted. Refresh to see changes.');
      window.location.reload();
    } catch (err) {
      alert('Failed to delete reviewer');
    }
  };

  // Delete a couple
  const deleteCouple = async (coupleId: string) => {
    if (!confirm(`Delete couple "${coupleId}" and all their data? This cannot be undone.`)) return;

    try {
      // Delete couple data
      await d1Delete('couples', coupleId);
      await d1DeleteWhere('tried_clips', { coupleId });

      // Clean up all related data
      await d1DeleteWhere('couple_recommendations', { coupleId });
      await d1DeleteWhere('scheduled_activities', { coupleId });
      await d1DeleteWhere('couple_messages', { coupleId });
      await d1DeleteWhere('couple_submissions', { coupleId });
      await d1DeleteWhere('couple_requests', { coupleId });
      await d1DeleteWhere('activity_followups', { coupleId });
      await d1DeleteWhere('wishlist_items', { coupleId });

      // Also unlink any reviewer with this coupleId
      const reviewersList = await d1List<Reviewer>('reviewers');
      for (const r of reviewersList) {
        if (r.coupleId === coupleId) {
          await d1Upsert('reviewers', { ...r, coupleId: undefined, partnerName: undefined });
        }
      }

      // Update local state
      setAllCouples(prev => prev.filter(c => c.coupleId !== coupleId));
      alert('Couple deleted.');
    } catch (err) {
      alert('Failed to delete couple');
    }
  };

  // Seed a fully filled-out example reviewer for demo/testing
  const seedExampleReviewer = async () => {
    const exampleReviewer: Reviewer = {
      id: 'reviewer-example-jessica',
      code: 'JSC-2024',
      registeredAt: now(),
      coupleId: 'VBC-JESMIK',
      partnerName: 'Mike',
      // Section A: Background
      gender: 'Female',
      ageRange: '25-34',
      relationshipStatus: 'In a relationship',
      sexuallyActive: 'Yes, regularly',
      watchesAdultContent: 'Yes',
      adultContentThemes: 'Couples content, realistic scenarios, passionate/romantic',
      adultContentExample: 'Really enjoy watching couples who genuinely seem into each other - the chemistry is everything. Bellesa and similar female-friendly content.',
      whatMakesGoodContent: 'Natural reactions, real chemistry, build-up and anticipation, good lighting and angles',
      whatFeelsFake: 'Over-the-top moaning, unrealistic positions held for too long, bad dialogue',
      preferredStyle: 'Lean towards amateur',
      familiarWithProducts: 'Yes - know them well',
      relevantExperience: 'Social media',
      whyInterested: 'Curious about the products and want to help couples find content that actually enhances their intimacy',
      // Section B: Load Boost (Female)
      enjoysVolumeContent: 'Yes, definitely',
      wantPartnerLoadBoost: 'Yes, definitely',
      enjoysSwallowing: 'Yes, usually',
      swallowingDescription: 'I love the intimacy of it - makes me feel really connected to my partner. The first time I swallowed was actually a bit nervous but Mike was so sweet about it. Now it\'s one of my favorite ways to finish him. The amount definitely makes it more exciting for both of us!',
      wouldEnjoyBetterTaste: 'Yes, definitely',
      enjoysFacials: 'Sometimes',
      volumeMakesDifference: 'Yes, definitely',
      // Section C: Soaking Wet (Female)
      canSquirt: 'Sometimes',
      squirtScenarioDescription: 'Usually happens with intense G-spot stimulation, especially when Mike uses his fingers with a "come hither" motion while going down on me. Takes some build-up but when it happens it\'s incredible. Also more likely when I\'m really relaxed and we\'ve had a lot of foreplay.',
      wantToSquirtMore: 'Yes, definitely',
      usedArousalProducts: 'No but interested',
      // General
      additionalNotes: 'We\'re always looking to spice things up! Been together 3 years and still exploring. Open to trying new things together.',
      // Section D: Sexual Experience Details
      lastSexualActivity: 'Last night actually! Started with a massage that turned into more. Mike went down on me for a while (he\'s gotten SO good at that), then we did cowgirl which is my favorite. I came twice before he finished on my chest. We showered together after - that was really sweet.',
      facialExperience: 'Yes, we\'ve done facials maybe a dozen times. At first I was hesitant but Mike asked nicely and I decided to try it. I actually like it now - something about seeing how turned on he gets is really hot. I prefer warning though so I can close my eyes! The mess is real but we laugh about it.',
      oralExperience: 'Love both giving and receiving! Going down on Mike is actually really fun - I love the control and watching his reactions. He\'s very vocal which helps me know what works. For receiving, it took me a while to relax enough but now it\'s probably my favorite thing. Mike says he could do it for hours.',
      analExperience: 'We\'ve tried it a few times. First time was uncomfortable but we went really slow with lots of lube. It\'s gotten better but still not something I crave. Maybe once a month when I\'m really in the mood. Mike is always patient and checks in which I appreciate.',
      favoriteSexualMemory: 'Our anniversary trip to this little cabin in the mountains. We had the whole weekend with no phones, no distractions. Spent basically the entire Saturday in bed, trying new positions, using toys we\'d bought for the trip. Made love by the fireplace that night - felt like a movie. We still talk about that weekend.',
      fantasiesNotTried: 'Threesome with another woman (we\'ve discussed it and are both open), light bondage/restraints, role play scenarios, outdoor sex somewhere secluded, maybe a sex club just to watch others',
      biggestTurnOns: 'Neck kisses, dirty talk, being desired, spontaneity, when Mike takes control, eye contact during',
      dealBreakers: 'Anything non-consensual in content, degrading language, extreme stuff, bad hygiene'
    };

    // Save to D1
    const existingReviewers = await d1List<Reviewer>('reviewers');

    // Check if already exists
    if (existingReviewers.some(r => r.id === exampleReviewer.id)) {
      alert('Example reviewer already exists!');
      return;
    }

    await d1Upsert('reviewers', exampleReviewer);

    // Also create the couple data
    const coupleData: CoupleData = {
      coupleId: 'VBC-JESMIK',
      accessCode: 'VBA-JESMIK',
      hisName: 'Mike',
      herName: 'Jessica',
      setupAt: now(),
      preferences: {
        'oral': 'love',
        'positions': 'like',
        'finish': 'love',
        'toys': 'curious',
        'roleplay': 'curious',
        'anal': 'neutral'
      },
      tried: [
        { clipId: 'clip-001', rating: 'hot', hisThoughts: 'Wow, definitely trying this again!', herThoughts: 'So hot! Loved it.', triedAt: '2026-01-20T20:00:00Z' },
        { clipId: 'clip-002', rating: 'good', hisThoughts: 'Really enjoyed this one', herThoughts: 'Fun but challenging position', triedAt: '2026-01-18T21:30:00Z' }
      ],
      favorites: ['clip-001'],
      hisProfile: {
        size: 4, // 7-8" Large
        girth: 3, // Thick
        cumIntensity: 4, // Flood
      }
    };

    // Save couple data globally via D1
    await saveCoupleByCode(coupleData);

    alert('Example reviewer "Jessica" and couple data created! Refresh to see.');
    window.location.reload();
  };

  // Queue a follow-up for confirmation before sending
  // If there's already a pending follow-up for the same reviewer, adds to the queue
  const queueFollowUp = (reviewerId: string, reviewerName: string, question: string, context?: string) => {
    setPendingFollowUp(prev => {
      if (prev && prev.reviewerId === reviewerId) {
        // Add to existing queue for same reviewer
        return {
          ...prev,
          questions: [...prev.questions, { question, context }]
        };
      }
      // New queue for this reviewer
      return { reviewerId, reviewerName, questions: [{ question, context }] };
    });
  };

  // Remove a question from the pending queue
  const removeFromQueue = (index: number) => {
    setPendingFollowUp(prev => {
      if (!prev) return null;
      const newQuestions = prev.questions.filter((_, i) => i !== index);
      if (newQuestions.length === 0) return null;
      return { ...prev, questions: newQuestions };
    });
  };

  // Actually send the follow-ups after confirmation
  // First question is sent immediately (status: 'pending'), rest are queued (status: 'queued')
  const confirmAndSendFollowUp = async () => {
    if (!saveReviewerFollowUps || !pendingFollowUp || pendingFollowUp.questions.length === 0) return;

    const sequenceId = pendingFollowUp.questions.length > 1 ? `seq-${Date.now()}` : undefined;
    const baseTime = Date.now();

    const newFollowUps: ReviewerFollowUp[] = pendingFollowUp.questions.map((q, index) => ({
      id: `fu-${baseTime}-${index}`,
      reviewerId: pendingFollowUp.reviewerId,
      question: q.question.trim(),
      context: q.context,
      sentAt: new Date().toISOString(),
      status: index === 0 ? 'pending' : 'queued' as const, // First is pending, rest are queued
      sequenceId,
      sequenceOrder: sequenceId ? index : undefined
    }));

    await saveReviewerFollowUps([...reviewerFollowUps, ...newFollowUps]);
    setPendingFollowUp(null);
    setNewFollowUpQuestion('');
    setAskingFollowUp(null);
  };

  // Generate solo exploration questions from reviewer's review data
  const generateSoloQuestions = (reviewer: Reviewer) => {
    // Get all clips this reviewer has reviewed
    const reviewedClips = dateNiteClips.filter(clip =>
      clip.reviews?.some(rev => rev.reviewerId === reviewer.id || rev.reviewerId === reviewer.code)
    );

    // Get highly-rated clips (Must Try / Good)
    const highRated = reviewedClips.filter(clip => {
      const rev = clip.reviews?.find(r => r.reviewerId === reviewer.id || r.reviewerId === reviewer.code);
      return rev && (rev.rating === '🔥 Must Try' || rev.rating === '👍 Good');
    });

    // Get her-focused clips (area === 'her')
    const herCategories = CATEGORIES.filter(c => c.area === 'her');
    const herCategoryIds: string[] = herCategories.map(c => c.id);
    const herClips = highRated.filter(clip => {
      const catIds = [clip.categoryId, ...(clip.additionalCategoryIds || [])];
      return catIds.some(id => herCategoryIds.includes(id));
    });

    // Count reviews per her-category
    const herCategoryCounts: Record<string, number> = {};
    reviewedClips.forEach(clip => {
      const catIds = [clip.categoryId, ...(clip.additionalCategoryIds || [])];
      catIds.filter(id => herCategoryIds.includes(id)).forEach(id => {
        herCategoryCounts[id] = (herCategoryCounts[id] || 0) + 1;
      });
    });

    // Top 3 must-try clips for solo inspiration
    const topMustTry = reviewedClips
      .filter(clip => clip.reviews?.find(r => (r.reviewerId === reviewer.id || r.reviewerId === reviewer.code) && r.rating === '🔥 Must Try'))
      .slice(0, 3);

    const questions: string[] = [];

    // Question about a specific highly-rated clip
    if (herClips.length > 0) {
      const pick = herClips[0]!;
      const cat = CATEGORIES.find(c => c.id === pick.categoryId);
      questions.push(`You gave "${pick.title}" a 🔥 Must Try — what specifically stood out? Is that something you explore on your own?`);
      if (cat) {
        questions.push(`You've reviewed ${herCategoryCounts[cat.id] || 0} clips in ${cat.emoji} ${cat.name} — have you been exploring that side of yourself?`);
      }
    }

    // Top 3 solo inspiration question
    if (topMustTry.length >= 2) {
      const titles = topMustTry.map(c => `"${c.title}"`).join(', ');
      questions.push(`Which of these would you use for solo inspiration: ${titles}?`);
    }

    // Squirting exploration (based on her-category engagement)
    if (herCategoryCounts['female-masturbation'] || herCategoryCounts['fingering']) {
      questions.push(`Have you tried exploring squirting on your own? We have clips specifically around techniques and building up to it — want us to add some to your playlist?`);
    }

    // General solo pleasure question
    questions.push(`What kind of content helps you get in the mood when you're on your own? We'd love to curate your playlist around what actually works for you.`);

    // If very few questions generated, add a generic one
    if (questions.length < 3) {
      questions.push(`What's something you've always been curious about trying solo but haven't yet?`);
    }

    setSoloQuestions({ reviewerId: reviewer.id, questions });
    setSoloQuestionsEditing([...questions]);
  };

  // Legacy function for custom questions (direct send after typing)
  const sendFollowUp = async (reviewerId: string, question: string, context?: string, parentId?: string, clipIds?: string[]) => {
    if (!saveReviewerFollowUps || !question.trim()) return;

    const followUp: ReviewerFollowUp = {
      id: `fu-${Date.now()}`,
      reviewerId,
      question: question.trim(),
      context,
      sentAt: new Date().toISOString(),
      status: 'pending',
      parentId, // For threaded conversations
      ...(clipIds && clipIds.length > 0 && { clipIds })
    };

    await saveReviewerFollowUps([...reviewerFollowUps, followUp]);
    setNewFollowUpQuestion('');
    setAskingFollowUp(null);
    setReplyingToQuestionId(null);
    setReplyClipIds([]);
    setShowReplyClipPicker(false);
  };

  // Delete a reviewer follow-up question (with deep nesting support)
  const deleteFollowUp = async (questionId: string) => {
    if (!saveReviewerFollowUps) return;
    // Recursively find all descendant IDs to delete
    const getDescendantIds = (parentId: string): string[] => {
      const children = reviewerFollowUps.filter(fu => fu.parentId === parentId);
      return children.flatMap(child => [child.id, ...getDescendantIds(child.id)]);
    };
    const idsToDelete = new Set([questionId, ...getDescendantIds(questionId)]);
    const updated = reviewerFollowUps.filter(fu => !idsToDelete.has(fu.id));
    await saveReviewerFollowUps(updated);
    setDeleteConfirm(null);
  };

  // Delete a couple follow-up question (with deep nesting support)
  const deleteCoupleFollowUp = async (questionId: string) => {
    if (!saveCoupleFollowUps) return;
    // Recursively find all descendant IDs to delete
    const getDescendantIds = (parentId: string): string[] => {
      const children = coupleFollowUps.filter(fu => fu.parentId === parentId);
      return children.flatMap(child => [child.id, ...getDescendantIds(child.id)]);
    };
    const idsToDelete = new Set([questionId, ...getDescendantIds(questionId)]);
    const updated = coupleFollowUps.filter(fu => !idsToDelete.has(fu.id));
    await saveCoupleFollowUps(updated);
    setDeleteConfirm(null);
  };

  // Delete a couple question (with deep nesting support)
  const deleteCoupleQuestion = async (questionId: string) => {
    if (!saveCoupleQuestions) return;
    // Recursively find all descendant IDs to delete
    const getDescendantIds = (parentId: string): string[] => {
      const children = coupleQuestions.filter(q => q.parentId === parentId);
      return children.flatMap(child => [child.id, ...getDescendantIds(child.id)]);
    };
    const idsToDelete = new Set([questionId, ...getDescendantIds(questionId)]);
    const updated = coupleQuestions.filter(q => !idsToDelete.has(q.id));
    await saveCoupleQuestions(updated);
  };

  const resetClipSuggestionForm = () => {
    setCreatingClipSuggestion(null);
    setEditingClipSuggestionId(null);
    setClipSuggestionContext('');
    setClipSuggestionItems([]);
    setNewClipUrl('');
    setNewClipTitle('');
    setClipSuggestionLinkedQuestion(null);
  };

  const startEditingClipSuggestion = (suggestion: ClipSuggestionRequest) => {
    setCreatingClipSuggestion({
      coupleId: suggestion.coupleId,
      reviewerId: suggestion.reviewerId,
    });
    setEditingClipSuggestionId(suggestion.id);
    setClipSuggestionContext(suggestion.context || '');
    setClipSuggestionItems((suggestion.clips || []).map(clip => ({ ...clip })));
    setNewClipUrl('');
    setNewClipTitle('');
    setClipSuggestionLinkedQuestion(suggestion.linkedQuestionId || null);
  };

  const buildClipSuggestionItems = () => clipSuggestionItems
    .filter(item => item.url.trim() && item.title.trim())
    .map((item, i) => ({
      ...item,
      id: item.id || `${generateId('csi')}-${i}`,
      url: item.url.trim(),
      title: item.title.trim(),
      description: item.description?.trim() || undefined,
    }));

  // Send or update a clip suggestion request
  const sendClipSuggestion = async () => {
    if (!saveClipSuggestions || clipSuggestionItems.length === 0 || !creatingClipSuggestion) return;
    const normalizedClips = buildClipSuggestionItems();
    if (normalizedClips.length === 0) return;

    if (editingClipSuggestionId) {
      const updated = clipSuggestions.map(suggestion => {
        if (suggestion.id !== editingClipSuggestionId) return suggestion;

        const previousClipIds = new Set((suggestion.clips || []).map(clip => clip.id));
        const hasNewUnratedClip = normalizedClips.some(clip => !previousClipIds.has(clip.id) && clip.rating === undefined);
        const ratedCount = normalizedClips.filter(clip => clip.rating !== undefined).length;
        const nextStatus = hasNewUnratedClip
          ? (ratedCount > 0 ? 'partial' as const : 'pending' as const)
          : suggestion.status;

        return {
          ...suggestion,
          coupleId: creatingClipSuggestion.coupleId,
          reviewerId: creatingClipSuggestion.reviewerId,
          context: clipSuggestionContext.trim(),
          clips: normalizedClips,
          status: nextStatus,
          completedAt: nextStatus === 'completed' ? suggestion.completedAt : undefined,
          linkedQuestionId: clipSuggestionLinkedQuestion || undefined,
        };
      });

      await saveClipSuggestions(updated);
      resetClipSuggestionForm();
      return;
    }

    const request: ClipSuggestionRequest = {
      id: generateId('csr'),
      coupleId: creatingClipSuggestion.coupleId,
      reviewerId: creatingClipSuggestion.reviewerId,
      context: clipSuggestionContext.trim(),
      clips: normalizedClips,
      sentAt: now(),
      status: 'pending',
      linkedQuestionId: clipSuggestionLinkedQuestion || undefined
    };

    await saveClipSuggestions([...clipSuggestions, request]);
    resetClipSuggestionForm();
  };

  // Add clip to suggestion form
  const addClipToSuggestion = () => {
    if (!newClipUrl.trim() || !newClipTitle.trim()) return;
    setClipSuggestionItems([...clipSuggestionItems, {
      id: generateId('csi'),
      url: newClipUrl.trim(),
      title: newClipTitle.trim(),
    }]);
    setNewClipUrl('');
    setNewClipTitle('');
  };

  // Remove clip from suggestion form
  const removeClipFromSuggestion = (index: number) => {
    setClipSuggestionItems(clipSuggestionItems.filter((_, i) => i !== index));
  };

  // Remove a question from the pending couple follow-up queue
  const removePendingCoupleQuestion = (index: number) => {
    setPendingCoupleFollowUp(prev => {
      if (!prev) return null;
      const newQuestions = prev.questions.filter((_, i) => i !== index);
      if (newQuestions.length === 0) return null;
      return { ...prev, questions: newQuestions };
    });
  };

  // Actually send the couple follow-ups after confirmation
  const confirmAndSendCoupleFollowUp = async () => {
    if (!saveCoupleFollowUps || !pendingCoupleFollowUp || pendingCoupleFollowUp.questions.length === 0) return;

    const sequenceId = pendingCoupleFollowUp.questions.length > 1 ? `seq-${Date.now()}` : undefined;

    const newFollowUps: CoupleFollowUp[] = pendingCoupleFollowUp.questions.map((q, index) => ({
      id: generateId('cfu'),
      coupleId: pendingCoupleFollowUp.coupleId,
      question: q.question.trim(),
      context: q.context,
      clipId: q.clipId,
      sentAt: new Date().toISOString(),
      status: index === 0 ? 'pending' : 'queued' as const,
      sequenceId,
      sequenceOrder: sequenceId ? index : undefined
    }));

    await saveCoupleFollowUps([...coupleFollowUps, ...newFollowUps]);
    setPendingCoupleFollowUp(null);
  };

  // Queue a couple follow-up question (opens confirmation modal)
  const queueCoupleFollowUp = (coupleId: string, coupleName: string, question: string, context?: string, clipId?: string) => {
    setPendingCoupleFollowUp(prev => {
      if (prev && prev.coupleId === coupleId) {
        // Add to existing queue for same couple
        return {
          ...prev,
          questions: [...prev.questions, { question, context, clipId }]
        };
      }
      // New queue
      return {
        coupleId,
        coupleName,
        questions: [{ question, context, clipId }]
      };
    });
  };

  // Send a reply to a couple question (threaded conversation)
  const sendCoupleQuestionReply = async (coupleId: string, parentId: string, reviewerName: string) => {
    if (!saveCoupleQuestions || !newCoupleQuestionReply.trim()) return;

    const reply: CoupleQuestion = {
      id: `cq-${Date.now()}`,
      coupleId,
      fromReviewerId: 'admin',
      fromReviewerName: reviewerName || 'Admin',
      question: newCoupleQuestionReply.trim(),
      sentAt: now(),
      status: 'answered', // Admin replies are not pending questions
      answer: newCoupleQuestionReply.trim(), // It's a comment, not a question
      answeredBy: 'admin',
      parentId,
      ...(replyClipIds.length > 0 && { clipIds: replyClipIds })
    };

    await saveCoupleQuestions([...coupleQuestions, reply]);
    setNewCoupleQuestionReply('');
    setReplyingToCoupleQuestionId(null);
    setReplyClipIds([]);
    setShowReplyClipPicker(false);
  };

  // Edit existing CoupleQuestion
  const saveQuestionEdit = async (questionId: string) => {
    if (!saveCoupleQuestions || !editQuestionText.trim()) return;

    const updated = coupleQuestions.map(q => {
      if (q.id === questionId) {
        return {
          ...q,
          question: editQuestionText.trim(),
          answer: q.answer ? editQuestionText.trim() : q.answer, // Update answer too if it's a reply
          clipIds: editQuestionClipIds.length > 0 ? editQuestionClipIds : undefined,
          clipId: undefined // Clear legacy single clipId
        };
      }
      return q;
    });

    await saveCoupleQuestions(updated);
    setEditingQuestionId(null);
    setEditQuestionText('');
    setEditQuestionClipIds([]);
  };

  // Edit existing CoupleFollowUp
  const saveFollowUpEdit = async (followUpId: string) => {
    if (!saveCoupleFollowUps || !editQuestionText.trim()) return;

    const updated = coupleFollowUps.map(fu => {
      if (fu.id === followUpId) {
        return {
          ...fu,
          question: editQuestionText.trim(),
          answer: fu.answer ? editQuestionText.trim() : fu.answer, // Update answer too if it's a reply
          clipIds: editQuestionClipIds.length > 0 ? editQuestionClipIds : undefined,
          clipId: undefined // Clear legacy single clipId
        };
      }
      return fu;
    });

    await saveCoupleFollowUps(updated);
    setEditingQuestionId(null);
    setEditQuestionText('');
    setEditQuestionClipIds([]);
  };

  // Duplicate a couple profile with new codes and detached reviewer
  const duplicateCouple = async (couple: CoupleData) => {
    const newCoupleId = generateCoupleCode();
    const newAccessCode = generateAccessCode();

    const duplicatedCouple: CoupleData = {
      ...couple,
      coupleId: newCoupleId,
      accessCode: newAccessCode,
      setupAt: now(),
      lastActivityAt: undefined,
      // Clear reviewer link
      managedByReviewerId: undefined,
      managedByReviewerName: undefined,
      managedByAcceptedAt: undefined,
    };

    // Save via D1
    await saveCoupleByCode(duplicatedCouple);

    // Refresh couples list
    setAllCouples(prev => [...prev, duplicatedCouple]);

    // Show result modal
    setDuplicatedCoupleResult({
      coupleId: newCoupleId,
      accessCode: newAccessCode,
      hisName: couple.hisName,
      herName: couple.herName
    });
  };

  // Seed example creator submissions for testing the review workflow
  const seedExampleSubmissions = async () => {
    // Example creators
    const exampleCreators: Creator[] = [
      {
        id: 'Creator-1847',
        product: 'Load Boost',
        orderId: 'VB-78432',
        purchaseDate: '2024-12-20',
        registeredAt: '2025-01-15T10:30:00.000Z',
      },
      {
        id: 'Creator-2156',
        product: 'Soaking Wet',
        orderId: 'VB-91205',
        purchaseDate: '2025-01-02',
        registeredAt: '2025-01-10T15:45:00.000Z',
      },
    ];

    // Example submitted clips
    const exampleSubmissions: SubmittedClip[] = [
      {
        id: 'seed-001',
        clipTitle: 'Load Boost Demo - January',
        contentLink: 'https://www.redgifs.com/watch/contentunusedscaup',
        creatorId: 'Creator-1847',
        product: 'Load Boost',
        productsTaking: 'Load Boost (2 weeks)',
        gender: 'male',
        status: 'Submitted',
        dateSubmitted: '2025-01-18',
        standoutNotes: 'Thick finish, great volume',
      },
      {
        id: 'seed-002',
        clipTitle: 'Amazing Results After 2 Weeks',
        contentLink: 'https://www.redgifs.com/watch/mindlesshomelyassassinbug',
        creatorId: 'Creator-1847',
        product: 'Load Boost',
        productsTaking: 'Load Boost (2 weeks)',
        gender: 'male',
        status: 'Submitted',
        dateSubmitted: '2025-01-20',
        standoutNotes: 'Noticeable increase in volume',
      },
      {
        id: 'seed-003',
        clipTitle: 'First Time Trying Soaking Wet',
        contentLink: 'https://www.redgifs.com/watch/infantilesaltyblackbear',
        creatorId: 'Creator-2156',
        product: 'Soaking Wet',
        productsTaking: 'Soaking Wet (1 week)',
        gender: 'female',
        status: 'Submitted',
        dateSubmitted: '2025-01-19',
        standoutNotes: 'Very wet throughout',
      },
    ];

    // Check if already seeded
    if (clips.some(c => c.id.startsWith('seed-'))) {
      alert('Example submissions already exist!');
      return;
    }

    // Save creators
    const existingCreators = await d1List<Creator>('creators');
    const newCreators = exampleCreators.filter(ec => !existingCreators.some(c => c.id === ec.id));
    if (newCreators.length > 0) {
      await d1BatchUpsert('creators', newCreators);
    }

    // Save submissions
    await d1BatchUpsert('submitted_clips', exampleSubmissions);

    alert('Example submissions created! Refresh to see.');
    window.location.reload();
  };

  const filteredClips = categoryFilter === 'all'
    ? dateNiteClips
    : dateNiteClips.filter(c => getClipCategoryIds(c).includes(categoryFilter));

  if (!auth) return (
    <div className="min-h-screen bg-zinc-900 flex items-center justify-center p-4">
      <div className="bg-zinc-800 rounded-2xl p-6 w-full max-w-md border border-zinc-700">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">Admin Access</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        <input
          type="password"
          value={password}
          onChange={e => { setPassword(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && login()}
          placeholder="Password"
          className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white mb-4"
          autoFocus
        />
        {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
        <PrimaryButton onClick={login} className="w-full">Access Admin</PrimaryButton>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-900 p-4 overflow-auto">
      <div className="bg-zinc-800 rounded-2xl p-6 w-full max-w-5xl mx-auto border border-zinc-700 my-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">Admin Dashboard</h2>
          <div className="flex gap-2">
            {onRefresh && (
              <button
                onClick={async () => {
                  await onRefresh();
                  // Also reload couples list
                  const loadedCouples = await d1List<CoupleData>('couples');
                  setAllCouples(loadedCouples);
                  // Reload presence
                  try {
                    const presences = await d1List<UserPresence>('user_presence');
                    const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000).toISOString();
                    setUserPresences(presences.filter(p => p.lastSeen > twoMinutesAgo));
                  } catch { /* skip */ }
                }}
                className="flex items-center gap-2 bg-lime-500 text-zinc-900 px-3 py-1.5 rounded-lg text-sm hover:bg-lime-400 font-medium"
              >
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
            )}
            <button
              onClick={exportData}
              className="flex items-center gap-2 bg-zinc-700 text-zinc-300 px-3 py-1.5 rounded-lg text-sm hover:bg-zinc-600"
            >
              <Download className="w-4 h-4" /> Export
            </button>
            <button onClick={onClose} className="text-zinc-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => { setTab('people'); setRecordCoupleId(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'people' ? 'text-zinc-900' : 'bg-zinc-700 text-zinc-400'}`}
            style={tab === 'people' ? { backgroundColor: COLORS.lime } : {}}
          >
            <Users className="w-4 h-4" /> People
            {(people?.couples.reduce((n, c) => n + c.pendingSubmissionCount, 0) ?? 0) > 0 && (
              <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                {people?.couples.reduce((n, c) => n + c.pendingSubmissionCount, 0)}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab('stats')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'stats' ? 'text-zinc-900' : 'bg-zinc-700 text-zinc-400'}`}
            style={tab === 'stats' ? { backgroundColor: COLORS.lime } : {}}
          >
            <BarChart3 className="w-4 h-4" /> Stats
          </button>
          <button
            onClick={() => setTab('clips')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'clips' ? 'text-zinc-900' : 'bg-zinc-700 text-zinc-400'}`}
            style={tab === 'clips' ? { backgroundColor: COLORS.lime } : {}}
          >
            <Film className="w-4 h-4" /> Clips ({dateNiteClips.length})
          </button>
          <button
            onClick={() => setTab('addclip')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'addclip' ? 'text-zinc-900' : 'bg-zinc-700 text-zinc-400'}`}
            style={tab === 'addclip' ? { backgroundColor: COLORS.lime } : {}}
          >
            <Plus className="w-4 h-4" /> Add Clip
          </button>
          <button
            onClick={() => setTab('reviewers')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'reviewers' ? 'text-zinc-900' : 'bg-zinc-700 text-zinc-400'}`}
            style={tab === 'reviewers' ? { backgroundColor: COLORS.lime } : {}}
          >
            <Users className="w-4 h-4" /> Reviewers ({reviewers.length})
          </button>
          <button
            onClick={() => setTab('couples')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'couples' ? 'text-zinc-900' : 'bg-zinc-700 text-zinc-400'}`}
            style={tab === 'couples' ? { backgroundColor: COLORS.lime } : {}}
          >
            <HeartHandshake className="w-4 h-4" /> Couples ({allCouples.length || people?.couples.length || reviewers.filter(r => r.coupleId).length})
          </button>
          <button
            onClick={() => setTab('questions')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'questions' ? 'text-zinc-900' : 'bg-zinc-700 text-zinc-400'}`}
            style={tab === 'questions' ? { backgroundColor: COLORS.lime } : {}}
          >
            <MessageSquare className="w-4 h-4" /> Questions ({researchQuestions.length})
          </button>
          <button
            onClick={() => setTab('survey')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'survey' ? 'text-zinc-900' : 'bg-zinc-700 text-zinc-400'}`}
            style={tab === 'survey' ? { backgroundColor: COLORS.lime } : {}}
          >
            <Droplets className="w-4 h-4" /> Her Survey
          </button>
          <button
            onClick={() => setTab('edits')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'edits' ? 'text-zinc-900' : 'bg-zinc-700 text-zinc-400'}`}
            style={tab === 'edits' ? { backgroundColor: COLORS.lime } : {}}
          >
            <Edit2 className="w-4 h-4" /> Edits
            {suggestedEdits.filter(e => e.status === 'pending').length > 0 && (
              <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                {suggestedEdits.filter(e => e.status === 'pending').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab('submissions')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'submissions' ? 'text-zinc-900' : 'bg-zinc-700 text-zinc-400'}`}
            style={tab === 'submissions' ? { backgroundColor: COLORS.lime } : {}}
          >
            <Send className="w-4 h-4" /> Submissions
            {clips.filter(c => c.status === 'Submitted' || c.status === 'Under Review').length > 0 && (
              <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                {clips.filter(c => c.status === 'Submitted' || c.status === 'Under Review').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab('tools')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'tools' ? 'text-zinc-900' : 'bg-zinc-700 text-zinc-400'}`}
            style={tab === 'tools' ? { backgroundColor: COLORS.lime } : {}}
          >
            <Trash2 className="w-4 h-4" /> Data Tools
          </button>
        </div>

        {tab === 'people' && recordCouple && (
          <CoupleRecord
            couple={recordCouple}
            clips={dateNiteClips}
            loginTrackingEnabled={people?.loginTrackingEnabled ?? false}
            onBack={() => setRecordCoupleId(null)}
            onOpenCard={id => openFromPeople('couples', id)}
            onOpenReviewer={id => openFromPeople('reviewers', id)}
            onOpenSubmissions={() => setTab('submissions')}
          />
        )}

        {tab === 'people' && !recordCouple && (
          <PeopleOverview
            people={people}
            loading={peopleLoading}
            error={peopleError}
            onRefresh={loadPeople}
            onOpenCouple={id => { setRecordCoupleId(id); window.scrollTo({ top: 0 }); }}
            onOpenReviewer={id => openFromPeople('reviewers', id)}
          />
        )}

        {/* Stats Tab - Category Overview */}
        {tab === 'stats' && (
          <div>
            {/* Online Now Section */}
            <div className="mb-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl p-4 border border-green-500/30">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <span className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
                Online Now ({userPresences.length})
              </h4>
              {userPresences.length === 0 ? (
                <p className="text-zinc-500 text-sm">No users currently online</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {userPresences.map(p => (
                    <div
                      key={p.id}
                      className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
                        p.userType === 'reviewer'
                          ? 'bg-purple-500/20 border border-purple-500/30'
                          : 'bg-pink-500/20 border border-pink-500/30'
                      }`}
                    >
                      {p.userType === 'reviewer' ? (
                        <Users className="w-4 h-4 text-purple-400" />
                      ) : (
                        <Heart className="w-4 h-4 text-pink-400" />
                      )}
                      <span className={p.userType === 'reviewer' ? 'text-purple-300' : 'text-pink-300'}>
                        {p.userName}
                      </span>
                      <span className="text-zinc-500 text-xs">
                        • {p.currentPage === 'home' ? 'Home' : p.currentPage}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <h3 className="text-lg font-semibold text-white mb-4">Clips by Category</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {getCategoryStats().map(cat => (
                <div
                  key={cat.id}
                  className={`bg-gradient-to-r ${cat.color} rounded-xl p-4 cursor-pointer hover:scale-[1.02] transition-transform`}
                  onClick={() => { setCategoryFilter(cat.id); setTab('clips'); }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{cat.emoji}</span>
                      <div>
                        <p className="text-white font-semibold">{cat.name}</p>
                        <p className="text-white/70 text-sm">{cat.clipCount} clips</p>
                      </div>
                    </div>
                    <div className="text-right text-white/70 text-xs">
                      <p>{cat.totalReviews} reviews</p>
                      <p>{cat.totalPicks} picks</p>
                      <p>{cat.totalTried} tried</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-zinc-900 rounded-xl p-4 border border-zinc-700">
              <h4 className="text-white font-semibold mb-3">Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold" style={{ color: COLORS.lime }}>{dateNiteClips.length}</p>
                  <p className="text-zinc-400 text-sm">Total Clips</p>
                </div>
                <div>
                  <p className="text-3xl font-bold" style={{ color: COLORS.lime }}>{CATEGORIES.length}</p>
                  <p className="text-zinc-400 text-sm">Categories</p>
                </div>
                <div>
                  <p className="text-3xl font-bold" style={{ color: COLORS.lime }}>{reviewers.length}</p>
                  <p className="text-zinc-400 text-sm">Reviewers</p>
                </div>
                <div>
                  <p className="text-3xl font-bold" style={{ color: COLORS.lime }}>{creators.length}</p>
                  <p className="text-zinc-400 text-sm">Creators</p>
                </div>
              </div>
            </div>

            {/* Creator Submissions Stats */}
            <div className="mt-6 bg-gradient-to-r from-orange-500/10 to-purple-500/10 rounded-xl p-4 border border-orange-500/30">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Film className="w-4 h-4 text-orange-400" /> Creator Submissions
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-amber-400">{clips.filter(c => c.status === 'Submitted').length}</p>
                  <p className="text-zinc-400 text-xs">Pending</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-400">{clips.filter(c => c.status === 'Under Review').length}</p>
                  <p className="text-zinc-400 text-xs">Under Review</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">{clips.filter(c => c.status === 'Approved').length}</p>
                  <p className="text-zinc-400 text-xs">Approved</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-400">{clips.filter(c => c.status === 'Rejected').length}</p>
                  <p className="text-zinc-400 text-xs">Rejected</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-400">{clips.filter(c => c.promotedToClipId).length}</p>
                  <p className="text-zinc-400 text-xs">In Library</p>
                </div>
              </div>
              <button
                onClick={seedExampleSubmissions}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded-lg hover:bg-orange-500/30 text-sm"
              >
                <Plus className="w-4 h-4" /> Seed Example Submissions
              </button>
            </div>

            {/* Clip Views & Engagement Stats */}
            <div className="mt-6 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl p-4 border border-blue-500/30">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-400" /> Clip View Activity
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-blue-400">{clipViews.length}</p>
                  <p className="text-zinc-400 text-xs">Total Views</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-cyan-400">{new Set(clipViews.map(v => v.clipId)).size}</p>
                  <p className="text-zinc-400 text-xs">Unique Clips Viewed</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-teal-400">{new Set(clipViews.map(v => v.viewerId)).size}</p>
                  <p className="text-zinc-400 text-xs">Active Viewers</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-sky-400">
                    {clipViews.filter(v => {
                      const viewDate = new Date(v.viewedAt);
                      const today = new Date();
                      return viewDate.toDateString() === today.toDateString();
                    }).length}
                  </p>
                  <p className="text-zinc-400 text-xs">Views Today</p>
                </div>
              </div>

              {/* Most Viewed Clips */}
              {clipViews.length > 0 && (
                <div className="mt-4 pt-4 border-t border-blue-500/20">
                  <p className="text-zinc-400 text-xs mb-2">Most Viewed Clips</p>
                  <div className="space-y-2">
                    {Object.entries(
                      clipViews.reduce((acc, v) => {
                        acc[v.clipId] = (acc[v.clipId] || 0) + 1;
                        return acc;
                      }, {} as Record<string, number>)
                    )
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 5)
                      .map(([clipId, count]) => {
                        const clip = dateNiteClips.find(c => c.id === clipId);
                        const category = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                        return (
                          <div key={clipId} className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-2">
                            <div className="flex items-center gap-2">
                              <span>{category?.emoji || '🎬'}</span>
                              <span className="text-white text-sm truncate max-w-[200px]">{clip?.title || clipId}</span>
                            </div>
                            <span className="text-blue-400 text-sm font-medium">{count} views</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {/* Couple Wishlist Stats */}
            <div className="mt-6 bg-gradient-to-r from-pink-500/10 to-rose-500/10 rounded-xl p-4 border border-pink-500/30">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Heart className="w-4 h-4 text-pink-400" /> Couple Wishlists
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-pink-400">{wishlist.length}</p>
                  <p className="text-zinc-400 text-xs">Total Items</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-rose-400">{wishlist.filter(w => w.status === 'want-to-try').length}</p>
                  <p className="text-zinc-400 text-xs">Want to Try</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">{wishlist.filter(w => w.status === 'tried').length}</p>
                  <p className="text-zinc-400 text-xs">Tried</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-400">{new Set(wishlist.map(w => w.coupleId)).size}</p>
                  <p className="text-zinc-400 text-xs">Couples with Lists</p>
                </div>
              </div>

              {/* Most Wishlisted Clips */}
              {wishlist.length > 0 && (
                <div className="mt-4 pt-4 border-t border-pink-500/20">
                  <p className="text-zinc-400 text-xs mb-2">Most Wishlisted Clips</p>
                  <div className="space-y-2">
                    {Object.entries(
                      wishlist.reduce((acc, w) => {
                        acc[w.clipId] = (acc[w.clipId] || 0) + 1;
                        return acc;
                      }, {} as Record<string, number>)
                    )
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 5)
                      .map(([clipId, count]) => {
                        const clip = dateNiteClips.find(c => c.id === clipId);
                        const category = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                        return (
                          <div key={clipId} className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-2">
                            <div className="flex items-center gap-2">
                              <span>{category?.emoji || '🎬'}</span>
                              <span className="text-white text-sm truncate max-w-[200px]">{clip?.title || clipId}</span>
                            </div>
                            <span className="text-pink-400 text-sm font-medium">{count} couples</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {/* Reviewers with Couple Links */}
            <div className="mt-6 bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-xl p-4 border border-purple-500/30">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-purple-400" /> Linked Reviewer-Couples
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center mb-4">
                <div>
                  <p className="text-2xl font-bold text-purple-400">{reviewers.filter(r => r.coupleId).length}</p>
                  <p className="text-zinc-400 text-xs">Reviewers with Couples</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-indigo-400">{reviewers.filter(r => !r.coupleId).length}</p>
                  <p className="text-zinc-400 text-xs">Solo Reviewers</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-violet-400">{reviewers.filter(r => r.mode === 'couple').length}</p>
                  <p className="text-zinc-400 text-xs">In Couple Mode</p>
                </div>
              </div>

              {reviewers.filter(r => r.coupleId).length > 0 && (
                <div className="space-y-2">
                  {reviewers.filter(r => r.coupleId).slice(0, 5).map(reviewer => {
                    const couple = allCouples.find(c => c.coupleId === reviewer.coupleId);
                    return (
                      <div key={reviewer.id} className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-3">
                        <div>
                          <p className="text-white text-sm">{reviewer.id}</p>
                          <p className="text-zinc-500 text-xs">{reviewer.partnerName || 'No partner name'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-purple-400 text-xs font-mono">{reviewer.coupleId}</p>
                          {couple && (
                            <p className="text-zinc-500 text-xs">{couple.hisName} & {couple.herName}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Clips Tab */}
        {tab === 'clips' && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <select
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                className="bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="all">All Categories ({dateNiteClips.length})</option>
                {CATEGORIES.map(cat => {
                  const count = dateNiteClips.filter(c => getClipCategoryIds(c).includes(cat.id)).length;
                  return (
                    <option key={cat.id} value={cat.id}>
                      {cat.emoji} {cat.name} ({count})
                    </option>
                  );
                })}
              </select>
              <button
                onClick={() => setTab('addclip')}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-zinc-900"
                style={{ backgroundColor: COLORS.lime }}
              >
                <Plus className="w-4 h-4" /> Add Clip
              </button>
            </div>

            <div className="space-y-2">
              {filteredClips.map(c => {
                const cat = CATEGORIES.find(cat => cat.id === c.categoryId);
                const viewCount = clipViews.filter(v => v.clipId === c.id).length;
                const wishlistCount = wishlist.filter(w => w.clipId === c.id).length;
                return (
                  <div key={c.id} className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{cat?.emoji}</span>
                          <span className="font-semibold text-white">{c.title}</span>
                          {viewCount > 0 && (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                              <Eye className="w-3 h-3" /> {viewCount}
                            </span>
                          )}
                          {wishlistCount > 0 && (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-pink-500/20 text-pink-400 text-xs rounded-full">
                              <Heart className="w-3 h-3" /> {wishlistCount}
                            </span>
                          )}
                        </div>
                        <p className="text-zinc-500 text-sm mt-1">{c.description}</p>
                        <p className="text-zinc-400 text-xs mt-2">
                          {c.reviews?.length || 0} reviews &bull; {c.picks?.length || 0} picks &bull; {c.couplesTried || 0} couples tried
                        </p>
                        <a
                          href={c.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400 hover:underline mt-1 block truncate"
                        >
                          {c.link}
                        </a>
                        {/* Show tags and additional categories */}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {c.additionalCategoryIds && c.additionalCategoryIds.length > 0 && (
                            <>
                              {c.additionalCategoryIds.map(catId => {
                                const additionalCat = CATEGORIES.find(cat => cat.id === catId);
                                return additionalCat ? (
                                  <span key={catId} className="px-2 py-0.5 bg-zinc-700 text-zinc-300 text-xs rounded">
                                    +{additionalCat.emoji} {additionalCat.name}
                                  </span>
                                ) : null;
                              })}
                            </>
                          )}
                          {(c.tags || []).length > 0 && (c.tags || []).map(tagId => {
                            const tag = TAGS.find(t => t.id === tagId);
                            return tag ? (
                              <span key={tagId} className={`px-2 py-0.5 text-xs rounded border ${tag.color}`}>
                                {tag.label}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setPreviewClip({ title: c.title, link: c.link })}
                          className="p-2 text-zinc-500 hover:text-lime-400"
                          title="Preview clip"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => startEditClip(c)}
                          className="p-2 text-zinc-500 hover:text-blue-400"
                          title="Edit clip"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteClip(c.id)}
                          className="p-2 text-zinc-500 hover:text-red-400"
                          title="Delete clip"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredClips.length === 0 && (
                <div className="text-center py-8 text-zinc-500">
                  No clips in this category
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add Clip Tab */}
        {tab === 'addclip' && (
          <div className="max-w-lg">
            <h3 className="text-lg font-semibold text-white mb-4">Add New Clip</h3>
            <div className="space-y-4">
              {/* Categories - Multi-select */}
              <div>
                <label className="block text-zinc-400 text-sm mb-2">
                  Categories * <span className="text-zinc-500">(select at least one, first is primary)</span>
                </label>
                <div className="flex flex-wrap gap-2 p-3 bg-zinc-800 rounded-xl border border-zinc-700">
                  {CATEGORIES.map(cat => {
                    const isSelected = newClip.categoryIds.includes(cat.id);
                    const isPrimary = newClip.categoryIds[0] === cat.id;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            // Don't allow removing the last category
                            if (newClip.categoryIds.length > 1) {
                              setNewClip({
                                ...newClip,
                                categoryIds: newClip.categoryIds.filter(id => id !== cat.id)
                              });
                            }
                          } else {
                            setNewClip({
                              ...newClip,
                              categoryIds: [...newClip.categoryIds, cat.id]
                            });
                          }
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                          isSelected
                            ? isPrimary
                              ? 'bg-emerald-500 text-white ring-2 ring-emerald-300'
                              : 'bg-blue-500/30 text-blue-300 ring-1 ring-blue-400'
                            : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                        }`}
                      >
                        <span>{cat.emoji}</span>
                        <span>{cat.name}</span>
                        {isPrimary && <span className="text-xs bg-emerald-600 px-1.5 py-0.5 rounded">1st</span>}
                      </button>
                    );
                  })}
                </div>
                {newClip.categoryIds.length > 1 && (
                  <p className="text-xs text-zinc-500 mt-1">
                    Primary: {CATEGORIES.find(c => c.id === newClip.categoryIds[0])?.name} + {newClip.categoryIds.length - 1} more
                  </p>
                )}
              </div>

              {/* Tags - Multi-select */}
              <div>
                <label className="block text-zinc-400 text-sm mb-2">
                  Tags <span className="text-zinc-500">(optional)</span>
                </label>
                <div className="flex flex-wrap gap-2 p-3 bg-zinc-800 rounded-xl border border-zinc-700">
                  {TAGS.map(tag => {
                    const isSelected = newClip.tags.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            setNewClip({
                              ...newClip,
                              tags: newClip.tags.filter(t => t !== tag.id)
                            });
                          } else {
                            setNewClip({
                              ...newClip,
                              tags: [...newClip.tags, tag.id]
                            });
                          }
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                          isSelected
                            ? tag.color
                            : 'bg-zinc-700 text-zinc-400 border-zinc-600 hover:bg-zinc-600'
                        }`}
                      >
                        {tag.label}
                      </button>
                    );
                  })}
                </div>
                {newClip.tags.length > 0 && (
                  <p className="text-xs text-zinc-500 mt-1">
                    {newClip.tags.length} tag{newClip.tags.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-2">Title *</label>
                <input
                  type="text"
                  value={newClip.title}
                  onChange={e => setNewClip({ ...newClip, title: e.target.value })}
                  placeholder="Clip title..."
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-2">RedGifs Link *</label>
                <input
                  type="url"
                  value={newClip.link}
                  onChange={e => setNewClip({ ...newClip, link: e.target.value })}
                  placeholder="https://redgifs.com/watch/..."
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-2">Description</label>
                <textarea
                  value={newClip.description}
                  onChange={e => setNewClip({ ...newClip, description: e.target.value })}
                  placeholder="Brief description..."
                  rows={3}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white resize-none"
                />
              </div>

              <PrimaryButton
                onClick={addClip}
                disabled={!newClip.title.trim() || !newClip.link.trim() || newClip.categoryIds.length === 0}
                className="w-full"
              >
                <Plus className="w-4 h-4" /> Add Clip
              </PrimaryButton>
            </div>
          </div>
        )}

        {/* Reviewers Tab */}
        {tab === 'reviewers' && (
          <div className="space-y-3">
            {/* Seed Example Button */}
            <div className="flex justify-end mb-2">
              <button
                onClick={seedExampleReviewer}
                className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" /> Seed Example Reviewer
              </button>
            </div>

            {reviewers.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">No reviewers yet</div>
            ) : (
              reviewers.map(r => {
                const isExpanded = expandedReviewer === r.id;
                const recs = reviewerRecommendations[r.id] || [];
                const reviewerClipReviews = dateNiteClips.filter(c => c.reviews?.some(rev => rev.reviewerId === r.id));
                const reviewerPicks = dateNiteClips.filter(c => c.picks?.includes(r.id));
                const pendingQuestionsCount = reviewerFollowUps.filter(fu => fu.reviewerId === r.id && fu.status === 'pending').length;
                const managedCoupleId = r.managedCoupleId || r.coupleId;
                const pendingClipRequestsCount = managedCoupleId
                  ? (allCoupleRequests[managedCoupleId] || []).filter(req =>
                      req.status === 'pending' &&
                      ((req.exampleUrl && req.exampleUrl.trim()) || (req.exampleUrls && req.exampleUrls.filter(u => u.trim()).length > 0))
                    ).length
                  : 0;

                return (
                  <div key={r.id} id={`admin-reviewers-${r.id}`} className="bg-zinc-900 rounded-xl border border-zinc-700 overflow-hidden scroll-mt-4">
                    {/* Header - Always visible */}
                    <div
                      className="p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                      onClick={() => setExpandedReviewer(isExpanded ? null : r.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-white">{r.id}</p>
                            {/* Online status indicator */}
                            {(() => {
                              const presence = isUserOnline('reviewer', r.id);
                              if (presence) {
                                return (
                                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1 animate-pulse">
                                    <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                                    Online • {presence.currentPage === 'home' ? 'Home' : presence.currentPage}
                                  </span>
                                );
                              }
                              return null;
                            })()}
                            {r.coupleId && (
                              <span className="px-2 py-0.5 bg-pink-500/20 text-pink-400 text-xs rounded-full flex items-center gap-1">
                                <Heart className="w-3 h-3" /> Couple
                              </span>
                            )}
                            {pendingQuestionsCount > 0 && (
                              <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" /> {pendingQuestionsCount} pending
                              </span>
                            )}
                            {pendingClipRequestsCount > 0 && (
                              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full flex items-center gap-1">
                                <Film className="w-3 h-3" /> {pendingClipRequestsCount} clip{pendingClipRequestsCount !== 1 ? 's' : ''} pending
                              </span>
                            )}
                          </div>
                          <p className="text-zinc-400 text-xs mt-1">
                            {r.gender} &bull; {r.ageRange} &bull; {r.relationshipStatus || 'N/A'}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className="text-xs text-zinc-500">Code: <span className="text-zinc-300 font-mono">{r.code}</span></span>
                            {r.code && <CopyLinkButton url={reviewerLoginLink(r.code)} className="!py-0.5" />}
                            {r.coupleId && (
                              <span className="text-xs text-zinc-500">Couple: <span className="text-pink-400 font-mono">{r.coupleId}</span></span>
                            )}
                            {peopleReviewers.get(r.id)?.managedCouple && (
                              <span className="text-xs text-zinc-500">Coaching: <span className="text-lime-300">{peopleReviewers.get(r.id)!.managedCouple!.label}</span></span>
                            )}
                            {(() => {
                              const summary = peopleReviewers.get(r.id);
                              if (!summary) return null;
                              return (
                                <>
                                  {summary.lastSeen && !isUserOnline('reviewer', r.id) && (
                                    <span className="text-xs text-zinc-500">Seen {formatLastActive(summary.lastSeen)}</span>
                                  )}
                                  {summary.loginCount > 0 && (
                                    <span className="text-xs text-zinc-500">{summary.loginCount} login{summary.loginCount !== 1 ? 's' : ''}</span>
                                  )}
                                  <span className="text-xs text-zinc-500">{summary.clipReviewCount} clip · {summary.submissionReviewCount} submission reviews</span>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right text-zinc-500 text-xs">
                            {new Date(r.registeredAt).toLocaleDateString()}
                          </div>
                          {isExpanded ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t border-zinc-700 p-4 space-y-4">
                        {/* View All Q&A button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setConsolidatedQAReviewerId(r.id); }}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#c8e265]/10 border border-[#c8e265]/30 text-[#c8e265] rounded-lg hover:bg-[#c8e265]/20 transition-colors text-sm font-medium"
                        >
                          <BookOpen className="w-4 h-4" /> View All Q&A & Copy Profile
                        </button>

                        {/* Activity Tracking - for ALL reviewers */}
                        {(() => {
                          const reviewerViews = clipViews.filter(v => v.viewerId === r.id);
                          const reviewedCount = reviewerClipReviews.length;
                          const picksCount = reviewerPicks.length;

                          // Calculate category coverage
                          const viewedClipIds = reviewerViews.map(v => v.clipId);
                          const viewedCategories = new Set(
                            dateNiteClips
                              .filter(c => viewedClipIds.includes(c.id))
                              .map(c => c.categoryId)
                          );
                          const coverage = (viewedCategories.size / CATEGORIES.length) * 100;

                          return (
                            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                              <h4 className="text-blue-400 font-semibold text-sm flex items-center gap-2 mb-2">
                                <Eye className="w-4 h-4" /> Activity Tracking
                              </h4>

                              {/* Quick Stats */}
                              <div className="grid grid-cols-4 gap-2 mb-3">
                                <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                                  <p className="text-lg font-bold text-blue-400">{reviewerViews.length}</p>
                                  <p className="text-zinc-500 text-xs">Views</p>
                                </div>
                                <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                                  <p className="text-lg font-bold text-purple-400">{reviewedCount}</p>
                                  <p className="text-zinc-500 text-xs">Reviews</p>
                                </div>
                                <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                                  <p className="text-lg font-bold text-yellow-400">{picksCount}</p>
                                  <p className="text-zinc-500 text-xs">Picks</p>
                                </div>
                                <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                                  <p className="text-lg font-bold" style={{ color: coverage > 50 ? COLORS.lime : coverage > 25 ? '#fbbf24' : '#ef4444' }}>
                                    {Math.round(coverage)}%
                                  </p>
                                  <p className="text-zinc-500 text-xs">Coverage</p>
                                </div>
                              </div>

                              {/* Category Coverage Visual */}
                              <div className="mb-3">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-zinc-400 text-xs">Categories Explored</p>
                                  <p className="text-xs font-medium text-zinc-400">
                                    {viewedCategories.size}/{CATEGORIES.length}
                                  </p>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {CATEGORIES.map(cat => (
                                    <span
                                      key={cat.id}
                                      className={`px-2 py-1 rounded text-xs ${
                                        viewedCategories.has(cat.id)
                                          ? 'bg-green-500/20 text-green-400'
                                          : 'bg-zinc-700 text-zinc-500'
                                      }`}
                                      title={`${cat.name} - ${viewedCategories.has(cat.id) ? 'Viewed' : 'Not viewed'}`}
                                    >
                                      {cat.emoji}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              {/* Watch History - Full Expandable */}
                              {reviewerViews.length > 0 ? (
                                <div>
                                  <button
                                    onClick={() => setExpandedWatchHistory(expandedWatchHistory === r.id ? null : r.id)}
                                    className="w-full text-left text-zinc-400 text-xs mb-2 flex items-center justify-between hover:text-zinc-300"
                                  >
                                    <span className="flex items-center gap-1">
                                      <Play className="w-3 h-3" /> Full Watch History ({reviewerViews.length} clips viewed)
                                    </span>
                                    {expandedWatchHistory === r.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                  </button>
                                  <div className={`space-y-1 overflow-y-auto ${expandedWatchHistory === r.id ? 'max-h-96' : 'max-h-32'}`}>
                                    {reviewerViews
                                      .sort((a, b) => new Date(b.viewedAt).getTime() - new Date(a.viewedAt).getTime())
                                      .slice(0, expandedWatchHistory === r.id ? undefined : 10)
                                      .map((view, i) => {
                                        const clip = dateNiteClips.find(c => c.id === view.clipId);
                                        const cat = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                                        return (
                                          <div key={i} className="flex items-center justify-between bg-zinc-800/50 rounded px-2 py-1 text-xs">
                                            <div className="flex items-center gap-2 truncate">
                                              <span>{cat?.emoji || '🎬'}</span>
                                              <span className="text-zinc-300 truncate">{clip?.title || view.clipId}</span>
                                              <span className="text-zinc-600">({view.source})</span>
                                            </div>
                                            <span className="text-zinc-500 flex-shrink-0">
                                              {new Date(view.viewedAt).toLocaleDateString()} {new Date(view.viewedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                          </div>
                                        );
                                      })}
                                  </div>
                                  {reviewerViews.length > 10 && expandedWatchHistory !== r.id && (
                                    <button
                                      onClick={() => setExpandedWatchHistory(r.id)}
                                      className="text-blue-400 text-xs mt-1 hover:underline"
                                    >
                                      Show all {reviewerViews.length} views →
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <p className="text-zinc-500 text-xs text-center py-2">No activity recorded yet</p>
                              )}
                            </div>
                          );
                        })()}

                        {/* Reviewer Signup Profile */}
                        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
                          <h4 className="text-zinc-300 font-semibold text-sm flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4" /> Signup Profile
                          </h4>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                            <div><span className="text-zinc-500">Gender:</span> <span className="text-white">{r.gender}</span></div>
                            <div><span className="text-zinc-500">Age:</span> <span className="text-white">{r.ageRange}</span></div>
                            <div><span className="text-zinc-500">Relationship:</span> <span className="text-white">{r.relationshipStatus}</span></div>
                            {r.sexuallyActive && <div><span className="text-zinc-500">Sexually Active:</span> <span className="text-white">{r.sexuallyActive}</span></div>}
                            <div><span className="text-zinc-500">Watches Content:</span> <span className="text-white">{r.watchesAdultContent}</span></div>
                            <div><span className="text-zinc-500">Familiar w/ Products:</span> <span className="text-white">{r.familiarWithProducts}</span></div>
                            {r.adultContentThemes && <div className="col-span-2"><span className="text-zinc-500">Content Themes:</span> <span className="text-white">{r.adultContentThemes}</span></div>}
                            {r.preferredStyle && <div className="col-span-2"><span className="text-zinc-500">Preferred Style:</span> <span className="text-white">{r.preferredStyle}</span></div>}
                          </div>
                          {/* Section B: Load Boost */}
                          <div className="mt-2 pt-2 border-t border-zinc-700">
                            <p className="text-blue-400 text-xs font-medium mb-1">Load Boost</p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                              <div><span className="text-zinc-500">Enjoys Volume Content:</span> <span className="text-white">{r.enjoysVolumeContent}</span></div>
                              {r.triedLoadBoost && <div><span className="text-zinc-500">Tried Load Boost:</span> <span className="text-white">{r.triedLoadBoost}</span></div>}
                              {r.loadBoostExperience && <div className="col-span-2"><span className="text-zinc-500">Experience:</span> <span className="text-white">{r.loadBoostExperience}</span></div>}
                              {r.partnerTypicalVolume && <div><span className="text-zinc-500">Partner Volume:</span> <span className="text-white">{r.partnerTypicalVolume}</span></div>}
                              {r.partnerVolumeLikeDislike && <div className="col-span-2"><span className="text-zinc-500">Volume Like/Dislike:</span> <span className="text-white">{r.partnerVolumeLikeDislike}</span></div>}
                              {r.wantPartnerLoadBoost && <div><span className="text-zinc-500">Want Partner on LB:</span> <span className="text-white">{r.wantPartnerLoadBoost}</span></div>}
                              {r.enjoysSwallowing && <div><span className="text-zinc-500">Enjoys Swallowing:</span> <span className="text-white">{r.enjoysSwallowing}</span></div>}
                              {r.swallowingDescription && <div className="col-span-2"><span className="text-zinc-500">Swallowing Detail:</span> <span className="text-white">{r.swallowingDescription}</span></div>}
                              {r.wouldEnjoyBetterTaste && <div><span className="text-zinc-500">Better Taste:</span> <span className="text-white">{r.wouldEnjoyBetterTaste}</span></div>}
                              {r.enjoysFacials && <div><span className="text-zinc-500">Enjoys Facials:</span> <span className="text-white">{r.enjoysFacials}</span></div>}
                              {r.volumeMakesDifference && <div className="col-span-2"><span className="text-zinc-500">Volume Difference:</span> <span className="text-white">{r.volumeMakesDifference}</span></div>}
                            </div>
                          </div>
                          {/* Section C: Soaking Wet */}
                          {(r.canSquirt || r.enjoysSquirtingContent || r.usedArousalProducts || r.typicalWetness) && (
                            <div className="mt-2 pt-2 border-t border-zinc-700">
                              <p className="text-pink-400 text-xs font-medium mb-1">Soaking Wet</p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                                {r.canSquirt && <div><span className="text-zinc-500">Can Squirt:</span> <span className="text-white">{r.canSquirt}</span></div>}
                                {r.squirtScenarioDescription && <div className="col-span-2"><span className="text-zinc-500">Squirt Scenario:</span> <span className="text-white">{r.squirtScenarioDescription}</span></div>}
                                {r.wantToSquirtMore && <div><span className="text-zinc-500">Want More:</span> <span className="text-white">{r.wantToSquirtMore}</span></div>}
                                {r.usedArousalProducts && <div><span className="text-zinc-500">Arousal Products:</span> <span className="text-white">{r.usedArousalProducts}</span></div>}
                                {r.orgasmFromSex && <div><span className="text-zinc-500">Orgasm from Sex:</span> <span className="text-white">{r.orgasmFromSex}</span></div>}
                                {r.typicalWetness && <div><span className="text-zinc-500">Wetness Level:</span> <span className="text-white">{r.typicalWetness}/5</span></div>}
                                {r.wetnessNotes && <div className="col-span-2"><span className="text-zinc-500">Wetness Notes:</span> <span className="text-white">{r.wetnessNotes}</span></div>}
                                {r.wantToBeWetter && <div><span className="text-zinc-500">Want Wetter:</span> <span className="text-white">{r.wantToBeWetter}</span></div>}
                                {r.enjoysSquirtingContent && <div><span className="text-zinc-500">Enjoys Squirt Content:</span> <span className="text-white">{r.enjoysSquirtingContent}</span></div>}
                                {r.wantMoreWithPartner && <div><span className="text-zinc-500">More w/ Partner:</span> <span className="text-white">{r.wantMoreWithPartner}</span></div>}
                                {r.partnerUsedArousalProducts && <div><span className="text-zinc-500">Partner Products:</span> <span className="text-white">{r.partnerUsedArousalProducts}</span></div>}
                              </div>
                            </div>
                          )}
                          {/* Section D: Sexual Experience */}
                          {(r.lastSexualActivity || r.facialExperience || r.oralExperience || r.analExperience || r.favoriteSexualMemory || r.fantasiesNotTried || r.biggestTurnOns || r.dealBreakers) && (
                            <div className="mt-2 pt-2 border-t border-zinc-700 space-y-1.5">
                              <p className="text-orange-400 text-xs font-medium mb-1">Sexual Experience</p>
                              {r.lastSexualActivity && <div className="text-xs"><span className="text-zinc-500">Last Activity:</span> <span className="text-zinc-300">"{r.lastSexualActivity}"</span></div>}
                              {r.facialExperience && <div className="text-xs"><span className="text-zinc-500">Facials:</span> <span className="text-zinc-300">"{r.facialExperience}"</span></div>}
                              {r.oralExperience && <div className="text-xs"><span className="text-zinc-500">Oral:</span> <span className="text-zinc-300">"{r.oralExperience}"</span></div>}
                              {r.analExperience && <div className="text-xs"><span className="text-zinc-500">Anal:</span> <span className="text-zinc-300">"{r.analExperience}"</span></div>}
                              {r.favoriteSexualMemory && <div className="text-xs"><span className="text-zinc-500">Favorite Memory:</span> <span className="text-zinc-300">"{r.favoriteSexualMemory}"</span></div>}
                              {r.fantasiesNotTried && <div className="text-xs"><span className="text-zinc-500">Fantasies:</span> <span className="text-zinc-300">"{r.fantasiesNotTried}"</span></div>}
                              {r.biggestTurnOns && <div className="text-xs"><span className="text-zinc-500">Turn Ons:</span> <span className="text-zinc-300">"{r.biggestTurnOns}"</span></div>}
                              {r.dealBreakers && <div className="text-xs"><span className="text-zinc-500">Deal Breakers:</span> <span className="text-zinc-300">"{r.dealBreakers}"</span></div>}
                            </div>
                          )}
                          {/* Free text answers */}
                          {(r.adultContentExample || r.whatMakesGoodContent || r.whatFeelsFake || r.relevantExperience || r.whyInterested) && (
                            <div className="mt-2 pt-2 border-t border-zinc-700 space-y-1.5">
                              {r.whatMakesGoodContent && (
                                <div className="text-xs"><span className="text-zinc-500">Good Content:</span> <span className="text-zinc-300">"{r.whatMakesGoodContent}"</span></div>
                              )}
                              {r.whatFeelsFake && (
                                <div className="text-xs"><span className="text-zinc-500">Feels Fake:</span> <span className="text-zinc-300">"{r.whatFeelsFake}"</span></div>
                              )}
                              {r.adultContentExample && (
                                <div className="text-xs"><span className="text-zinc-500">Content Example:</span> <span className="text-zinc-300">"{r.adultContentExample}"</span></div>
                              )}
                              {r.relevantExperience && (
                                <div className="text-xs"><span className="text-zinc-500">Experience:</span> <span className="text-zinc-300">"{r.relevantExperience}"</span></div>
                              )}
                              {r.whyInterested && (
                                <div className="text-xs"><span className="text-zinc-500">Why Interested:</span> <span className="text-zinc-300">"{r.whyInterested}"</span></div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Couple Info */}
                        {r.coupleId && (() => {
                          const linkedCouple = allCouples.find(c => c.coupleId === r.coupleId);
                          const reviewerWishlist = wishlist.filter(w => w.coupleId === r.coupleId);
                          const coupleOnline = isUserOnline('couple', r.coupleId);
                          return (
                            <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-3">
                              <h4 className="text-pink-400 font-semibold text-sm flex items-center gap-2 mb-2">
                                <Heart className="w-4 h-4" /> Linked Couple Profile
                                {coupleOnline && (
                                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1 animate-pulse ml-auto">
                                    <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                                    Couple Online • {coupleOnline.currentPage}
                                  </span>
                                )}
                              </h4>
                              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                                <p className="text-zinc-400">Code: <span className="text-white font-mono">{r.coupleId}</span></p>
                                <p className="text-zinc-400">Partner: <span className="text-white">{r.partnerName || 'Not set'}</span></p>
                                {linkedCouple && (
                                  <>
                                    <p className="text-zinc-400">Couple Names: <span className="text-white">{linkedCouple.hisName} & {linkedCouple.herName}</span></p>
                                    <p className="text-zinc-400">Setup: <span className="text-white">{new Date(linkedCouple.setupAt).toLocaleDateString()}</span></p>
                                    <p className="text-zinc-400">Activities Tried: <span className="text-white">{linkedCouple.tried?.length || 0}</span></p>
                                    <p className="text-zinc-400">Favorites: <span className="text-white">{linkedCouple.favorites?.length || 0}</span></p>
                                  </>
                                )}
                                <p className="text-zinc-400">Mode: <span className={`font-medium ${r.mode === 'couple' ? 'text-pink-400' : 'text-zinc-300'}`}>{r.mode || 'reviewer'}</span></p>
                              </div>

                              {/* Couple-Specific Stats */}
                              <div className="grid grid-cols-3 gap-2 mb-3">
                                <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                                  <p className="text-lg font-bold text-pink-400">{reviewerWishlist.filter(w => w.status === 'want-to-try').length}</p>
                                  <p className="text-zinc-500 text-xs">Wishlist</p>
                                </div>
                                <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                                  <p className="text-lg font-bold text-green-400">{linkedCouple?.tried?.length || 0}</p>
                                  <p className="text-zinc-500 text-xs">Tried</p>
                                </div>
                                <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                                  <p className="text-lg font-bold text-yellow-400">{linkedCouple?.favorites?.length || 0}</p>
                                  <p className="text-zinc-500 text-xs">Favorites</p>
                                </div>
                              </div>

                              {/* Detailed Wishlist Items */}
                              {reviewerWishlist.length > 0 && (
                                <div className="mb-3">
                                  <button
                                    onClick={() => setExpandedWishlist(expandedWishlist === r.id ? null : r.id)}
                                    className="w-full text-left text-pink-400 text-xs mb-2 flex items-center justify-between hover:text-pink-300"
                                  >
                                    <span>💕 Wishlist Items ({reviewerWishlist.length})</span>
                                    {expandedWishlist === r.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                  </button>
                                  {expandedWishlist === r.id && (
                                    <div className="space-y-1 max-h-48 overflow-y-auto">
                                      {reviewerWishlist.map(item => {
                                        const clip = dateNiteClips.find(c => c.id === item.clipId);
                                        const cat = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                                        return (
                                          <div key={item.id} className="flex items-center justify-between bg-zinc-800/50 rounded px-2 py-1.5 text-xs">
                                            <div className="flex items-center gap-2 truncate">
                                              <span>{cat?.emoji || '🎬'}</span>
                                              <span className="text-zinc-300 truncate">{item.clipTitle}</span>
                                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                                item.status === 'want-to-try' ? 'bg-pink-500/20 text-pink-400' :
                                                item.status === 'tried' ? 'bg-green-500/20 text-green-400' :
                                                'bg-zinc-700 text-zinc-400'
                                              }`}>
                                                {item.status}
                                              </span>
                                            </div>
                                            <span className="text-zinc-500 flex-shrink-0">{new Date(item.addedAt).toLocaleDateString()}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Tried Clips with Ratings */}
                              {linkedCouple?.tried && linkedCouple.tried.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-green-400 text-xs mb-2">✅ Tried Activities ({(linkedCouple.tried || []).length})</p>
                                  <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {(linkedCouple.tried || []).slice(0, 5).map((tried, i) => {
                                      const clip = dateNiteClips.find(c => c.id === tried.clipId);
                                      const cat = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                                      return (
                                        <div key={i} className="bg-zinc-800/50 rounded px-2 py-1.5 text-xs">
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <span>{cat?.emoji || '🎬'}</span>
                                              <span className="text-zinc-300">{clip?.title || tried.clipId}</span>
                                            </div>
                                            <span className="text-green-400">{tried.rating}</span>
                                          </div>
                                          {(tried.hisThoughts || tried.herThoughts) && (
                                            <div className="mt-1 grid grid-cols-2 gap-2 text-zinc-400">
                                              {tried.hisThoughts && <p><span className="text-blue-400">Him:</span> "{tried.hisThoughts}"</p>}
                                              {tried.herThoughts && <p><span className="text-pink-400">Her:</span> "{tried.herThoughts}"</p>}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                    {(linkedCouple.tried || []).length > 5 && (
                                      <p className="text-zinc-500 text-xs text-center">+{(linkedCouple.tried || []).length - 5} more</p>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Favorites with Notes */}
                              {linkedCouple?.favorites && linkedCouple.favorites.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-yellow-400 text-xs mb-2">⭐ Favorites ({(linkedCouple.favorites || []).length})</p>
                                  <div className="flex flex-wrap gap-1">
                                    {(linkedCouple.favorites || []).map((favId, i) => {
                                      const clip = dateNiteClips.find(c => c.id === favId);
                                      const cat = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                                      const note = linkedCouple.favoriteNotes?.[favId];
                                      return (
                                        <span
                                          key={i}
                                          className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded"
                                          title={note || clip?.title}
                                        >
                                          {cat?.emoji} {clip?.title?.slice(0, 20) || favId}
                                          {note && ' 📝'}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Scheduled Activities */}
                              {r.scheduledActivities && r.scheduledActivities.length > 0 && (
                                <div className="mb-3">
                                  <button
                                    onClick={() => setExpandedSchedule(expandedSchedule === r.id ? null : r.id)}
                                    className="w-full text-left text-cyan-400 text-xs mb-2 flex items-center justify-between hover:text-cyan-300"
                                  >
                                    <span>📅 Scheduled Activities ({r.scheduledActivities.length})</span>
                                    {expandedSchedule === r.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                  </button>
                                  {expandedSchedule === r.id && (
                                    <div className="space-y-1 max-h-48 overflow-y-auto">
                                      {r.scheduledActivities
                                        .sort((a, b) => new Date(b.scheduledDate).getTime() - new Date(a.scheduledDate).getTime())
                                        .map(activity => {
                                          const clip = dateNiteClips.find(c => c.id === activity.clipId);
                                          const cat = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                                          const isPast = new Date(activity.scheduledDate) < new Date();
                                          return (
                                            <div key={activity.id} className={`bg-zinc-800/50 rounded px-2 py-1.5 text-xs ${activity.completedAt ? 'border-l-2 border-green-500' : isPast ? 'border-l-2 border-amber-500' : ''}`}>
                                              <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                  <span>{cat?.emoji || '🎬'}</span>
                                                  <span className="text-zinc-300">{clip?.title || activity.clipId}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  {activity.completedAt ? (
                                                    <span className="text-green-400">✓ Done</span>
                                                  ) : isPast ? (
                                                    <span className="text-amber-400">Missed</span>
                                                  ) : (
                                                    <span className="text-cyan-400">Upcoming</span>
                                                  )}
                                                  <span className="text-zinc-500">{new Date(activity.scheduledDate).toLocaleDateString()}</span>
                                                </div>
                                              </div>
                                              {activity.notes && <p className="text-zinc-400 mt-1">"{activity.notes}"</p>}
                                            </div>
                                          );
                                        })}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Category Q&A Responses */}
                              {linkedCouple?.categoryQA && Object.keys(linkedCouple.categoryQA).length > 0 && (
                                <div className="mb-3 pt-3 border-t border-zinc-700">
                                  <p className="text-purple-400 text-xs mb-2">💬 Category Q&A Responses</p>
                                  <div className="space-y-2">
                                    {Object.entries(linkedCouple.categoryQA).map(([catId, qa]) => {
                                      const cat = CATEGORIES.find(c => c.id === catId);
                                      const answerCount = Object.keys(qa.answers || {}).length;
                                      return (
                                        <div key={catId} className="bg-zinc-800/50 rounded-lg p-2">
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="text-white text-xs flex items-center gap-1">
                                              {cat?.emoji} {cat?.name || catId}
                                            </span>
                                            <span className="text-zinc-500 text-xs">{answerCount} answers</span>
                                          </div>
                                          <div className="space-y-1">
                                            {Object.entries(qa.answers || {}).map(([qId, answer]) => (
                                              <p key={qId} className="text-zinc-400 text-xs pl-2 border-l border-purple-500/30">
                                                "{answer}"
                                              </p>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* His Profile Stats */}
                              {linkedCouple?.hisProfile && (linkedCouple.hisProfile.size || linkedCouple.hisProfile.girth || linkedCouple.hisProfile.cumIntensity) && (
                                <div className="mb-3 pt-3 border-t border-zinc-700">
                                  <p className="text-blue-400 text-xs mb-2">🍆 His Profile Stats</p>
                                  <div className="flex flex-wrap gap-3 text-center bg-zinc-800/50 rounded-lg p-2">
                                    {linkedCouple.hisProfile.size && (
                                      <div>
                                        <span className="text-lg">{HIS_SIZE_SCALE.find(s => s.value === linkedCouple.hisProfile?.size)?.emoji}</span>
                                        <p className="text-xs text-zinc-400">{HIS_SIZE_SCALE.find(s => s.value === linkedCouple.hisProfile?.size)?.label}</p>
                                      </div>
                                    )}
                                    {linkedCouple.hisProfile.girth && (
                                      <div>
                                        <span className="text-lg">{HIS_GIRTH_SCALE.find(s => s.value === linkedCouple.hisProfile?.girth)?.emoji}</span>
                                        <p className="text-xs text-zinc-400">{HIS_GIRTH_SCALE.find(s => s.value === linkedCouple.hisProfile?.girth)?.size}</p>
                                      </div>
                                    )}
                                    {linkedCouple.hisProfile.cumIntensity && (
                                      <div>
                                        <span className="text-lg">{HIS_CUM_SCALE.find(s => s.value === linkedCouple.hisProfile?.cumIntensity)?.emoji}</span>
                                        <p className="text-xs text-zinc-400">{HIS_CUM_SCALE.find(s => s.value === linkedCouple.hisProfile?.cumIntensity)?.label}</p>
                                      </div>
                                    )}
                                    {linkedCouple.hisProfile.cumConsistency && (
                                      <div>
                                        <span className="text-lg">{HIS_CONSISTENCY_SCALE.find(s => s.value === linkedCouple.hisProfile?.cumConsistency)?.emoji}</span>
                                        <p className="text-xs text-zinc-400">{HIS_CONSISTENCY_SCALE.find(s => s.value === linkedCouple.hisProfile?.cumConsistency)?.label}</p>
                                      </div>
                                    )}
                                    {linkedCouple.hisProfile.cumTaste && (
                                      <div>
                                        <span className="text-lg">{HIS_TASTE_SCALE.find(s => s.value === linkedCouple.hisProfile?.cumTaste)?.emoji}</span>
                                        <p className="text-xs text-zinc-400">{HIS_TASTE_SCALE.find(s => s.value === linkedCouple.hisProfile?.cumTaste)?.label}</p>
                                      </div>
                                    )}
                                    {linkedCouple.hisProfile.precumProduction && (
                                      <div>
                                        <span className="text-lg">{HIS_PRECUM_SCALE.find(s => s.value === linkedCouple.hisProfile?.precumProduction)?.emoji}</span>
                                        <p className="text-xs text-zinc-400">Pre: {HIS_PRECUM_SCALE.find(s => s.value === linkedCouple.hisProfile?.precumProduction)?.label}</p>
                                      </div>
                                    )}
                                  </div>
                                  {/* Notes */}
                                  {(linkedCouple.hisProfile.consistencyNotes || linkedCouple.hisProfile.tasteNotes || linkedCouple.hisProfile.precumNotes) && (
                                    <div className="mt-2 space-y-1 text-xs">
                                      {linkedCouple.hisProfile.consistencyNotes && (
                                        <p className="text-zinc-400"><span className="text-purple-400">Consistency:</span> "{linkedCouple.hisProfile.consistencyNotes}"</p>
                                      )}
                                      {linkedCouple.hisProfile.tasteNotes && (
                                        <p className="text-zinc-400"><span className="text-amber-400">Taste:</span> "{linkedCouple.hisProfile.tasteNotes}"</p>
                                      )}
                                      {linkedCouple.hisProfile.precumNotes && (
                                        <p className="text-zinc-400"><span className="text-blue-400">Precum:</span> "{linkedCouple.hisProfile.precumNotes}"</p>
                                      )}
                                    </div>
                                  )}
                                  {/* Visual Sharing */}
                                  {linkedCouple.hisProfile.visualSharingOpen && (
                                    <div className="mt-2 pt-2 border-t border-zinc-700">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span>📸</span>
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                          linkedCouple.hisProfile.visualSharingOpen === 'yes' ? 'bg-green-500/20 text-green-400' :
                                          linkedCouple.hisProfile.visualSharingOpen === 'maybe' ? 'bg-amber-500/20 text-amber-400' :
                                          'bg-zinc-500/20 text-zinc-400'
                                        }`}>
                                          Visuals: {linkedCouple.hisProfile.visualSharingOpen === 'yes' ? 'Open to sharing!' : linkedCouple.hisProfile.visualSharingOpen === 'maybe' ? 'Maybe' : 'No'}
                                        </span>
                                      </div>
                                      {linkedCouple.hisProfile.visualUrls && (
                                        <div className="space-y-1 text-xs">
                                          {linkedCouple.hisProfile.visualUrls.sizeUrl && (
                                            <a href={linkedCouple.hisProfile.visualUrls.sizeUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-pink-400 hover:underline">
                                              📏 Size verification →
                                            </a>
                                          )}
                                          {linkedCouple.hisProfile.visualUrls.cumUrl && (
                                            <a href={linkedCouple.hisProfile.visualUrls.cumUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-pink-400 hover:underline">
                                              💦 Cum verification →
                                            </a>
                                          )}
                                          {linkedCouple.hisProfile.visualUrls.precumUrl && (
                                            <a href={linkedCouple.hisProfile.visualUrls.precumUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-pink-400 hover:underline">
                                              💧 Precum verification →
                                            </a>
                                          )}
                                          {linkedCouple.hisProfile.visualUrls.otherUrl && (
                                            <a href={linkedCouple.hisProfile.visualUrls.otherUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-pink-400 hover:underline">
                                              🔗 Other visuals →
                                            </a>
                                          )}
                                        </div>
                                      )}
                                      {linkedCouple.hisProfile.visualSharingNotes && (
                                        <p className="text-zinc-400 text-xs mt-1 italic">"{linkedCouple.hisProfile.visualSharingNotes}"</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Couple Feedback */}
                              {linkedCouple && (linkedCouple.wouldSubmitClips || linkedCouple.productExcitement || linkedCouple.generalFeedback) && (
                                <div className="mb-3 pt-3 border-t border-zinc-700">
                                  <p className="text-amber-400 text-xs mb-2">📝 Couple Feedback</p>
                                  <div className="space-y-1 text-xs">
                                    {linkedCouple.wouldSubmitClips && (
                                      <p className="text-zinc-400">Would submit clips: <span className={`font-medium ${linkedCouple.wouldSubmitClips === 'yes' ? 'text-green-400' : linkedCouple.wouldSubmitClips === 'maybe' ? 'text-amber-400' : 'text-zinc-500'}`}>{linkedCouple.wouldSubmitClips}</span></p>
                                    )}
                                    {linkedCouple.productExcitement && (
                                      <p className="text-zinc-400">Product excitement: <span className="text-yellow-400">{'⭐'.repeat(linkedCouple.productExcitement)}</span></p>
                                    )}
                                    {linkedCouple.generalFeedback && (
                                      <p className="text-zinc-300 bg-zinc-800/50 rounded p-2 mt-1">"{linkedCouple.generalFeedback}"</p>
                                    )}
                                  </div>
                                </div>
                              )}

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedCoupleSchedule({ coupleId: r.coupleId!, partnerName: r.partnerName, reviewerId: r.id });
                                }}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 rounded-lg text-sm font-medium transition-colors"
                              >
                                <Calendar className="w-4 h-4" /> View Full Schedule
                              </button>
                            </div>
                          );
                        })()}

                        {/* Couple Experience Questionnaire Data */}
                        {(r.interestedInCoupleExperience || r.wouldSubmitClips || r.comfortWithAnonymousFeedback || r.interestedInScheduledActivities || r.interestedInCuratedExperiences || r.partnerSizePreference || r.wetnessImportance || r.openToNewThings || r.coupleExperienceNotes) && (
                          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                            <h4 className="text-purple-400 font-semibold text-sm flex items-center gap-2 mb-2">
                              <HeartHandshake className="w-4 h-4" /> Couple Experience Preferences
                            </h4>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              {r.interestedInCoupleExperience && (
                                <div className="col-span-2">
                                  <span className="text-zinc-500">Interest Level:</span>{' '}
                                  <span className={`font-medium ${r.interestedInCoupleExperience === 'Yes, definitely' ? 'text-green-400' : r.interestedInCoupleExperience === 'Curious' ? 'text-amber-400' : 'text-zinc-400'}`}>
                                    {r.interestedInCoupleExperience}
                                  </span>
                                </div>
                              )}
                              {r.wouldSubmitClips && (
                                <div>
                                  <span className="text-zinc-500">Submit Clips:</span>{' '}
                                  <span className="text-white">{r.wouldSubmitClips}</span>
                                </div>
                              )}
                              {r.comfortWithAnonymousFeedback && (
                                <div>
                                  <span className="text-zinc-500">Anonymous Feedback:</span>{' '}
                                  <span className="text-white">{r.comfortWithAnonymousFeedback}</span>
                                </div>
                              )}
                              {r.interestedInScheduledActivities && (
                                <div>
                                  <span className="text-zinc-500">Scheduled Activities:</span>{' '}
                                  <span className="text-white">{r.interestedInScheduledActivities}</span>
                                </div>
                              )}
                              {r.interestedInCuratedExperiences && (
                                <div>
                                  <span className="text-zinc-500">Curated Experiences:</span>{' '}
                                  <span className="text-white">{r.interestedInCuratedExperiences}</span>
                                </div>
                              )}
                              {r.partnerSizePreference && (
                                <div>
                                  <span className="text-zinc-500">Size Preference:</span>{' '}
                                  <span className="text-white">{r.partnerSizePreference}</span>
                                </div>
                              )}
                              {r.partnerSizeDescription && (
                                <div>
                                  <span className="text-zinc-500">Size Description:</span>{' '}
                                  <span className="text-white">{r.partnerSizeDescription}</span>
                                </div>
                              )}
                              {r.wetnessImportance && (
                                <div>
                                  <span className="text-zinc-500">Wetness Importance:</span>{' '}
                                  <span className="text-white">{r.wetnessImportance}</span>
                                </div>
                              )}
                              {r.openToNewThings && (
                                <div>
                                  <span className="text-zinc-500">Open to New Things:</span>{' '}
                                  <span className="text-white">{r.openToNewThings}</span>
                                </div>
                              )}
                            </div>
                            {r.coupleExperienceNotes && (
                              <div className="mt-2 pt-2 border-t border-purple-500/20">
                                <p className="text-zinc-500 text-xs">Notes:</p>
                                <p className="text-zinc-300 text-xs mt-1 bg-zinc-800/50 rounded p-2">"{r.coupleExperienceNotes}"</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Couple Preferences from Onboarding - Interactive with Follow-ups */}
                        {r.couplePreferences && r.couplePreferences.length > 0 && (() => {
                          // Get all sent questions for this reviewer
                          const sentQuestions = reviewerFollowUps.filter(fu => fu.reviewerId === r.id);
                          const sentQuestionTexts = sentQuestions.map(fu => fu.question);

                          return (
                          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                            <h4 className="text-emerald-400 font-semibold text-sm flex items-center gap-2 mb-2">
                              🎯 Couple Preferences ({r.couplePreferences.length} rated)
                            </h4>
                            <div className="space-y-3">
                              {r.couplePreferences.map((pref, i) => {
                                const cat = CATEGORIES.find(c => c.id === pref.categoryId);
                                const ratingColors: Record<string, string> = {
                                  'try': 'text-green-400 bg-green-500/20 border-green-500/30',
                                  'maybe': 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30',
                                  'skip': 'text-red-400 bg-red-500/20 border-red-500/30'
                                };
                                const ratingLabels: Record<string, string> = {
                                  'try': "✓ We'd Try",
                                  'maybe': '~ Maybe',
                                  'skip': '✗ Skip'
                                };
                                // Find similar clips in same category (checking both primary and additional categories)
                                const similarClips = dateNiteClips.filter(c => {
                                  if (c.id === pref.clipId) return false;
                                  const clipCats = getClipCategoryIds(c);
                                  return clipCats.includes(pref.categoryId);
                                }).slice(0, 3);

                                // Generate follow-up questions based on rating
                                // Include watch button for the referenced clip
                                const clipWatch = `\n\n[WATCH_CLIP:${pref.clipId}:${pref.clipTitle}]`;
                                const followUpQuestions: { label: string; question: string; context: string }[] = [];
                                if (pref.rating === 'try') {
                                  followUpQuestions.push(
                                    { label: 'What excited you?', question: `You said you'd try "${pref.clipTitle}" - what about it excited you or caught your attention? Was it the scenario, the people, a specific moment?${clipWatch}`, context: `Follow-up: ${pref.clipTitle} (Try)` },
                                    { label: 'Done before?', question: `Have you done anything like "${pref.clipTitle}" before? If yes, how did it go? If not, what's stopped you?${clipWatch}`, context: `Follow-up: ${pref.clipTitle} (Experience)` },
                                    { label: 'How would you want it?', question: `If you were to try something like "${pref.clipTitle}", how would you want it to go? Any specific details about how you'd set it up or what you'd want your partner to do?${clipWatch}`, context: `Follow-up: ${pref.clipTitle} (Preferences)` }
                                  );
                                } else if (pref.rating === 'skip') {
                                  followUpQuestions.push(
                                    { label: 'Why skip?', question: `You skipped "${pref.clipTitle}" - was there something specific that turned you off? Was it the content type, the people, or something else?${clipWatch}`, context: `Follow-up: ${pref.clipTitle} (Skip reason)` },
                                    { label: 'Ever tried?', question: `Have you ever tried anything like "${pref.clipTitle}"? If yes, what was your experience? If not, is it a firm boundary or just not appealing right now?${clipWatch}`, context: `Follow-up: ${pref.clipTitle} (Experience)` },
                                    { label: 'What would change mind?', question: `Is there any version of "${pref.clipTitle}" that might appeal to you? Different people, different scenario, different approach? Or is it completely off the table?${clipWatch}`, context: `Follow-up: ${pref.clipTitle} (Conditions)` }
                                  );
                                } else if (pref.rating === 'maybe') {
                                  followUpQuestions.push(
                                    { label: 'What\'s holding back?', question: `You said "maybe" to "${pref.clipTitle}" - what's making you hesitant? Is it something about the clip specifically or the activity in general?${clipWatch}`, context: `Follow-up: ${pref.clipTitle} (Hesitation)` },
                                    { label: 'What would make it a Try?', question: `What would need to be different about "${pref.clipTitle}" for it to become a "We'd try"? Different scenario, different people, more context?${clipWatch}`, context: `Follow-up: ${pref.clipTitle} (Conditions)` },
                                    { label: 'Curious or uncertain?', question: `With "${pref.clipTitle}" - are you curious but nervous, or genuinely unsure if you'd enjoy it? What's the underlying feeling?${clipWatch}`, context: `Follow-up: ${pref.clipTitle} (Feelings)` }
                                  );
                                }

                                // Check which questions have been sent
                                const availableQuestions = followUpQuestions.filter(fq =>
                                  !sentQuestionTexts.some(sent => sent.includes(fq.question.slice(0, 40)))
                                );
                                const sentQuestionsForPref = followUpQuestions.filter(fq =>
                                  sentQuestionTexts.some(sent => sent.includes(fq.question.slice(0, 40)))
                                );

                                // Check which similar clips have been shared
                                const sharedClipIds = sentQuestions
                                  .filter(fu => fu.context?.includes('Similar clip suggestion'))
                                  .map(fu => {
                                    const match = fu.context?.match(/Similar clip suggestion: (.+)/);
                                    return match ? match[1] : null;
                                  })
                                  .filter(Boolean);

                                return (
                                  <div key={i} className={`rounded-lg border ${ratingColors[pref.rating]} p-3`}>
                                    {/* Header */}
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-lg">{cat?.emoji || '📹'}</span>
                                        <span className="text-white font-medium">{pref.clipTitle}</span>
                                      </div>
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${ratingColors[pref.rating]}`}>
                                        {ratingLabels[pref.rating] || pref.rating}
                                      </span>
                                    </div>

                                    {/* Follow-up Questions */}
                                    <div className="mb-2">
                                      <p className="text-zinc-500 text-xs mb-1">Ask follow-up:</p>
                                      <div className="flex flex-wrap gap-1">
                                        {/* Available (not sent) questions */}
                                        {availableQuestions.map((fq, j) => (
                                          <button
                                            key={j}
                                            onClick={() => queueFollowUp(r.id, r.code, fq.question, fq.context)}
                                            className="px-2 py-1 bg-zinc-700/50 hover:bg-zinc-600/50 text-zinc-300 text-xs rounded transition-colors flex items-center gap-1"
                                            title={fq.question}
                                          >
                                            <Send className="w-3 h-3" /> {fq.label}
                                          </button>
                                        ))}
                                        {/* Already sent questions - show as disabled with checkmark */}
                                        {sentQuestionsForPref.map((fq, j) => (
                                          <span
                                            key={`sent-${j}`}
                                            className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded flex items-center gap-1 cursor-default"
                                            title={`Already sent: ${fq.question}`}
                                          >
                                            ✓ {fq.label}
                                          </span>
                                        ))}
                                        {availableQuestions.length === 0 && sentQuestionsForPref.length === followUpQuestions.length && (
                                          <span className="text-green-400 text-xs">All follow-ups sent!</span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Similar Clips */}
                                    {similarClips.length > 0 && (
                                      <div>
                                        <p className="text-zinc-500 text-xs mb-1">Share similar clip:</p>
                                        <div className="flex flex-wrap gap-1">
                                          {similarClips.map(clip => {
                                            const alreadyShared = sharedClipIds.includes(clip.title);
                                            if (alreadyShared) {
                                              return (
                                                <span
                                                  key={clip.id}
                                                  className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded flex items-center gap-1 cursor-default"
                                                  title={`Already shared: ${clip.title}`}
                                                >
                                                  ✓ {clip.title.slice(0, 20)}{clip.title.length > 20 ? '...' : ''}
                                                </span>
                                              );
                                            }
                                            return (
                                              <button
                                                key={clip.id}
                                                onClick={() => {
                                                  // Use clip ID marker for in-app viewing instead of external URL
                                                  const clipMarker = `[WATCH_CLIP:${clip.id}:${clip.title}]`;
                                                  const shareQuestion = pref.rating === 'try'
                                                    ? `Since you liked "${pref.clipTitle}", here's another one you might enjoy: "${clip.title}".\n\n${clipMarker}\n\nWhat do you think - would you try this one too? What catches your eye (or doesn't)?`
                                                    : pref.rating === 'skip'
                                                    ? `I know you skipped "${pref.clipTitle}", but here's a different take on that category: "${clip.title}".\n\n${clipMarker}\n\nDoes this one feel different to you? Better, worse, or still not your thing?`
                                                    : `You were on the fence about "${pref.clipTitle}" - here's another option: "${clip.title}".\n\n${clipMarker}\n\nDoes this one tip you more toward "try" or "skip"? What's the difference for you?`;
                                                  queueFollowUp(r.id, r.code, shareQuestion, `Similar clip suggestion: ${clip.title}`);
                                                }}
                                                className="px-2 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 text-xs rounded transition-colors"
                                                title={`Share: ${clip.title}`}
                                              >
                                                🎬 {clip.title.slice(0, 20)}{clip.title.length > 20 ? '...' : ''}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <div className="mt-3 pt-2 border-t border-emerald-500/20 flex gap-3 text-xs">
                              <span className="text-green-400">
                                {r.couplePreferences.filter(p => p.rating === 'try').length} try
                              </span>
                              <span className="text-yellow-400">
                                {r.couplePreferences.filter(p => p.rating === 'maybe').length} maybe
                              </span>
                              <span className="text-red-400">
                                {r.couplePreferences.filter(p => p.rating === 'skip').length} skip
                              </span>
                            </div>
                          </div>
                          );
                        })()}

                        {/* Questionnaire Answers */}
                        <div>
                          <h4 className="text-zinc-300 font-semibold text-sm mb-2">📋 Questionnaire Answers</h4>
                          <div className="bg-zinc-800 rounded-lg p-3 grid grid-cols-2 gap-2 text-xs">
                            <p className="text-zinc-400">Sexually active: <span className="text-white">{r.sexuallyActive || 'N/A'}</span></p>
                            <p className="text-zinc-400">Watches content: <span className="text-white">{r.watchesAdultContent || r.watchesContent}</span></p>
                            <p className="text-zinc-400">Familiar w/ products: <span className="text-white">{r.familiarWithProducts || 'N/A'}</span></p>
                            <p className="text-zinc-400">Enjoys volume: <span className="text-white">{r.enjoysVolumeContent || 'N/A'}</span></p>
                            {/* Show all answered fields regardless of gender */}
                            {r.canSquirt && <p className="text-zinc-400">Can squirt: <span className="text-white">{r.canSquirt}</span></p>}
                            {r.enjoysSwallowing && <p className="text-zinc-400">Enjoys swallowing: <span className="text-white">{r.enjoysSwallowing}</span></p>}
                            {r.orgasmFromSex && <p className="text-zinc-400">Orgasm from sex: <span className="text-white">{r.orgasmFromSex}</span></p>}
                            {r.typicalWetness != null && <p className="text-zinc-400">Typical wetness: <span className="text-white">{'💧'.repeat(r.typicalWetness) + ` (${r.typicalWetness}/5)`}</span></p>}
                            {r.wantToBeWetter && <p className="text-zinc-400">Wants to be wetter: <span className="text-white">{r.wantToBeWetter}</span></p>}
                            {r.triedLoadBoost && <p className="text-zinc-400">Tried Load Boost: <span className="text-white">{r.triedLoadBoost}</span></p>}
                            {r.enjoysSquirtingContent && <p className="text-zinc-400">Enjoys squirting: <span className="text-white">{r.enjoysSquirtingContent}</span></p>}
                            {r.swallowingDescription && (
                              <div className="mt-1 ml-2 bg-zinc-800 rounded p-2">
                                <p className="text-pink-400 text-xs">Swallowing experience:</p>
                                <p className="text-white text-sm">"{r.swallowingDescription}"</p>
                              </div>
                            )}
                            {r.wetnessNotes && (
                              <div className="mt-1 ml-2 bg-zinc-800 rounded p-2">
                                <p className="text-pink-400 text-xs">Wetness notes:</p>
                                <p className="text-white text-sm">"{r.wetnessNotes}"</p>
                              </div>
                            )}
                          </div>
                          {r.whatMakesGoodContent && (
                            <div className="mt-2 bg-zinc-800 rounded-lg p-3">
                              <p className="text-zinc-400 text-xs">What makes good content:</p>
                              <p className="text-white text-sm mt-1">"{r.whatMakesGoodContent}"</p>
                            </div>
                          )}
                          {r.whyInterested && (
                            <div className="mt-2 bg-zinc-800 rounded-lg p-3">
                              <p className="text-zinc-400 text-xs">Why interested:</p>
                              <p className="text-white text-sm mt-1">"{r.whyInterested}"</p>
                            </div>
                          )}

                          {/* Sexual Experience Details */}
                          {(r.lastSexualActivity || r.facialExperience || r.oralExperience || r.analExperience || r.favoriteSexualMemory || r.fantasiesNotTried || r.biggestTurnOns || r.dealBreakers) && (
                            <div className="mt-3 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-lg p-3">
                              <h4 className="text-red-400 font-semibold text-sm mb-3">🔥 Sexual Experience Details</h4>
                              <div className="space-y-3 text-sm">
                                {r.lastSexualActivity && (
                                  <div>
                                    <p className="text-zinc-400 text-xs">Last sexual activity:</p>
                                    <p className="text-white">"{r.lastSexualActivity}"</p>
                                  </div>
                                )}
                                {r.facialExperience && (
                                  <div>
                                    <p className="text-zinc-400 text-xs">Facial experience:</p>
                                    <p className="text-white">"{r.facialExperience}"</p>
                                  </div>
                                )}
                                {r.oralExperience && (
                                  <div>
                                    <p className="text-zinc-400 text-xs">Oral experience:</p>
                                    <p className="text-white">"{r.oralExperience}"</p>
                                  </div>
                                )}
                                {r.analExperience && (
                                  <div>
                                    <p className="text-zinc-400 text-xs">Anal experience:</p>
                                    <p className="text-white">"{r.analExperience}"</p>
                                  </div>
                                )}
                                {r.favoriteSexualMemory && (
                                  <div>
                                    <p className="text-zinc-400 text-xs">Favorite memory:</p>
                                    <p className="text-white">"{r.favoriteSexualMemory}"</p>
                                  </div>
                                )}
                                {r.fantasiesNotTried && (
                                  <div>
                                    <p className="text-zinc-400 text-xs">Fantasies not tried:</p>
                                    <p className="text-white">"{r.fantasiesNotTried}"</p>
                                  </div>
                                )}
                                <div className="grid grid-cols-2 gap-3">
                                  {r.biggestTurnOns && (
                                    <div>
                                      <p className="text-green-400 text-xs">Turn-ons:</p>
                                      <p className="text-white text-xs">"{r.biggestTurnOns}"</p>
                                    </div>
                                  )}
                                  {r.dealBreakers && (
                                    <div>
                                      <p className="text-red-400 text-xs">Deal breakers:</p>
                                      <p className="text-white text-xs">"{r.dealBreakers}"</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Squirt scenario description (female) */}
                          {r.squirtScenarioDescription && (
                            <div className="mt-2 bg-zinc-800 rounded-lg p-3">
                              <p className="text-pink-400 text-xs">Squirt scenario:</p>
                              <p className="text-white text-sm mt-1">"{r.squirtScenarioDescription}"</p>
                            </div>
                          )}
                        </div>

                        {/* Follow-up Opportunities - Data-driven from profile-reactive-questions */}
                        {(() => {
                          const alreadySent = reviewerFollowUps.filter(fu => fu.reviewerId === r.id).map(fu => fu.question);
                          const triggered = getTriggeredQuestions(r, alreadySent);

                          if (triggered.length === 0) return null;

                          // Group by section
                          const sections = new Map<string, typeof triggered>();
                          for (const q of triggered) {
                            if (!sections.has(q.section)) sections.set(q.section, []);
                            sections.get(q.section)!.push(q);
                          }

                          return (
                            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                              <h4 className="text-amber-400 font-semibold text-sm flex items-center gap-2 mb-2">
                                💬 Profile Follow-ups ({triggered.length})
                                <span className="text-amber-400/60 text-xs font-normal">Click to send</span>
                              </h4>
                              <div className="space-y-3">
                                {Array.from(sections.entries()).map(([section, questions]) => (
                                  <div key={section}>
                                    <p className="text-amber-400/70 text-xs font-medium mb-1.5">{section}</p>
                                    <div className="space-y-2">
                                      {questions.map((q, i) => (
                                        <button
                                          key={`${section}-${i}`}
                                          onClick={() => queueFollowUp(r.id, r.code, q.prompt, q.context)}
                                          className="w-full text-left bg-zinc-800/50 hover:bg-zinc-700/50 rounded-lg p-2 transition-colors group"
                                        >
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="text-zinc-300 text-xs font-medium">{q.field}</span>
                                            <div className="flex items-center gap-2">
                                              <span className="text-amber-400 text-xs">"{q.value}"</span>
                                              <span className="text-blue-400 text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                                                <Send className="w-3 h-3" /> Send
                                              </span>
                                            </div>
                                          </div>
                                          <p className="text-zinc-400 text-xs italic">→ {q.prompt}</p>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <p className="text-zinc-500 text-xs mt-2">Responses appear in "Custom Q&A" below ↓</p>
                            </div>
                          );
                        })()}

                        {/* AI Research Questions - Universal prompts for feedback */}
                        {(() => {
                          const alreadySent = reviewerFollowUps.filter(fu => fu.reviewerId === r.id).map(fu => fu.question);

                          // Use dynamic questions from state (only active ones)
                          const activeQuestions = researchQuestions.filter(rq => rq.active);

                          // Filter out already asked questions
                          const availableResearch = activeQuestions.filter(rq =>
                            !alreadySent.some(sent => sent.includes(rq.question.slice(0, 50)))
                          );

                          if (availableResearch.length === 0) return null;

                          return (
                            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                              <h4 className="text-purple-400 font-semibold text-sm flex items-center gap-2 mb-2">
                                🤖 AI Research Questions
                                <span className="text-purple-400/60 text-xs font-normal">Feedback prompts</span>
                              </h4>
                              <p className="text-zinc-500 text-xs mb-3">Universal questions to gather insights. Click to send.</p>
                              <div className="flex flex-wrap gap-2">
                                {availableResearch.map(rq => (
                                  <button
                                    key={rq.id}
                                    onClick={() => queueFollowUp(r.id, r.code, rq.question, rq.context)}
                                    className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-full text-xs transition-colors flex items-center gap-1"
                                    title={rq.question}
                                  >
                                    {rq.label}
                                    <Send className="w-3 h-3 opacity-60" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Custom Q&A - Sent Questions */}
                        {(() => {
                          const reviewerQuestions = reviewerFollowUps.filter(fu => fu.reviewerId === r.id);
                          if (reviewerQuestions.length === 0 && askingFollowUp !== r.id) return null;

                          const pendingCount = reviewerQuestions.filter(q => q.status === 'pending').length;
                          const queuedCount = reviewerQuestions.filter(q => q.status === 'queued').length;

                          // Separate root questions from replies for threaded display
                          const rootQuestions = reviewerQuestions.filter(q => !q.parentId);
                          const getReplies = (parentId: string) => reviewerQuestions.filter(q => q.parentId === parentId);

                          // Render a single Q&A item (used for both root and replies)
                          const renderQuestion = (q: typeof reviewerQuestions[0], isReply = false) => (
                            <div key={q.id} className={`rounded-lg p-2 ${isReply ? 'ml-4 border-l-2 border-blue-500/30' : ''} ${
                              q.status === 'answered' ? 'bg-green-500/10 border border-green-500/30' :
                              q.status === 'queued' ? 'bg-purple-500/10 border border-purple-500/30 opacity-60' :
                              'bg-zinc-800/50'
                            }`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  {q.context && (
                                    <p className="text-zinc-500 text-xs mb-0.5">{q.context}</p>
                                  )}
                                  <p className="text-zinc-300 text-xs">Q: {q.question}</p>
                                  {/* Show attached clips */}
                                  {(() => {
                                    const clipIdsToShow = q.clipIds || (q.clipId ? [q.clipId] : []);
                                    if (clipIdsToShow.length === 0) return null;
                                    return (
                                      <div className="mt-1 space-y-1">
                                        {clipIdsToShow.map(clipId => {
                                          const attachedClip = dateNiteClips.find(c => c.id === clipId);
                                          if (!attachedClip) return null;
                                          return (
                                            <div key={clipId} className="flex items-center gap-2 bg-pink-500/10 border border-pink-500/30 rounded px-2 py-1">
                                              <Film className="w-3 h-3 text-pink-400" />
                                              <span className="text-pink-300 text-xs">{attachedClip.title}</span>
                                              {attachedClip.link && (
                                                <a
                                                  href={attachedClip.link}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-pink-400 hover:text-pink-300 text-xs underline ml-auto"
                                                >
                                                  Watch
                                                </a>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}
                                  {/* Show sequence info */}
                                  {q.sequenceId && (
                                    <p className="text-purple-400 text-xs mt-0.5">
                                      Queue #{(q.sequenceOrder || 0) + 1} in sequence
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
                                    q.status === 'answered' ? 'bg-green-500/20 text-green-400' :
                                    q.status === 'queued' ? 'bg-purple-500/20 text-purple-400' :
                                    'bg-amber-500/20 text-amber-400'
                                  }`}>
                                    {q.status}
                                  </span>
                                  {deleteConfirm?.id === q.id ? (
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => deleteFollowUp(q.id)}
                                        className="px-1.5 py-0.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs rounded transition-colors"
                                      >
                                        Yes
                                      </button>
                                      <button
                                        onClick={() => setDeleteConfirm(null)}
                                        className="px-1.5 py-0.5 bg-zinc-600 hover:bg-zinc-500 text-zinc-300 text-xs rounded transition-colors"
                                      >
                                        No
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setDeleteConfirm({ id: q.id, type: 'reviewer' })}
                                      className="p-1 text-red-400/50 hover:text-red-400 hover:bg-red-500/20 rounded transition-colors"
                                      title="Delete question"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              </div>
                              {q.answer && (
                                <div className="mt-2 bg-green-500/5 border border-green-500/20 rounded p-2">
                                  <p className="text-green-400 text-xs font-medium mb-1">Their response:</p>
                                  <p className="text-white text-sm">"{q.answer}"</p>
                                  <div className="flex items-center justify-between mt-2">
                                    {q.answeredAt && (
                                      <p className="text-zinc-500 text-xs">Answered: {new Date(q.answeredAt).toLocaleDateString()}</p>
                                    )}
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => {
                                          setReplyingToQuestionId(q.id);
                                          setNewFollowUpQuestion('');
                                          setAskingFollowUp(r.id);
                                        }}
                                        className="px-2 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 text-xs rounded flex items-center gap-1 transition-colors"
                                      >
                                        <MessageSquare className="w-3 h-3" /> Reply
                                      </button>
                                      <button
                                        onClick={() => {
                                          setCreatingClipSuggestion({ reviewerId: r.id });
                                          setClipSuggestionContext(`Following up on your answer: "${q.answer?.slice(0, 80)}${(q.answer?.length || 0) > 80 ? '...' : ''}" - here are some clips we'd love you to review:`);
                                          setClipSuggestionLinkedQuestion(q.id);
                                        }}
                                        className="px-2 py-1 bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 text-xs rounded flex items-center gap-1 transition-colors"
                                      >
                                        <Film className="w-3 h-3" /> Send Clips
                                      </button>
                                      {/* Show if clips were sent for this question */}
                                      {clipSuggestions.filter(cs => cs.linkedQuestionId === q.id && cs.reviewerId === r.id).length > 0 && (
                                        <span className="px-2 py-1 bg-pink-500/10 text-pink-400 text-xs rounded flex items-center gap-1">
                                          <Film className="w-3 h-3" />
                                          {clipSuggestions.filter(cs => cs.linkedQuestionId === q.id && cs.reviewerId === r.id).reduce((sum, cs) => sum + (cs.clips || []).length, 0)} clips sent
                                          {clipSuggestions.filter(cs => cs.linkedQuestionId === q.id && cs.reviewerId === r.id).some(cs => cs.status === 'completed') && ' ✓'}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {q.status === 'pending' && (
                                <p className="text-zinc-500 text-xs mt-1">Sent: {new Date(q.sentAt).toLocaleDateString()} - awaiting response...</p>
                              )}
                              {q.status === 'queued' && (
                                <p className="text-purple-400 text-xs mt-1">Queued - will show after they answer current question</p>
                              )}

                              {/* Render threaded replies */}
                              {getReplies(q.id).length > 0 && (
                                <div className="mt-2 space-y-2">
                                  {getReplies(q.id).map(reply => renderQuestion(reply, true))}
                                </div>
                              )}
                            </div>
                          );

                          return (
                            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                              <h4 className="text-blue-400 font-semibold text-sm flex items-center gap-2 mb-2">
                                <MessageSquare className="w-4 h-4" /> Custom Q&A
                                {pendingCount > 0 && <span className="bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded text-xs">{pendingCount} pending</span>}
                                {queuedCount > 0 && <span className="bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded text-xs">{queuedCount} queued</span>}
                              </h4>

                              {/* Existing questions - only show root questions (replies render nested) */}
                              {rootQuestions.length > 0 && (
                                <div className="space-y-2 mb-3">
                                  {rootQuestions.map(q => renderQuestion(q))}
                                </div>
                              )}

                              {/* Ask new question form */}
                              {askingFollowUp === r.id ? (
                                <div className="space-y-2">
                                  {replyingToQuestionId && (
                                    <div className="bg-blue-500/10 border border-blue-500/30 rounded px-2 py-1 text-xs text-blue-400 flex items-center justify-between">
                                      <span>Replying to thread...</span>
                                      <button
                                        onClick={() => setReplyingToQuestionId(null)}
                                        className="hover:text-blue-300"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  )}
                                  <div className="flex gap-2">
                                    <textarea
                                      value={newFollowUpQuestion}
                                      onChange={e => setNewFollowUpQuestion(e.target.value)}
                                      placeholder={replyingToQuestionId ? "Type your follow-up reply..." : "Type your follow-up question..."}
                                      className="flex-1 bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white placeholder-zinc-500 text-sm h-20 resize-none"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() => setShowReplyClipPicker(!showReplyClipPicker)}
                                      className={`px-3 h-20 rounded-lg text-sm ${replyClipIds.length > 0 ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30' : 'bg-zinc-600 text-zinc-400 hover:bg-zinc-500'}`}
                                      title="Attach clips"
                                    >
                                      <Film className="w-4 h-4" />
                                      {replyClipIds.length > 0 && <span className="ml-1">{replyClipIds.length}</span>}
                                    </button>
                                  </div>
                                  {/* Selected clips preview */}
                                  {replyClipIds.length > 0 && (
                                    <div className="space-y-1">
                                      {replyClipIds.map(clipId => {
                                        const clip = dateNiteClips.find(c => c.id === clipId);
                                        return clip ? (
                                          <div key={clipId} className="flex items-center gap-2 bg-pink-500/10 border border-pink-500/30 rounded px-2 py-1">
                                            <Film className="w-3 h-3 text-pink-400" />
                                            <span className="text-pink-300 text-xs flex-1 truncate">{clip.title}</span>
                                            <button onClick={() => setReplyClipIds(prev => prev.filter(id => id !== clipId))} className="text-pink-400 hover:text-pink-300">
                                              <X className="w-3 h-3" />
                                            </button>
                                          </div>
                                        ) : null;
                                      })}
                                    </div>
                                  )}
                                  {/* Clip picker dropdown */}
                                  {showReplyClipPicker && (
                                    <div className="bg-zinc-800 border border-zinc-600 rounded-lg p-2 max-h-48 overflow-y-auto">
                                      <input
                                        type="text"
                                        value={replyClipSearch}
                                        onChange={e => setReplyClipSearch(e.target.value)}
                                        placeholder="Search clips..."
                                        className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-xs mb-2"
                                      />
                                      <div className="space-y-1">
                                        {dateNiteClips
                                          .filter(c => !replyClipSearch || c.title.toLowerCase().includes(replyClipSearch.toLowerCase()))
                                          .filter(c => !replyClipIds.includes(c.id))
                                          .slice(0, 10)
                                          .map(clip => (
                                            <button
                                              key={clip.id}
                                              onClick={() => {
                                                setReplyClipIds(prev => [...prev, clip.id]);
                                                setReplyClipSearch('');
                                              }}
                                              className="w-full text-left px-2 py-1 rounded text-xs hover:bg-zinc-700 flex items-center gap-2"
                                            >
                                              <span>{CATEGORIES.find(cat => cat.id === clip.categoryId)?.emoji}</span>
                                              <span className="text-white truncate">{clip.title}</span>
                                            </button>
                                          ))}
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => { setAskingFollowUp(null); setNewFollowUpQuestion(''); setReplyingToQuestionId(null); setReplyClipIds([]); setShowReplyClipPicker(false); }}
                                      className="flex-1 px-3 py-2 bg-zinc-700 text-zinc-300 rounded-lg text-sm hover:bg-zinc-600"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => sendFollowUp(r.id, newFollowUpQuestion, undefined, replyingToQuestionId || undefined, replyClipIds.length > 0 ? replyClipIds : undefined)}
                                      disabled={!newFollowUpQuestion.trim()}
                                      className="flex-1 px-3 py-2 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30 disabled:opacity-50 flex items-center justify-center gap-1"
                                    >
                                      <Send className="w-3 h-3" /> {replyingToQuestionId ? 'Send Reply' : 'Send Question'} {replyClipIds.length > 0 && `+ ${replyClipIds.length} Clip${replyClipIds.length > 1 ? 's' : ''}`}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <button
                                    onClick={() => setAskingFollowUp(r.id)}
                                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-sm font-medium transition-colors"
                                  >
                                    <Plus className="w-4 h-4" /> Ask Custom Question
                                  </button>
                                  <button
                                    onClick={() => generateSoloQuestions(r)}
                                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 rounded-lg text-sm font-medium transition-colors"
                                  >
                                    <Droplets className="w-4 h-4" /> Generate Solo Questions
                                  </button>
                                </div>
                              )}

                              {/* Solo Questions Editor */}
                              {soloQuestions?.reviewerId === r.id && (
                                <div className="mt-3 bg-pink-500/5 border border-pink-500/20 rounded-lg p-3 space-y-2">
                                  <h5 className="text-pink-400 text-xs font-semibold uppercase tracking-wide flex items-center gap-1">
                                    <Droplets className="w-3 h-3" /> Generated Solo Questions
                                  </h5>
                                  <p className="text-zinc-500 text-xs">Edit before sending. Remove any you don't want.</p>
                                  {soloQuestionsEditing.map((q, idx) => (
                                    <div key={idx} className="flex gap-2">
                                      <textarea
                                        value={q}
                                        onChange={e => {
                                          const updated = [...soloQuestionsEditing];
                                          updated[idx] = e.target.value;
                                          setSoloQuestionsEditing(updated);
                                        }}
                                        className="flex-1 bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-xs h-16 resize-none"
                                      />
                                      <button
                                        onClick={() => setSoloQuestionsEditing(prev => prev.filter((_, i) => i !== idx))}
                                        className="p-1 text-red-400/50 hover:text-red-400 self-start"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                  <div className="flex gap-2 pt-1">
                                    <button
                                      onClick={() => { setSoloQuestions(null); setSoloQuestionsEditing([]); }}
                                      className="flex-1 px-3 py-2 bg-zinc-700 text-zinc-300 rounded-lg text-xs hover:bg-zinc-600"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => {
                                        const validQs = soloQuestionsEditing.filter(q => q.trim());
                                        validQs.forEach(q => queueFollowUp(r.id, r.code, q, 'Solo exploration'));
                                        setSoloQuestions(null);
                                        setSoloQuestionsEditing([]);
                                      }}
                                      disabled={soloQuestionsEditing.filter(q => q.trim()).length === 0}
                                      className="flex-1 px-3 py-2 bg-pink-500/20 text-pink-400 rounded-lg text-xs hover:bg-pink-500/30 disabled:opacity-50 flex items-center justify-center gap-1"
                                    >
                                      <Send className="w-3 h-3" /> Queue {soloQuestionsEditing.filter(q => q.trim()).length} Questions
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Tag Tutorial Assignment */}
                        {(() => {
                          const reviewerTutorials = tagTutorials.filter(t => t.reviewerId === r.id);
                          const completedTags = reviewerTutorials.filter(t => t.status === 'completed').map(t => t.tagId);
                          const assignedTags = reviewerTutorials.filter(t => t.assignedBy).map(t => t.tagId);
                          const availableToAssign = TAG_TUTORIAL_DEFINITIONS.filter(def =>
                            !completedTags.includes(def.tagId) && !assignedTags.includes(def.tagId)
                          );

                          return (
                            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                              <h4 className="text-cyan-400 font-semibold text-sm flex items-center gap-2 mb-2">
                                🏷️ Tag Tutorials
                                <span className="text-xs text-zinc-400">({completedTags.length}/{TAG_TUTORIAL_DEFINITIONS.length} done)</span>
                              </h4>

                              {/* Progress */}
                              <div className="mb-3">
                                <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-cyan-500 transition-all"
                                    style={{ width: `${(completedTags.length / TAG_TUTORIAL_DEFINITIONS.length) * 100}%` }}
                                  />
                                </div>
                              </div>

                              {/* Completed with feedback */}
                              {completedTags.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-zinc-400 text-xs mb-2">✅ Completed with feedback:</p>
                                  <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {reviewerTutorials.filter(t => t.status === 'completed').map(tutorial => {
                                      const def = TAG_TUTORIAL_DEFINITIONS.find(d => d.tagId === tutorial.tagId);
                                      return (
                                        <div key={tutorial.id} className="bg-zinc-800/50 rounded p-2">
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="text-white text-xs font-medium">{def?.tagLabel || tutorial.tagId}</span>
                                            {tutorial.understanding && (
                                              <span className="text-xs flex items-center gap-1">
                                                <span className="text-zinc-500">Understanding:</span>
                                                <span className="text-lime-400">{'★'.repeat(tutorial.understanding)}</span>
                                                <span className="text-zinc-600">{'★'.repeat(5 - tutorial.understanding)}</span>
                                              </span>
                                            )}
                                          </div>
                                          {tutorial.notes && (
                                            <p className="text-zinc-400 text-xs italic">"{tutorial.notes}"</p>
                                          )}
                                          {tutorial.exampleClipNotes && Object.keys(tutorial.exampleClipNotes).length > 0 && (
                                            <div className="mt-1">
                                              <p className="text-zinc-500 text-xs mb-1">Example clip notes:</p>
                                              {Object.entries(tutorial.exampleClipNotes).map(([clipId, note]) => (
                                                <p key={clipId} className="text-zinc-400 text-xs italic pl-2">"{note}"</p>
                                              ))}
                                            </div>
                                          )}
                                          {tutorial.comboNotes && Object.keys(tutorial.comboNotes).length > 0 && (
                                            <div className="mt-1">
                                              <p className="text-zinc-500 text-xs mb-1">Category combo notes:</p>
                                              {Object.entries(tutorial.comboNotes).map(([comboId, note]) => (
                                                <p key={comboId} className="text-zinc-400 text-xs italic pl-2">"{note}"</p>
                                              ))}
                                            </div>
                                          )}
                                          {!tutorial.understanding && !tutorial.notes &&
                                           (!tutorial.exampleClipNotes || Object.keys(tutorial.exampleClipNotes).length === 0) &&
                                           (!tutorial.comboNotes || Object.keys(tutorial.comboNotes).length === 0) && (
                                            <p className="text-zinc-500 text-xs">No feedback provided</p>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Already assigned (pending) */}
                              {assignedTags.length > 0 && (
                                <div className="mb-2">
                                  <p className="text-zinc-400 text-xs mb-1">⏳ Assigned (pending):</p>
                                  <div className="flex flex-wrap gap-1">
                                    {assignedTags.map(tagId => {
                                      const tutorial = TAG_TUTORIAL_DEFINITIONS.find(t => t.tagId === tagId);
                                      return (
                                        <span key={tagId} className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">
                                          {tutorial?.tagLabel || tagId}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Assign new tutorials */}
                              {availableToAssign.length > 0 && saveTagTutorials && (
                                <div>
                                  <p className="text-zinc-400 text-xs mb-1">Assign tutorial:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {availableToAssign.slice(0, 5).map(def => (
                                      <button
                                        key={def.tagId}
                                        onClick={() => {
                                          const newTutorial: TagTutorial = {
                                            id: generateId('tt'),
                                            reviewerId: r.id,
                                            tagId: def.tagId,
                                            status: 'unread',
                                            sentAt: now(),
                                            assignedBy: 'admin',
                                            assignedAt: now(),
                                            priority: true
                                          };
                                          saveTagTutorials([...tagTutorials, newTutorial]);
                                        }}
                                        className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded hover:bg-cyan-500/30 transition-colors"
                                      >
                                        + {def.tagLabel}
                                      </button>
                                    ))}
                                    {availableToAssign.length > 5 && (
                                      <span className="text-zinc-500 text-xs py-0.5">+{availableToAssign.length - 5} more</span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {availableToAssign.length === 0 && assignedTags.length === 0 && (
                                <p className="text-green-400 text-xs">All tutorials assigned or completed!</p>
                              )}
                            </div>
                          );
                        })()}

                        {/* Clip Suggestions Sent to Reviewer */}
                        {(() => {
                          // Check both id and code since older data may use code
                          const reviewerSuggestions = clipSuggestions.filter(cs => cs.reviewerId === r.id || cs.reviewerId === r.code);

                          if (reviewerSuggestions.length === 0) return null;

                          return (
                            <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-3">
                              <h4 className="text-pink-400 font-semibold text-sm flex items-center gap-2 mb-2">
                                <Film className="w-4 h-4" /> Clips Sent for Review
                                {reviewerSuggestions.filter(cs => cs.status === 'pending').length > 0 && (
                                  <span className="bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded text-xs">
                                    {reviewerSuggestions.filter(cs => cs.status === 'pending').length} pending
                                  </span>
                                )}
                              </h4>

                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {reviewerSuggestions.map(cs => (
                                  <div key={cs.id} className={`rounded-lg p-2 ${
                                    cs.status === 'completed' ? 'bg-green-500/10 border border-green-500/30' :
                                    cs.status === 'partial' ? 'bg-amber-500/10 border border-amber-500/30' :
                                    'bg-zinc-800/50'
                                  }`}>
                                    <div className="flex justify-between text-xs mb-1">
                                      <span className="text-pink-400 truncate flex-1">{cs.context || 'Clip suggestions'}</span>
                                      <span className={`px-1.5 py-0.5 rounded text-xs ml-2 ${
                                        cs.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                        cs.status === 'partial' ? 'bg-amber-500/20 text-amber-400' :
                                        'bg-zinc-500/20 text-zinc-400'
                                      }`}>
                                        {cs.status}
                                      </span>
                                    </div>
                                    <div className="space-y-1">
                                      {(cs.clips || []).map((clip, idx) => (
                                        <div key={clip.id} className="bg-zinc-900/50 rounded p-1.5">
                                          <div className="flex items-start gap-2">
                                            <span className="text-zinc-500 text-xs w-4">{idx + 1}.</span>
                                            <div className="flex-1 min-w-0">
                                              <a href={clip.url} target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:text-pink-300 text-xs truncate block">
                                                {clip.title}
                                              </a>
                                              {clip.rating && (
                                                <div className="flex items-center gap-2 mt-0.5">
                                                  <div className="flex">
                                                    {[1,2,3,4,5].map(s => (
                                                      <Star key={s} className={`w-3 h-3 ${s <= clip.rating! ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'}`} />
                                                    ))}
                                                  </div>
                                                  {clip.interested !== undefined && (
                                                    <span className={`text-xs ${clip.interested ? 'text-lime-400' : 'text-zinc-500'}`}>
                                                      {clip.interested ? '✓ Would try' : '✗ Not interested'}
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                              {clip.comment && (
                                                <p className="text-zinc-400 text-xs mt-0.5 italic">"{clip.comment}"</p>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="flex items-center justify-between mt-1">
                                      <p className="text-zinc-500 text-xs">Sent: {new Date(cs.sentAt).toLocaleDateString()}</p>
                                      <div className="flex items-center gap-3">
                                        <button
                                          onClick={() => startEditingClipSuggestion(cs)}
                                          className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1"
                                        >
                                          <Edit2 className="w-3 h-3" /> Edit
                                        </button>
                                        <button
                                          onClick={async () => {
                                            if (!saveClipSuggestions) return;
                                            const updated = clipSuggestions.filter(s => s.id !== cs.id);
                                            await saveClipSuggestions(updated);
                                          }}
                                          className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1"
                                        >
                                          <Trash2 className="w-3 h-3" /> Delete
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Current Homework */}
                        {r.tryAtHome && (
                          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                            <h4 className="text-orange-400 font-semibold text-sm flex items-center gap-2 mb-2">
                              <Home className="w-4 h-4" /> Current Homework
                            </h4>
                            {(() => {
                              const clip = dateNiteClips.find(c => c.id === r.tryAtHome?.clipId);
                              const cat = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                              return clip ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">{cat?.emoji}</span>
                                  <div>
                                    <p className="text-white text-sm">{clip.title}</p>
                                    <p className="text-zinc-400 text-xs">Assigned {new Date(r.tryAtHome!.assignedAt).toLocaleDateString()}</p>
                                  </div>
                                </div>
                              ) : <p className="text-zinc-500 text-xs">Clip not found</p>;
                            })()}
                          </div>
                        )}

                        {/* Homework History */}
                        {r.homeworkHistory && r.homeworkHistory.length > 0 && (
                          <div>
                            <h4 className="text-zinc-300 font-semibold text-sm flex items-center gap-2 mb-2">
                              <MessageSquare className="w-4 h-4" /> Homework History ({r.homeworkHistory.length})
                            </h4>
                            <div className="space-y-2">
                              {r.homeworkHistory.map((hw, i) => {
                                const clip = dateNiteClips.find(c => c.id === hw.clipId);
                                const cat = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                                return (
                                  <div key={i} className="bg-zinc-800 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span>{cat?.emoji || '📹'}</span>
                                      <span className="text-white text-sm">{clip?.title || 'Unknown clip'}</span>
                                      <span className="text-zinc-500 text-xs ml-auto">{hw.completedAt ? new Date(hw.completedAt).toLocaleDateString() : 'Pending'}</span>
                                    </div>
                                    {hw.rating && <p className="text-orange-400 text-sm">{hw.rating}</p>}
                                    {hw.comparisonToClip && <p className="text-zinc-400 text-xs mt-1">Comparison: "{hw.comparisonToClip}"</p>}
                                    {hw.feedback && <p className="text-zinc-300 text-xs mt-1">"{hw.feedback}"</p>}

                                    {/* Media capture info */}
                                    {hw.tookMedia && (
                                      <div className="mt-2 pt-2 border-t border-zinc-700 flex flex-wrap gap-2 items-center">
                                        <span className={`px-2 py-0.5 rounded text-xs ${hw.tookMedia === 'yes' ? 'bg-pink-500/20 text-pink-400' : 'bg-zinc-700 text-zinc-400'}`}>
                                          📸 {hw.tookMedia === 'yes' ? 'Took media' : 'No media'}
                                        </span>
                                        {hw.tookMedia === 'yes' && hw.willingToShare && (
                                          <span className={`px-2 py-0.5 rounded text-xs ${
                                            hw.willingToShare === 'yes' ? 'bg-green-500/20 text-green-400' :
                                            hw.willingToShare === 'maybe' ? 'bg-amber-500/20 text-amber-400' :
                                            'bg-zinc-700 text-zinc-400'
                                          }`}>
                                            Share: {hw.willingToShare}
                                          </span>
                                        )}
                                        {hw.mediaUrl && (
                                          <a href={hw.mediaUrl} target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:underline text-xs flex items-center gap-1">
                                            🔗 View media →
                                          </a>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Recommendations Received */}
                        {r.coupleId && (
                          <div>
                            <h4 className="text-zinc-300 font-semibold text-sm flex items-center gap-2 mb-2">
                              <Gift className="w-4 h-4 text-purple-400" /> Recommendations Received ({recs.length})
                            </h4>
                            {recs.length === 0 ? (
                              <p className="text-zinc-500 text-xs">No recommendations yet</p>
                            ) : (
                              <div className="space-y-2">
                                {recs.map(rec => {
                                  const clip = dateNiteClips.find(c => c.id === rec.clipId);
                                  return (
                                    <div key={rec.id} className="bg-zinc-800 rounded-lg p-3">
                                      <div className="flex justify-between text-xs mb-1">
                                        <span className="text-purple-400">From {rec.fromName}</span>
                                        <span className="text-zinc-500">{new Date(rec.createdAt).toLocaleDateString()}</span>
                                      </div>
                                      <p className="text-white text-sm">{clip?.title || 'Unknown clip'}</p>
                                      {rec.message && <p className="text-zinc-400 text-xs mt-1">"{rec.message}"</p>}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Head-to-Head Matchup History */}
                        {r.categoryChampions && r.categoryChampions.matchups.length > 0 && (
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                            <h4 className="text-amber-400 font-semibold text-sm flex items-center gap-2 mb-2">
                              <Trophy className="w-4 h-4" /> Head-to-Head Matchups ({r.categoryChampions.matchups.length})
                            </h4>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {[...r.categoryChampions.matchups].reverse().map((m, i) => {
                                const cat = CATEGORIES.find(c => c.id === m.categoryId);
                                const winnerClip = dateNiteClips.find(c => c.id === m.winnerId);
                                const loserClip = dateNiteClips.find(c => c.id === m.loserId);
                                return (
                                  <div key={i} className="bg-zinc-800/50 rounded-lg p-2">
                                    <div className="flex items-center gap-2 text-xs">
                                      <span>{cat?.emoji}</span>
                                      <button onClick={() => setMatchupPreviewClip(matchupPreviewClip === m.winnerId ? null : m.winnerId)} className="text-green-400 font-medium line-clamp-1 flex-1 text-left hover:underline">{winnerClip?.title || m.winnerId}</button>
                                      <span className="text-zinc-500">beat</span>
                                      <button onClick={() => setMatchupPreviewClip(matchupPreviewClip === m.loserId ? null : m.loserId)} className="text-red-400 line-clamp-1 flex-1 text-left hover:underline">{loserClip?.title || m.loserId}</button>
                                      <span className="text-zinc-600 text-[10px]">{new Date(m.decidedAt).toLocaleDateString()}</span>
                                    </div>
                                    {m.reason && (
                                      <p className="text-zinc-300 text-xs mt-1 ml-5 italic">"{m.reason}"</p>
                                    )}
                                    {/* Quick video preview */}
                                    {matchupPreviewClip && (matchupPreviewClip === m.winnerId || matchupPreviewClip === m.loserId) && (() => {
                                      const previewClip = dateNiteClips.find(c => c.id === matchupPreviewClip);
                                      if (!previewClip) return null;
                                      return (
                                        <div className="mt-2 rounded-lg overflow-hidden border border-zinc-600">
                                          <VideoPlayer url={previewClip.link} title={previewClip.title} />
                                        </div>
                                      );
                                    })()}
                                  </div>
                                );
                              })}
                            </div>
                            {/* Per-matchup follow-up tags */}
                            {(() => {
                              // Get all follow-ups already sent for this reviewer to check what's been asked
                              const sentFollowUps = reviewerFollowUps.filter(fu => fu.reviewerId === r.id);
                              const sentClipPairs = new Set(sentFollowUps.flatMap(fu => {
                                if (!fu.clipIds || fu.clipIds.length < 2) return [];
                                return [`${fu.clipIds[0]}|${fu.clipIds[1]}`];
                              }));
                              const sentClipIds = new Set(sentFollowUps.flatMap(fu => fu.clipIds || (fu.clipId ? [fu.clipId] : [])));

                              return (
                                <div className="mt-2 pt-2 border-t border-amber-500/20">
                                  <p className="text-amber-400/70 text-[10px] uppercase tracking-wide mb-1.5">Ask about specific picks</p>
                                  <div className="flex flex-wrap gap-1 mb-2">
                                    {[...r.categoryChampions!.matchups].reverse().slice(0, 6).map((m, mi) => {
                                      const w = dateNiteClips.find(c => c.id === m.winnerId)?.title || m.winnerId;
                                      const l = dateNiteClips.find(c => c.id === m.loserId)?.title || m.loserId;
                                      const alreadySent = sentClipPairs.has(`${m.winnerId}|${m.loserId}`);
                                      return alreadySent ? (
                                        <span
                                          key={mi}
                                          className="px-2 py-0.5 text-[10px] bg-zinc-800 text-zinc-500 border border-zinc-700 rounded line-through"
                                          title={`Already asked: ${w} beat ${l}`}
                                        >
                                          {w.slice(0, 15)}{w.length > 15 ? '...' : ''} &gt; {l.slice(0, 15)}{l.length > 15 ? '...' : ''}
                                        </span>
                                      ) : (
                                        <button
                                          key={mi}
                                          onClick={() => {
                                            setSubmissionFollowUp({ reviewerId: r.id, subId: '__matchup__', clipIds: [m.winnerId, m.loserId] });
                                            setSubmissionFollowUpText(`You picked "${w}" over "${l}" — what made that one stand out? Was it the couple, the angle, the outcome, or something else?`);
                                          }}
                                          className="px-2 py-0.5 text-[10px] bg-amber-500/10 text-amber-300 border border-amber-500/20 rounded hover:bg-amber-500/20 hover:border-amber-500/40 transition-colors"
                                          title={`${w} beat ${l}`}
                                        >
                                          {w.slice(0, 15)}{w.length > 15 ? '...' : ''} &gt; {l.slice(0, 15)}{l.length > 15 ? '...' : ''}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {/* Overall matchup pattern question */}
                                  {(() => {
                                    const winCounts: Record<string, number> = {};
                                    r.categoryChampions!.matchups.forEach(m => { winCounts[m.winnerId] = (winCounts[m.winnerId] || 0) + 1; });
                                    const topWinner = Object.entries(winCounts).sort((a, b) => b[1] - a[1])[0];
                                    const topClip = topWinner ? dateNiteClips.find(c => c.id === topWinner[0]) : null;
                                    if (!topClip || !topWinner) return null;
                                    const topAlreadySent = sentClipIds.has(topWinner[0]) && sentFollowUps.some(fu => fu.context?.includes('head-to-head'));
                                    return topAlreadySent ? (
                                      <span className="w-full block px-2 py-1.5 text-[11px] bg-zinc-800 text-zinc-500 border border-zinc-700 rounded-lg text-center line-through">
                                        Already asked about "{topClip.title.slice(0, 25)}"
                                      </span>
                                    ) : (
                                      <button
                                        onClick={() => {
                                          setSubmissionFollowUp({ reviewerId: r.id, subId: '__matchup__', clipIds: [topWinner[0]] });
                                          setSubmissionFollowUpText(`"${topClip.title}" won ${topWinner[1]} out of ${r.categoryChampions!.matchups.length} matchups — what is it about this clip that keeps winning for you? What does your ideal clip look like?`);
                                        }}
                                        className="w-full px-2 py-1.5 text-[11px] bg-amber-500/15 text-amber-300 border border-amber-500/25 rounded-lg hover:bg-amber-500/25 transition-colors"
                                      >
                                        Why does "{topClip.title.slice(0, 25)}" keep winning? ({topWinner[1]}/{r.categoryChampions!.matchups.length} wins)
                                      </button>
                                    );
                                  })()}
                                </div>
                              );
                            })()}
                            {/* Matchup follow-up input */}
                            {submissionFollowUp?.reviewerId === r.id && submissionFollowUp?.subId === '__matchup__' && (
                              <div className="mt-2">
                                <textarea
                                  value={submissionFollowUpText}
                                  onChange={e => setSubmissionFollowUpText(e.target.value)}
                                  rows={3}
                                  className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-2 text-white text-xs resize-none focus:outline-none focus:border-amber-500/50"
                                  autoFocus
                                />
                                <div className="flex gap-2 mt-2">
                                  <button
                                    onClick={() => { setSubmissionFollowUp(null); setSubmissionFollowUpText(''); }}
                                    className="px-2 py-1 text-xs text-zinc-500 hover:text-white"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (submissionFollowUpText.trim()) {
                                        sendFollowUp(r.id, submissionFollowUpText.trim(), 'Re: their head-to-head matchup picks', undefined, submissionFollowUp?.clipIds);
                                        setSubmissionFollowUp(null);
                                        setSubmissionFollowUpText('');
                                        setFollowUpSentFlash('__matchup__');
                                        setTimeout(() => setFollowUpSentFlash(null), 2500);
                                      }
                                    }}
                                    disabled={!submissionFollowUpText.trim()}
                                    className="px-3 py-1 text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500/30 disabled:opacity-50 transition-colors"
                                  >
                                    Send
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Champions summary */}
                            {Object.keys(r.categoryChampions.champions || {}).length > 0 && (
                              <div className="mt-2 pt-2 border-t border-amber-500/20">
                                <p className="text-amber-400/70 text-[10px] uppercase tracking-wide mb-1">Current Champions</p>
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(r.categoryChampions.champions).map(([catId, clipId]) => {
                                    const cat = CATEGORIES.find(c => c.id === catId);
                                    const clip = dateNiteClips.find(c => c.id === clipId);
                                    return (
                                      <span key={catId} className="px-2 py-0.5 bg-amber-500/20 text-amber-300 text-xs rounded">
                                        {cat?.emoji} {clip?.title?.slice(0, 20) || clipId}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Their Reviews - Full Expandable */}
                        <div>
                          <button
                            onClick={() => setExpandedReviews(expandedReviews === r.id ? null : r.id)}
                            className="w-full text-left flex items-center justify-between mb-2"
                          >
                            <h4 className="text-zinc-300 font-semibold text-sm flex items-center gap-2">
                              <Star className="w-4 h-4 text-amber-400" /> All Reviews ({reviewerClipReviews.length})
                            </h4>
                            {reviewerClipReviews.length > 5 && (
                              expandedReviews === r.id ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />
                            )}
                          </button>
                          {reviewerClipReviews.length === 0 ? (
                            <p className="text-zinc-500 text-xs">No reviews yet</p>
                          ) : (
                            <div className={`space-y-2 ${expandedReviews === r.id ? 'max-h-[500px]' : 'max-h-64'} overflow-y-auto`}>
                              {reviewerClipReviews
                                .sort((a, b) => {
                                  const reviewA = a.reviews?.find(rev => rev.reviewerId === r.id);
                                  const reviewB = b.reviews?.find(rev => rev.reviewerId === r.id);
                                  return new Date(reviewB?.date || 0).getTime() - new Date(reviewA?.date || 0).getTime();
                                })
                                .slice(0, expandedReviews === r.id ? undefined : 5)
                                .map(clip => {
                                  const review = clip.reviews?.find(rev => rev.reviewerId === r.id);
                                  const cat = CATEGORIES.find(c => c.id === clip.categoryId);
                                  const clipTags = clip.tags?.map(t => TAGS.find(tag => tag.id === t)?.label).filter(Boolean);
                                  return (
                                    <div key={clip.id} className="bg-zinc-800 rounded-lg p-3">
                                      <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-2">
                                          <span>{cat?.emoji}</span>
                                          <span className="text-white text-sm">{clip.title}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className="text-amber-400 text-sm font-medium">{review?.rating}</span>
                                          <span className="text-zinc-500 text-xs">{review?.date ? new Date(review.date).toLocaleDateString() : ''}</span>
                                        </div>
                                      </div>
                                      {review?.comment && (
                                        <p className="text-zinc-300 text-sm mt-1 bg-zinc-900/50 rounded p-2">"{review.comment}"</p>
                                      )}
                                      {clipTags && clipTags.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                          {clipTags.slice(0, 5).map((tag, i) => (
                                            <span key={i} className="px-1.5 py-0.5 bg-zinc-700 text-zinc-400 text-xs rounded">{tag}</span>
                                          ))}
                                        </div>
                                      )}
                                      {/* Per-response follow-up suggestions */}
                                      {review && (() => {
                                        const title = clip.title;
                                        const rating = review.rating;
                                        const suggestions: { label: string; question: string }[] = [];
                                        const cat = CATEGORIES.find(c => c.id === clip.categoryId);
                                        // Rating-based
                                        if (rating === '🔥 Must Try') {
                                          suggestions.push({ label: 'Why must-try?', question: `Re "${title}" — you rated this a Must Try. What specifically makes this one stand out? What intrigued you most?` });
                                          suggestions.push({ label: 'Try with partner?', question: `Re "${title}" — since this is a Must Try for you, would you want to try this activity with your partner? What appeals to you about it?` });
                                          suggestions.push({ label: 'What turned you on?', question: `Re "${title}" — was it the couple, the visual, the energy, or the outcome that made this a Must Try? What would you want to see more of?` });
                                        }
                                        if (rating === '👍 Good') {
                                          suggestions.push({ label: 'What held it back?', question: `Re "${title}" — you rated this Good but not Must Try. What would have pushed it higher — a different couple, angle, energy, or outcome?` });
                                          suggestions.push({ label: 'Better version?', question: `Re "${title}" — what would your ideal version of this clip look like? Different couple, different angle, longer/shorter, more reaction?` });
                                        }
                                        if (rating === '🤔 Maybe') {
                                          suggestions.push({ label: 'What made you unsure?', question: `Re "${title}" — you rated this a Maybe. What made you uncertain — not your thing, or just not the right execution?` });
                                          suggestions.push({ label: 'Would better execution help?', question: `Re "${title}" — if this same idea was done differently (better couple, angle, energy), would you rate it higher? Or is ${cat?.name || 'this category'} just not your thing?` });
                                        }
                                        // Comment-based
                                        if (review.comment && review.comment.length > 3) {
                                          suggestions.push({ label: 'Tell us more', question: `Re "${title}" — you said "${review.comment}". Can you expand on that? What specifically caught your attention?` });
                                        }
                                        // Category-based
                                        if (cat) {
                                          suggestions.push({ label: `More ${cat.name}?`, question: `Re "${title}" — would you like to see more ${cat.name} content? What would your ideal clip in this category look like?` });
                                        }
                                        // Couple/performer interest
                                        suggestions.push({ label: 'More from them?', question: `Re "${title}" — would you want to see more content from this couple/performer? What did you think of them?` });
                                        suggestions.push({ label: 'Your fantasy version?', question: `Re "${title}" — if you could direct your own version of this scene, what would you change? What's your fantasy take on it?` });
                                        // Arousal/reaction
                                        suggestions.push({ label: 'Arousal level?', question: `Re "${title}" — how aroused were you watching this? What moment hit hardest, or what was missing?` });
                                        // Comparison
                                        if (cat) {
                                          suggestions.push({ label: 'Compare to others?', question: `Re "${title}" — compared to other ${cat.name} clips you've seen, where does this rank? What makes a ${cat.name} clip great for you?` });
                                        }
                                        // Cum metrics
                                        if (review.cumLevel && review.cumLevel >= 4) suggestions.push({ label: 'Load reaction', question: `Re "${title}" — you rated the load ${review.cumLevel}/5. Is volume what you look for most, or is it more about consistency, distance, or the reaction?` });
                                        if (review.cumLevel && review.cumLevel <= 2) suggestions.push({ label: 'Load disappointing?', question: `Re "${title}" — the load was rated ${review.cumLevel}/5. Was the volume disappointing, or was it more about how it looked?` });
                                        // Wetness metrics
                                        if (review.wetnessLevel && review.wetnessLevel >= 4) suggestions.push({ label: 'Wetness reaction', question: `Re "${title}" — you rated the wetness ${review.wetnessLevel}/5. What about it stood out — the build-up, the visual, or the reaction?` });
                                        if (review.squirtLevel && review.squirtLevel >= 3) suggestions.push({ label: 'Squirt reaction', question: `Re "${title}" — how did the squirting make you feel? Is that something you'd want to try or see more of?` });

                                        // Similar clips from same category they haven't reviewed
                                        const similarClips = dateNiteClips.filter(c =>
                                          c.id !== clip.id &&
                                          (c.categoryId === clip.categoryId || c.additionalCategoryIds?.includes(clip.categoryId)) &&
                                          !c.reviews?.some(rev => rev.reviewerId === r.id)
                                        ).slice(0, 4);

                                        // Check which follow-ups already sent for this clip
                                        const clipSentFollowUps = reviewerFollowUps.filter(fu =>
                                          fu.reviewerId === r.id && (fu.clipIds?.includes(clip.id) || fu.clipId === clip.id)
                                        );
                                        const sentQuestionTexts = clipSentFollowUps.map(fu => fu.question.toLowerCase());

                                        return (
                                          <div className="mt-2 pt-2 border-t border-zinc-700">
                                            {suggestions.length > 0 && (
                                              <>
                                                <p className="text-purple-400/70 text-[10px] uppercase tracking-wide mb-1">Ask follow-up</p>
                                                <div className="flex flex-wrap gap-1 mb-2">
                                                  {suggestions.map((s, si) => {
                                                    // Check if a similar question was already sent
                                                    const alreadySent = sentQuestionTexts.some(sq => sq.includes(s.label.toLowerCase().replace('?', '')) || sq.includes(title.toLowerCase()));
                                                    return alreadySent ? (
                                                      <span
                                                        key={si}
                                                        className="px-2 py-0.5 text-[10px] bg-zinc-800 text-zinc-600 border border-zinc-700 rounded line-through"
                                                      >
                                                        {s.label}
                                                      </span>
                                                    ) : (
                                                    <button
                                                      key={si}
                                                      onClick={() => {
                                                        setClipReviewFollowUp({ reviewerId: r.id, clipId: clip.id });
                                                        setClipReviewFollowUpText(s.question);
                                                      }}
                                                      className="px-2 py-0.5 text-[10px] bg-purple-500/10 text-purple-300 border border-purple-500/20 rounded hover:bg-purple-500/20 hover:border-purple-500/40 transition-colors"
                                                    >
                                                      {s.label}
                                                    </button>
                                                    );
                                                  })}
                                                </div>
                                              </>
                                            )}
                                            {similarClips.length > 0 && (
                                              <>
                                                <p className="text-emerald-400/70 text-[10px] uppercase tracking-wide mb-1">Share similar clip</p>
                                                <div className="flex flex-wrap gap-1">
                                                  {similarClips.map(sc => (
                                                    <button
                                                      key={sc.id}
                                                      onClick={() => {
                                                        setClipReviewFollowUp({ reviewerId: r.id, clipId: clip.id, extraClipIds: [sc.id] });
                                                        setClipReviewFollowUpText(`You ${rating === '🔥 Must Try' ? 'loved' : rating === '👍 Good' ? 'liked' : 'were curious about'} "${title}" — check out "${sc.title}", it's in a similar vein. What do you think?`);
                                                      }}
                                                      className="px-2 py-0.5 text-[10px] bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 rounded hover:bg-emerald-500/20 hover:border-emerald-500/40 transition-colors"
                                                    >
                                                      {sc.title.length > 25 ? sc.title.slice(0, 25) + '...' : sc.title}
                                                    </button>
                                                  ))}
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        );
                                      })()}

                                      {/* Follow-up input */}
                                      {clipReviewFollowUp?.reviewerId === r.id && clipReviewFollowUp?.clipId === clip.id ? (
                                        <div className="mt-2 pt-2 border-t border-zinc-700">
                                          <textarea
                                            value={clipReviewFollowUpText}
                                            onChange={e => setClipReviewFollowUpText(e.target.value)}
                                            placeholder="Ask about their review..."
                                            rows={3}
                                            className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-2 text-white text-xs resize-none focus:outline-none focus:border-purple-500/50"
                                            autoFocus
                                          />
                                          <div className="flex gap-2 mt-2">
                                            <button
                                              onClick={() => { setClipReviewFollowUp(null); setClipReviewFollowUpText(''); }}
                                              className="px-2 py-1 text-xs text-zinc-500 hover:text-white"
                                            >
                                              Cancel
                                            </button>
                                            <button
                                              onClick={() => {
                                                if (clipReviewFollowUpText.trim()) {
                                                  const allClipIds = [clip.id, ...(clipReviewFollowUp?.extraClipIds || [])];
                                                  sendFollowUp(r.id, clipReviewFollowUpText.trim(), `Re: their ${review?.rating} review of "${clip.title}"`, undefined, allClipIds);
                                                  setClipReviewFollowUp(null);
                                                  setClipReviewFollowUpText('');
                                                  setFollowUpSentFlash(clip.id);
                                                  setTimeout(() => setFollowUpSentFlash(null), 2500);
                                                }
                                              }}
                                              disabled={!clipReviewFollowUpText.trim()}
                                              className="px-3 py-1 text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-lg hover:bg-purple-500/30 disabled:opacity-50 transition-colors"
                                            >
                                              Send Follow-up
                                            </button>
                                          </div>
                                        </div>
                                      ) : followUpSentFlash === clip.id ? (
                                        <p className="mt-2 text-center text-xs text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg py-1 animate-pulse">
                                          <Check className="w-3 h-3 inline mr-1" /> Sent!
                                        </p>
                                      ) : (
                                        <button
                                          onClick={() => { setClipReviewFollowUp({ reviewerId: r.id, clipId: clip.id }); setClipReviewFollowUpText(''); }}
                                          className="mt-2 w-full flex items-center justify-center gap-1 px-2 py-1 text-[10px] text-zinc-500 hover:text-purple-400 transition-colors"
                                        >
                                          <MessageSquare className="w-3 h-3" /> Custom question
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              {reviewerClipReviews.length > 5 && expandedReviews !== r.id && (
                                <button
                                  onClick={() => setExpandedReviews(r.id)}
                                  className="w-full text-center text-blue-400 text-xs py-2 hover:underline"
                                >
                                  Show all {reviewerClipReviews.length} reviews →
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Their Picks */}
                        <div>
                          <h4 className="text-zinc-300 font-semibold text-sm flex items-center gap-2 mb-2">
                            ⭐ Their Picks ({reviewerPicks.length})
                          </h4>
                          {reviewerPicks.length === 0 ? (
                            <p className="text-zinc-500 text-xs">No picks yet</p>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {reviewerPicks.map(clip => {
                                const cat = CATEGORIES.find(c => c.id === clip.categoryId);
                                return (
                                  <span key={clip.id} className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-lg">
                                    {cat?.emoji} {clip.title}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Submission Reviews by this Reviewer */}
                        {(() => {
                          // Show submission review rows for this reviewer; match both id and code for older saved data.
                          const reviewedSubmissionRows = clips.flatMap(sub => {
                            const matchingReviews = getSubmittedClipReviews(sub).filter(rev => submittedReviewBelongsToReviewer(rev, r));
                            if (matchingReviews.length > 0) {
                              return matchingReviews.map(rev => ({ sub, rev }));
                            }
                            const legacyReviewedBy = (sub as any).reviewedBy;
                            if (legacyReviewedBy === r.id || legacyReviewedBy === r.code) {
                              return [{ sub, rev: undefined }];
                            }
                            // If there's only one reviewer, show legacy reviewed submissions even if no row was saved.
                            if (reviewers.length === 1 && (sub.review || sub.reviews?.length || sub.status !== 'Submitted')) {
                              return [{ sub, rev: getSubmittedClipReviews(sub)[0] }];
                            }
                            return [];
                          });
                          if (reviewedSubmissionRows.length === 0) return null;
                          const reviewedClipCount = new Set(reviewedSubmissionRows.map(item => item.sub.id)).size;
                          return (
                            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                              <h4 className="text-emerald-400 font-semibold text-sm flex items-center gap-2 mb-3">
                                <Send className="w-4 h-4" /> Clip Submission Reviews ({reviewedSubmissionRows.length} review{reviewedSubmissionRows.length !== 1 ? 's' : ''} / {reviewedClipCount} clip{reviewedClipCount !== 1 ? 's' : ''})
                              </h4>
                              <div className="space-y-3">
                                {reviewedSubmissionRows.map(({ sub, rev }, rowIdx) => {
                                  return (
                                  <div key={`${sub.id}-${rev?.reviewerId || rowIdx}`} className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700">
                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                      <span className="text-white text-sm font-medium">{sub.clipTitle}</span>
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        sub.status === 'Approved' ? 'bg-green-500/20 text-green-400' :
                                        sub.status === 'Needs Revision' ? 'bg-orange-500/20 text-orange-400' :
                                        sub.status === 'Rejected' ? 'bg-red-500/20 text-red-400' :
                                        'bg-blue-500/20 text-blue-400'
                                      }`}>{sub.status}</span>
                                      {sub.gender && (
                                        <span className={`px-2 py-0.5 rounded text-xs ${sub.gender === 'male' ? 'bg-blue-500/20 text-blue-300' : sub.gender === 'couple' ? 'bg-purple-500/20 text-purple-300' : 'bg-pink-500/20 text-pink-300'}`}>
                                          {sub.gender}
                                        </span>
                                      )}
                                      {rev && (
                                        <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                                          Quality {rev.qualityRating}/5
                                        </span>
                                      )}
                                      {rev?.reviewedAt && (
                                        <span className="text-zinc-500 text-xs">{new Date(rev.reviewedAt).toLocaleDateString()}</span>
                                      )}
                                      {!rev && (
                                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">No review data saved</span>
                                      )}
                                    </div>

                                    {sub.product && <p className="text-zinc-500 text-xs mb-2">Product: {sub.product}</p>}

                                    {rev && (
                                      <>
                                        {/* Compliance */}
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                          <span className={`px-2 py-0.5 rounded text-xs ${rev.onBrand ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                            {rev.onBrand ? 'On Brand' : 'Not On Brand'}
                                          </span>
                                          <span className={`px-2 py-0.5 rounded text-xs ${rev.showsProduct ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                            {rev.showsProduct ? 'Shows Product' : 'No Product'}
                                          </span>
                                          <span className={`px-2 py-0.5 rounded text-xs ${rev.claimsCompliant ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                            {rev.claimsCompliant ? 'Claims OK' : 'Claims Issue'}
                                          </span>
                                        </div>

                                        {/* Gut Reaction */}
                                        <div className="grid grid-cols-3 gap-1.5 text-xs mb-2">
                                          <div className="bg-zinc-900/50 rounded px-2 py-1.5">
                                            <span className="text-zinc-500">Try product:</span> <span className="text-white capitalize">{rev.wouldTryProduct?.replace(/-/g, ' ')}</span>
                                          </div>
                                          <div className="bg-zinc-900/50 rounded px-2 py-1.5">
                                            <span className="text-zinc-500">Show partner:</span> <span className="text-white capitalize">{rev.wouldShowPartner?.replace(/-/g, ' ')}</span>
                                          </div>
                                          <div className="bg-zinc-900/50 rounded px-2 py-1.5">
                                            <span className="text-zinc-500">Share w/ friend:</span> <span className="text-white capitalize">{rev.wouldShareWithFriend?.replace(/-/g, ' ')}</span>
                                          </div>
                                          {rev.enjoyedIt && (
                                            <div className="bg-zinc-900/50 rounded px-2 py-1.5">
                                              <span className="text-zinc-500">Enjoyment:</span> <span className="text-white capitalize">{rev.enjoyedIt.replace(/-/g, ' ')}</span>
                                            </div>
                                          )}
                                          {rev.arousedThem && (
                                            <div className="bg-zinc-900/50 rounded px-2 py-1.5">
                                              <span className="text-zinc-500">Arousal:</span> <span className="text-white capitalize">{rev.arousedThem.replace(/-/g, ' ')}</span>
                                            </div>
                                          )}
                                        </div>

                                        {/* How it made them feel */}
                                        {rev.howItMadeThemFeel && (
                                          <p className="text-zinc-300 text-xs bg-zinc-900/50 rounded px-2 py-1.5 mb-2">
                                            <span className="text-zinc-500">Feeling:</span> "{rev.howItMadeThemFeel}"
                                          </p>
                                        )}

                                        {/* What intrigues them */}
                                        {rev.intrigues && (
                                          <p className="text-purple-300 text-xs bg-purple-500/10 rounded px-2 py-1.5 mb-2">
                                            <span className="text-purple-400">Intrigued by:</span> "{rev.intrigues}"
                                          </p>
                                        )}

                                        {/* Follow-up suggestions from review */}
                                        {rev.followUpSuggestions && rev.followUpSuggestions.length > 0 && (
                                          <div className="bg-purple-500/10 rounded px-2 py-1.5 mb-2">
                                            <p className="text-purple-400 text-[10px] uppercase tracking-wide mb-1">Queued Follow-ups</p>
                                            {rev.followUpSuggestions.map((q: string, qi: number) => (
                                              <p key={qi} className="text-purple-300 text-xs">• {q}</p>
                                            ))}
                                          </div>
                                        )}
                                      </>
                                    )}

                                    {/* Male Appearance */}
                                    {sub.maleAppearance && (
                                      <div className="flex gap-1.5 mb-2">
                                        <span className="px-2 py-0.5 bg-blue-500/10 text-blue-300 rounded text-xs">Size: {sub.maleAppearance.cockSize}</span>
                                        <span className="px-2 py-0.5 bg-blue-500/10 text-blue-300 rounded text-xs">Hardness: {sub.maleAppearance.hardness}</span>
                                        {sub.maleAppearance.notes && <span className="px-2 py-0.5 bg-blue-500/10 text-blue-300 rounded text-xs">{sub.maleAppearance.notes}</span>}
                                      </div>
                                    )}

                                    {/* Load Boost Profile */}
                                    {sub.loadBoostProfile && (
                                      <div className="flex flex-wrap gap-1.5 mb-2">
                                        <span className="px-2 py-0.5 bg-purple-500/10 text-purple-300 rounded text-xs">Consistency: {sub.loadBoostProfile.consistencyType?.replace(/-/g, ' ')}</span>
                                        {sub.loadBoostProfile.finishLocation && <span className="px-2 py-0.5 bg-purple-500/10 text-purple-300 rounded text-xs">Finish: {sub.loadBoostProfile.finishLocation}</span>}
                                        {sub.loadBoostProfile.standoutNotes && <p className="text-zinc-300 text-xs w-full">"{sub.loadBoostProfile.standoutNotes}"</p>}
                                      </div>
                                    )}

                                    {/* Notes */}
                                    {rev?.notes && (
                                      <p className="text-zinc-300 text-xs bg-zinc-900/50 rounded px-2 py-1.5 mb-2">
                                        <span className="text-zinc-500">Notes:</span> "{rev.notes}"
                                      </p>
                                    )}

                                    {/* Revision Requests */}
                                    {rev?.revisionRequests && (
                                      <p className="text-orange-300 text-xs bg-orange-500/10 rounded px-2 py-1.5 mb-2">
                                        <span className="text-orange-400">Revisions:</span> {rev.revisionRequests}
                                      </p>
                                    )}

                                    {/* Creator Suggestions */}
                                    {rev?.creatorSuggestions && (
                                      <p className="text-zinc-300 text-xs bg-zinc-900/50 rounded px-2 py-1.5">
                                        <span className="text-zinc-500">Suggestions:</span> "{rev.creatorSuggestions}"
                                      </p>
                                    )}

                                    {/* Standout Notes */}
                                    {sub.standoutNotes && (
                                      <p className="text-zinc-300 text-xs bg-zinc-900/50 rounded px-2 py-1.5 mt-2">
                                        <span className="text-zinc-500">Standout:</span> "{sub.standoutNotes}"
                                      </p>
                                    )}

                                    {/* Per-response follow-up suggestions */}
                                    {rev && (() => {
                                      const suggestions: { label: string; question: string }[] = [];
                                      const title = sub.clipTitle;
                                      // Arousal-based
                                      if (rev.arousedThem === 'somewhat') suggestions.push({ label: `Arousal: ${rev.arousedThem}`, question: `Re "${title}" — you said it was somewhat arousing. What specifically turned you on, and what would have made it more arousing?` });
                                      if (rev.arousedThem === 'not-really') suggestions.push({ label: `Arousal: not really`, question: `Re "${title}" — arousal was low for you. What kind of content would you find more arousing? Is it the couple, the angle, the energy, or something else?` });
                                      if (rev.arousedThem === 'very') suggestions.push({ label: `Arousal: very`, question: `Re "${title}" — you were really turned on by this. What specifically got you — the visuals, the couple's dynamic, or the outcome?` });
                                      // Enjoyment-based
                                      if (rev.enjoyedIt === 'neutral') suggestions.push({ label: `Enjoyment: neutral`, question: `Re "${title}" — you felt neutral about this one. What was missing for you — the energy, the angle, the couple, or the product result?` });
                                      if (rev.enjoyedIt === 'loved-it' || rev.enjoyedIt === 'enjoyed-it') suggestions.push({ label: `Enjoyed it`, question: `Re "${title}" — you enjoyed this one. Would you like to see more content from this couple?` });
                                      // Size/appearance-based
                                      if (sub.maleAppearance?.cockSize === 'huge' || sub.maleAppearance?.cockSize === 'large') {
                                        suggestions.push({ label: `Size: ${sub.maleAppearance.cockSize}`, question: `Re "${title}" — you noted the size as ${sub.maleAppearance.cockSize}. Do you prefer seeing larger sizes in content, or is the outcome more important than size to you?` });
                                        suggestions.push({ label: `Solo content?`, question: `Re "${title}" — would you be interested in just this man's solo content if he had it?` });
                                      }
                                      // Product conviction
                                      if (rev.wouldTryProduct === 'yes') suggestions.push({ label: `Would try product`, question: `Re "${title}" — you said you'd try the product after watching. What about this clip convinced you?` });
                                      if (rev.wouldShowPartner === 'yes') suggestions.push({ label: `Would show partner`, question: `Re "${title}" — you'd show this to your partner. What do you think they'd react to most?` });
                                      // Feeling-based
                                      if (rev.howItMadeThemFeel && rev.howItMadeThemFeel.length > 3) suggestions.push({ label: `Feeling`, question: `Re "${title}" — you said it made you feel "${rev.howItMadeThemFeel}". Can you tell us more about what triggered that reaction?` });
                                      // Load Boost specific
                                      if (sub.loadBoostProfile?.consistencyType === 'thick-ropey') suggestions.push({ label: `Thick consistency`, question: `Re "${title}" — the load was thick and ropey. Is that the consistency you'd want to see more of, or do you prefer a different look?` });
                                      // More from couple
                                      if (sub.gender === 'couple' || sub.gender === 'male') suggestions.push({ label: `More from couple?`, question: `Re "${title}" — would you like to see more content from this couple, or a different category/product from them?` });

                                      if (suggestions.length === 0) return null;
                                      return (
                                        <div className="mt-2 pt-2 border-t border-zinc-700">
                                          <p className="text-purple-400 text-[10px] uppercase tracking-wide mb-1.5">Follow-up based on responses</p>
                                          <div className="flex flex-wrap gap-1.5 mb-2">
                                            {suggestions.map((s, si) => (
                                              <button
                                                key={si}
                                                onClick={() => {
                                                  setSubmissionFollowUp({ reviewerId: r.id, subId: sub.id });
                                                  setSubmissionFollowUpText(s.question);
                                                }}
                                                className="px-2 py-1 text-[11px] bg-purple-500/10 text-purple-300 border border-purple-500/20 rounded-lg hover:bg-purple-500/20 hover:border-purple-500/40 transition-colors"
                                              >
                                                {s.label}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    })()}

                                    {/* Follow-up input */}
                                    {submissionFollowUp?.reviewerId === r.id && submissionFollowUp?.subId === sub.id ? (
                                      <div className="mt-2 pt-2 border-t border-zinc-700">
                                        <textarea
                                          value={submissionFollowUpText}
                                          onChange={e => setSubmissionFollowUpText(e.target.value)}
                                          placeholder="Ask about their review..."
                                          rows={3}
                                          className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-2 text-white text-xs resize-none focus:outline-none focus:border-purple-500/50"
                                          autoFocus
                                        />
                                        <div className="flex gap-2 mt-2">
                                          <button
                                            onClick={() => { setSubmissionFollowUp(null); setSubmissionFollowUpText(''); }}
                                            className="px-2 py-1 text-xs text-zinc-500 hover:text-white"
                                          >
                                            Cancel
                                          </button>
                                          <button
                                            onClick={() => {
                                              if (submissionFollowUpText.trim()) {
                                                sendFollowUp(r.id, submissionFollowUpText.trim(), `Re: submission review "${sub.clipTitle}" (${sub.product || 'clip'})`, undefined, [sub.id]);
                                                setSubmissionFollowUp(null);
                                                setSubmissionFollowUpText('');
                                                setFollowUpSentFlash(sub.id);
                                                setTimeout(() => setFollowUpSentFlash(null), 2500);
                                              }
                                            }}
                                            disabled={!submissionFollowUpText.trim()}
                                            className="px-3 py-1 text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-lg hover:bg-purple-500/30 disabled:opacity-50 transition-colors"
                                          >
                                            Send Follow-up
                                          </button>
                                        </div>
                                      </div>
                                    ) : followUpSentFlash === sub.id ? (
                                      <p className="mt-2 text-center text-xs text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg py-1.5 animate-pulse">
                                        <Check className="w-3 h-3 inline mr-1" /> Sent! Check Questions tab
                                      </p>
                                    ) : (
                                      <button
                                        onClick={() => { setSubmissionFollowUp({ reviewerId: r.id, subId: sub.id }); setSubmissionFollowUpText(''); }}
                                        className="mt-2 w-full flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-purple-400 bg-purple-500/10 border border-purple-500/20 rounded-lg hover:bg-purple-500/20 transition-colors"
                                      >
                                        <MessageSquare className="w-3 h-3" /> Custom follow-up
                                      </button>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Delete Reviewer Button */}
                        <div className="pt-4 border-t border-zinc-700">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteReviewer(r.id);
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg text-sm font-medium transition-colors"
                          >
                            <Trash2 className="w-4 h-4" /> Delete Reviewer
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Couples Tab */}
        {tab === 'couples' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30 rounded-xl p-4 mb-4">
              <h3 className="text-white font-semibold flex items-center gap-2 mb-2">
                <HeartHandshake className="w-5 h-5 text-pink-400" />
                All Couples
              </h3>
              <p className="text-zinc-400 text-sm">
                All couples registered in the system (from DateNite or Reviewer setup). Click "View Schedule" to manage their calendar.
              </p>
            </div>

            {allCouples.length === 0 && reviewers.filter(r => r.coupleId).length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                No couples registered yet. Couples are created through DateNite setup or reviewer onboarding.
              </div>
            ) : (
              <div className="space-y-3">
                {/* Show couples from global storage first */}
                {allCouples.map(couple => {
                  // Find linked reviewer if any
                  const linkedReviewer = reviewers.find(r => r.coupleId === couple.coupleId);
                  const isExpanded = expandedCouple === couple.coupleId;
                  const summary = peopleCouples.get(couple.coupleId);
                  const triedCount = summary?.triedCount ?? couple.tried?.length ?? 0;
                  const favoritesCount = couple.favorites?.length || 0;
                  const prefsCount = couple.preferences ? Object.keys(couple.preferences).length : 0;

                  return (
                    <div
                      key={couple.coupleId}
                      id={`admin-couples-${couple.coupleId}`}
                      className="bg-zinc-900 rounded-xl border border-pink-500/30 overflow-hidden scroll-mt-4"
                    >
                      {/* Clickable Header */}
                      <div
                        className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 p-3 cursor-pointer hover:from-pink-500/30 hover:to-purple-500/30 transition-colors"
                        onClick={() => setExpandedCouple(isExpanded ? null : couple.coupleId)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Heart className="w-5 h-5 text-pink-400" />
                            <span className="font-mono text-pink-400 font-semibold">{couple.coupleId}</span>
                            {/* Online status indicator - check both couple login AND linked reviewer */}
                            {(() => {
                              const couplePresence = isUserOnline('couple', couple.coupleId);
                              const reviewerPresence = linkedReviewer ? isUserOnline('reviewer', linkedReviewer.id) : undefined;

                              if (couplePresence) {
                                return (
                                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1 animate-pulse">
                                    <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                                    Online • {couplePresence.currentPage === 'home' ? 'Home' : couplePresence.currentPage}
                                  </span>
                                );
                              }
                              if (reviewerPresence) {
                                return (
                                  <span className="px-2 py-0.5 bg-lime-500/20 text-lime-400 text-xs rounded-full flex items-center gap-1 animate-pulse">
                                    <span className="w-2 h-2 bg-lime-400 rounded-full"></span>
                                    {linkedReviewer?.code} Online • {reviewerPresence.currentPage === 'home' ? 'Home' : reviewerPresence.currentPage}
                                  </span>
                                );
                              }
                              return null;
                            })()}
                            {linkedReviewer && !isUserOnline('reviewer', linkedReviewer.id) && (
                              <span className="px-2 py-0.5 bg-lime-500/20 text-lime-400 text-xs rounded-full">
                                Linked: {linkedReviewer.code}
                              </span>
                            )}
                            {summary?.coaches.map(coach => (
                              <span key={coach.id} className="px-2 py-0.5 bg-lime-500/10 text-lime-300 text-xs rounded-full border border-lime-500/30">
                                Coach: {coach.label}
                              </span>
                            ))}
                          </div>
                          <div className="flex items-center gap-3">
                            {/* Quick Stats */}
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-zinc-400">⭐ {favoritesCount}</span>
                              <span className="text-zinc-400">✓ {triedCount}</span>
                              <span className="text-zinc-400">🎯 {prefsCount}</span>
                            </div>
                            <span className="text-xs text-zinc-500">
                              {new Date(couple.setupAt).toLocaleDateString()}
                            </span>
                            {isExpanded ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
                          </div>
                        </div>

                        {/* Summary Row - Always visible */}
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium">{couple.hisName}</span>
                          <span className="text-pink-400">&</span>
                          <span className="text-white font-medium">{couple.herName}</span>
                          {couple.managedBy && (
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                              {couple.managedBy === 'him' ? '👨' : couple.managedBy === 'her' ? '👩' : '👫'} manages
                            </span>
                          )}
                          {summary && summary.submissionCount > 0 && (
                            <span className="px-2 py-0.5 bg-amber-500/15 text-amber-300 text-xs rounded-full">
                              {summary.submissionCount} submission{summary.submissionCount !== 1 ? 's' : ''}
                              {summary.pendingSubmissionCount > 0 && ` · ${summary.pendingSubmissionCount} pending`}
                            </span>
                          )}
                          {summary?.lastSeen && !isUserOnline('couple', couple.coupleId) && (
                            <span className="text-xs text-zinc-500">Seen {formatLastActive(summary.lastSeen)}</span>
                          )}
                          {summary && summary.loginCount > 0 && (
                            <span className="text-xs text-zinc-500">· {summary.loginCount} login{summary.loginCount !== 1 ? 's' : ''}</span>
                          )}
                          {couple.lastActivityAt && (
                            <span className="text-xs text-zinc-500 ml-auto">
                              Last: {new Date(couple.lastActivityAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {new Date(couple.lastActivityAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="border-t border-pink-500/30 p-4 space-y-4">
                          {/* Tab Navigation */}
                          {(() => {
                            const currentTab = coupleCardTab[couple.coupleId] || 'inbox';
                            const pendingFollowUpsCount = coupleFollowUps.filter(fu => fu.coupleId === couple.coupleId && fu.status === 'pending').length;
                            const pendingQuestionsCount = coupleQuestions.filter(q => q.coupleId === couple.coupleId && q.status === 'pending').length;
                            const pendingSuggestionsCount = clipSuggestions.filter(s => s.coupleId === couple.coupleId && s.status === 'pending').length;
                            const pendingCount = pendingFollowUpsCount + pendingQuestionsCount + pendingSuggestionsCount;
                            return (
                              <div className="flex gap-1 mb-4 border-b border-zinc-700 -mt-2">
                                {(['inbox', 'activity', 'profile'] as const).map(tab => (
                                  <button
                                    key={tab}
                                    onClick={() => setCoupleCardTab(prev => ({ ...prev, [couple.coupleId]: tab }))}
                                    className={`px-3 py-2 text-sm font-medium capitalize ${
                                      currentTab === tab
                                        ? 'text-white border-b-2 border-pink-500'
                                        : 'text-zinc-400 hover:text-zinc-200'
                                    }`}
                                  >
                                    {tab}
                                    {tab === 'inbox' && pendingCount > 0 && (
                                      <span className="ml-1 px-1.5 py-0.5 bg-pink-500 text-white text-xs rounded-full">
                                        {pendingCount}
                                      </span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            );
                          })()}

                          {/* ===== ACTIVITY TAB ===== */}
                          {(coupleCardTab[couple.coupleId] || 'inbox') === 'activity' && (
                          <>
                          {/* Activity Stats */}
                          {(() => {
                            // Calculate couple's category engagement
                            const coupleViews = clipViews.filter(v => v.viewerId === couple.coupleId);
                            const viewedClipIds = coupleViews.map(v => v.clipId);
                            const triedClipIds = (couple.tried || []).map(t => t.clipId);
                            const favoriteClipIds = couple.favorites || [];
                            const engagedClipIds = [...new Set([...viewedClipIds, ...triedClipIds, ...favoriteClipIds])];
                            const engagedCategories = new Set(
                              dateNiteClips
                                .filter(c => engagedClipIds.includes(c.id))
                                .map(c => c.categoryId)
                            );
                            const coverage = CATEGORIES.length > 0 ? (engagedCategories.size / CATEGORIES.length) * 100 : 0;

                            return (
                              <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-3">
                                <h4 className="text-pink-400 font-semibold text-sm flex items-center gap-2 mb-2">
                                  <BarChart3 className="w-4 h-4" /> Activity Summary
                                </h4>
                                <div className="grid grid-cols-5 gap-2 mb-3">
                                  <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                                    <p className="text-lg font-bold text-blue-400">{coupleViews.length}</p>
                                    <p className="text-zinc-500 text-xs">Views</p>
                                  </div>
                                  <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                                    <p className="text-lg font-bold text-yellow-400">{favoritesCount}</p>
                                    <p className="text-zinc-500 text-xs">Favorites</p>
                                  </div>
                                  <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                                    <p className="text-lg font-bold text-green-400">{triedCount}</p>
                                    <p className="text-zinc-500 text-xs">Tried</p>
                                  </div>
                                  <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                                    <p className="text-lg font-bold text-purple-400">{prefsCount}</p>
                                    <p className="text-zinc-500 text-xs">Preferences</p>
                                  </div>
                                  <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                                    <p className="text-lg font-bold" style={{ color: coverage > 50 ? COLORS.lime : coverage > 25 ? '#fbbf24' : '#ef4444' }}>
                                      {Math.round(coverage)}%
                                    </p>
                                    <p className="text-zinc-500 text-xs">Coverage</p>
                                  </div>
                                </div>

                                {/* Category Coverage Visual */}
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-zinc-400 text-xs">Categories Explored</p>
                                    <p className="text-xs font-medium text-zinc-400">
                                      {engagedCategories.size}/{CATEGORIES.length}
                                    </p>
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {CATEGORIES.map(cat => (
                                      <span
                                        key={cat.id}
                                        className={`px-2 py-1 rounded text-xs ${
                                          engagedCategories.has(cat.id)
                                            ? 'bg-green-500/20 text-green-400'
                                            : 'bg-zinc-700 text-zinc-500'
                                        }`}
                                        title={`${cat.name} - ${engagedCategories.has(cat.id) ? 'Engaged' : 'Not explored'}`}
                                      >
                                        {cat.emoji}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Couple Codes */}
                          <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
                            <h4 className="text-zinc-400 font-semibold text-sm mb-2">Couple Codes</h4>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="bg-pink-500/10 border border-pink-500/30 rounded p-2">
                                <p className="text-pink-400 font-semibold">🔐 Access</p>
                                <p className="text-pink-400 font-mono">
                                  {(() => {
                                    // Direct from couple data
                                    if (couple.accessCode) return couple.accessCode;
                                    // Fallback: reverse lookup from index
                                    const fromIndex = Object.entries(accessCodeIndex).find(([, cid]) => cid === couple.coupleId);
                                    if (fromIndex) return fromIndex[0];
                                    // Debug: log if missing
                                    console.log('Missing access code for couple:', couple.coupleId, 'Index entries:', Object.keys(accessCodeIndex).length);
                                    return 'Not set';
                                  })()}
                                </p>
                              </div>
                              <div className="bg-lime-500/10 border border-lime-500/30 rounded p-2">
                                <p className="text-lime-400 font-semibold">📤 Sharing</p>
                                <p className="text-lime-400 font-mono">{couple.coupleId}</p>
                              </div>
                            </div>
                          </div>

                          {/* Clips Viewed (Playback History) */}
                          {(() => {
                            const coupleViews = clipViews.filter(v => v.viewerId === couple.coupleId);
                            if (coupleViews.length === 0) return null;
                            return (
                              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                                <h4 className="text-blue-400 font-semibold text-sm flex items-center gap-2 mb-2">
                                  <Eye className="w-4 h-4" /> Clips Viewed ({coupleViews.length})
                                </h4>
                                <div className="space-y-1 max-h-32 overflow-y-auto">
                                  {coupleViews.slice().reverse().slice(0, 10).map((view, i) => {
                                    const clip = dateNiteClips.find(c => c.id === view.clipId);
                                    const cat = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                                    return (
                                      <div key={i} className="flex items-center justify-between text-xs bg-zinc-800/50 rounded px-2 py-1">
                                        <div className="flex items-center gap-2">
                                          <span>{cat?.emoji || '📹'}</span>
                                          <span className="text-zinc-300 truncate">{clip?.title || view.clipId}</span>
                                        </div>
                                        <span className="text-zinc-500">{new Date(view.viewedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {new Date(view.viewedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                                      </div>
                                    );
                                  })}
                                  {coupleViews.length > 10 && (
                                    <p className="text-zinc-500 text-xs text-center">+ {coupleViews.length - 10} more views</p>
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Activity Timeline */}
                          {couple.tried && couple.tried.length > 0 && (() => {
                            const withFeedback = (couple.tried || []).filter(t => t.wouldDoAgain || t.inspiresDifferent);
                            const missingFeedback = (couple.tried || []).filter(t => !t.wouldDoAgain && !t.inspiresDifferent);
                            return (
                            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                              <h4 className="text-green-400 font-semibold text-sm flex items-center gap-2 mb-2">
                                <Star className="w-4 h-4" /> Clips Tried ({(couple.tried || []).length})
                                {missingFeedback.length > 0 && (
                                  <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">
                                    {missingFeedback.length} need feedback
                                  </span>
                                )}
                                {withFeedback.length > 0 && (
                                  <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                                    {withFeedback.length} complete
                                  </span>
                                )}
                              </h4>
                              <div className="space-y-2 max-h-64 overflow-y-auto">
                                {(couple.tried || []).slice().reverse().map((tried, i) => {
                                  const clip = dateNiteClips.find(c => c.id === tried.clipId);
                                  const hasDetailedFeedback = tried.hisThoughts || tried.herThoughts || tried.wouldDoAgain || tried.inspiresDifferent;
                                  const needsFeedback = !tried.wouldDoAgain && !tried.inspiresDifferent;
                                  return (
                                    <div key={i} className={`bg-zinc-800/50 rounded p-2 ${needsFeedback ? 'border-l-2 border-amber-500' : ''}`}>
                                      <div className="flex items-center justify-between text-xs">
                                        <span className="text-zinc-300 font-medium flex items-center gap-1">
                                          {clip?.title || tried.clipId}
                                          {needsFeedback && (
                                            <span className="text-amber-400" title="Missing follow-up feedback">⚠️</span>
                                          )}
                                        </span>
                                        <div className="flex items-center gap-2">
                                          <span className={tried.rating === 'hot' ? 'text-red-400' : tried.rating === 'good' ? 'text-green-400' : tried.rating === 'meh' ? 'text-amber-400' : 'text-zinc-500'}>
                                            {tried.rating === 'hot' ? '🔥🔥🔥' : tried.rating === 'good' ? '🔥🔥' : tried.rating === 'meh' ? '🔥' : '❌'}
                                          </span>
                                          <span className="text-zinc-500">{new Date(tried.triedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                          {/* Request feedback button */}
                                          {needsFeedback && saveCoupleFollowUps && clip && (
                                            <button
                                              onClick={() => {
                                                const ratingText = tried.rating === 'hot' ? 'loved' : tried.rating === 'good' ? 'enjoyed' : tried.rating === 'meh' ? 'found okay' : 'tried';
                                                queueCoupleFollowUp(
                                                  couple.coupleId,
                                                  `${couple.hisName} & ${couple.herName}`,
                                                  `You ${ratingText} "${clip.title}" - we'd love more detail! Would you do it again? Did it inspire you to try something different?`,
                                                  'Requesting tried clip feedback',
                                                  clip.id
                                                );
                                              }}
                                              className="px-1.5 py-0.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded text-xs"
                                              title="Request feedback for this clip"
                                            >
                                              📩
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                      {hasDetailedFeedback && (
                                        <div className="mt-2 pt-2 border-t border-zinc-700 space-y-1">
                                          {tried.hisThoughts && (
                                            <p className="text-xs"><span className="text-blue-400">👨 Him:</span> <span className="text-zinc-400">"{tried.hisThoughts}"</span></p>
                                          )}
                                          {tried.herThoughts && (
                                            <p className="text-xs"><span className="text-pink-400">👩 Her:</span> <span className="text-zinc-400">"{tried.herThoughts}"</span></p>
                                          )}
                                          <div className="flex flex-wrap gap-2 mt-1">
                                            {tried.wouldDoAgain && (
                                              <span className={`px-2 py-0.5 rounded text-xs ${
                                                tried.wouldDoAgain === 'yes' ? 'bg-green-500/20 text-green-400' :
                                                tried.wouldDoAgain === 'maybe' ? 'bg-amber-500/20 text-amber-400' :
                                                'bg-red-500/20 text-red-400'
                                              }`}>
                                                🔄 {tried.wouldDoAgain === 'yes' ? 'Would do again!' : tried.wouldDoAgain === 'maybe' ? 'Maybe again' : 'Wouldn\'t repeat'}
                                              </span>
                                            )}
                                            {tried.inspiresDifferent && (
                                              <span className={`px-2 py-0.5 rounded text-xs ${
                                                tried.inspiresDifferent === 'yes' ? 'bg-purple-500/20 text-purple-400' : 'bg-zinc-600 text-zinc-400'
                                              }`}>
                                                💡 {tried.inspiresDifferent === 'yes' ? 'Inspired new ideas' : 'No new ideas'}
                                              </span>
                                            )}
                                          </div>
                                          {tried.howDifferent && (
                                            <p className="text-xs mt-1"><span className="text-purple-400">How differently:</span> <span className="text-zinc-400">"{tried.howDifferent}"</span></p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            );
                          })()}
                          </>
                          )}

                          {/* ===== PROFILE TAB ===== */}
                          {(coupleCardTab[couple.coupleId] || 'inbox') === 'profile' && (
                          <>
                          {/* Category Preferences */}
                          {couple.preferences && Object.keys(couple.preferences).length > 0 && (
                            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                              <h4 className="text-purple-400 font-semibold text-sm flex items-center gap-2 mb-2">
                                🎯 Category Preferences
                              </h4>
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(couple.preferences).map(([catId, pref]) => {
                                  const cat = CATEGORIES.find(c => c.id === catId);
                                  const prefColors: Record<string, string> = {
                                    'love': 'bg-pink-500/30 text-pink-400',
                                    'like': 'bg-green-500/30 text-green-400',
                                    'curious': 'bg-amber-500/30 text-amber-400',
                                    'nope': 'bg-red-500/30 text-red-400',
                                    'neutral': 'bg-zinc-700 text-zinc-400'
                                  };
                                  return (
                                    <span
                                      key={catId}
                                      className={`px-2 py-0.5 rounded text-xs ${prefColors[pref] || 'bg-zinc-700 text-zinc-400'}`}
                                    >
                                      {cat?.emoji} {pref}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Category Q&A Responses (Why they like categories) */}
                          {couple.categoryQA && Object.keys(couple.categoryQA).length > 0 && (
                            <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3">
                              <h4 className="text-indigo-400 font-semibold text-sm flex items-center gap-2 mb-2">
                                💬 Category Q&A ({Object.keys(couple.categoryQA).length} categories)
                              </h4>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {Object.entries(couple.categoryQA).map(([catId, qa]) => {
                                  const cat = CATEGORIES.find(c => c.id === catId);
                                  const answerCount = Object.keys(qa.answers || {}).length;
                                  return (
                                    <div key={catId} className="bg-zinc-800/50 rounded-lg p-2">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-white text-xs flex items-center gap-1">
                                          {cat?.emoji} {cat?.name || catId}
                                        </span>
                                        <span className="text-zinc-500 text-xs">{answerCount} answers</span>
                                      </div>
                                      <div className="space-y-1">
                                        {Object.entries(qa.answers || {}).map(([qId, answer]) => (
                                          <p key={qId} className="text-zinc-400 text-xs pl-2 border-l border-indigo-500/30">
                                            "{answer}"
                                          </p>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                        {/* His Profile Stats */}
                        {couple.hisProfile && (couple.hisProfile.size || couple.hisProfile.girth || couple.hisProfile.cumIntensity || couple.hisProfile.cumConsistency || couple.hisProfile.cumTaste || couple.hisProfile.precumProduction) && (
                          <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg p-2 border border-blue-500/20">
                            <p className="text-zinc-400 text-xs mb-2">🍆 His Stats</p>
                            <div className="flex flex-wrap gap-3 text-center">
                              {couple.hisProfile.size && (
                                <div>
                                  <span className="text-xl">{HIS_SIZE_SCALE.find(s => s.value === couple.hisProfile?.size)?.emoji}</span>
                                  <p className="text-xs text-zinc-400">{HIS_SIZE_SCALE.find(s => s.value === couple.hisProfile?.size)?.label}</p>
                                </div>
                              )}
                              {couple.hisProfile.girth && (
                                <div>
                                  <span className="text-xl">{HIS_GIRTH_SCALE.find(s => s.value === couple.hisProfile?.girth)?.emoji}</span>
                                  <p className="text-xs text-zinc-400">{HIS_GIRTH_SCALE.find(s => s.value === couple.hisProfile?.girth)?.size}</p>
                                </div>
                              )}
                              {couple.hisProfile.cumIntensity && (
                                <div>
                                  <span className="text-xl">{HIS_CUM_SCALE.find(s => s.value === couple.hisProfile?.cumIntensity)?.emoji}</span>
                                  <p className="text-xs text-zinc-400">{HIS_CUM_SCALE.find(s => s.value === couple.hisProfile?.cumIntensity)?.label}</p>
                                </div>
                              )}
                              {couple.hisProfile.cumConsistency && (
                                <div>
                                  <span className="text-xl">{HIS_CONSISTENCY_SCALE.find(s => s.value === couple.hisProfile?.cumConsistency)?.emoji}</span>
                                  <p className="text-xs text-zinc-400">{HIS_CONSISTENCY_SCALE.find(s => s.value === couple.hisProfile?.cumConsistency)?.label}</p>
                                </div>
                              )}
                              {couple.hisProfile.cumTaste && (
                                <div>
                                  <span className="text-xl">{HIS_TASTE_SCALE.find(s => s.value === couple.hisProfile?.cumTaste)?.emoji}</span>
                                  <p className="text-xs text-zinc-400">{HIS_TASTE_SCALE.find(s => s.value === couple.hisProfile?.cumTaste)?.label}</p>
                                </div>
                              )}
                              {couple.hisProfile.precumProduction && (
                                <div>
                                  <span className="text-xl">{HIS_PRECUM_SCALE.find(s => s.value === couple.hisProfile?.precumProduction)?.emoji}</span>
                                  <p className="text-xs text-zinc-400">Pre: {HIS_PRECUM_SCALE.find(s => s.value === couple.hisProfile?.precumProduction)?.label}</p>
                                </div>
                              )}
                            </div>
                            {(couple.hisProfile.consistencyNotes || couple.hisProfile.tasteNotes || couple.hisProfile.precumNotes) && (
                              <div className="mt-2 pt-2 border-t border-blue-500/20 space-y-1">
                                {couple.hisProfile.consistencyNotes && (
                                  <p className="text-zinc-400 text-xs"><span className="text-purple-400">Consistency:</span> {couple.hisProfile.consistencyNotes}</p>
                                )}
                                {couple.hisProfile.tasteNotes && (
                                  <p className="text-zinc-400 text-xs"><span className="text-amber-400">Taste:</span> {couple.hisProfile.tasteNotes}</p>
                                )}
                                {couple.hisProfile.precumNotes && (
                                  <p className="text-zinc-400 text-xs"><span className="text-blue-400">Precum:</span> {couple.hisProfile.precumNotes}</p>
                                )}
                              </div>
                            )}

                            {/* Visual Sharing Info */}
                            {couple.hisProfile.visualSharingOpen && (
                              <div className="mt-2 pt-2 border-t border-pink-500/20">
                                <div className="flex items-center gap-2 mb-2">
                                  <span>📸</span>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    couple.hisProfile.visualSharingOpen === 'yes' ? 'bg-green-500/20 text-green-400' :
                                    couple.hisProfile.visualSharingOpen === 'maybe' ? 'bg-amber-500/20 text-amber-400' :
                                    'bg-zinc-500/20 text-zinc-400'
                                  }`}>
                                    Visuals: {couple.hisProfile.visualSharingOpen === 'yes' ? 'Yes!' : couple.hisProfile.visualSharingOpen === 'maybe' ? 'Maybe' : 'No'}
                                  </span>
                                </div>
                                {couple.hisProfile.visualUrls && (
                                  <div className="space-y-1 text-xs">
                                    {couple.hisProfile.visualUrls.sizeUrl && (
                                      <a href={couple.hisProfile.visualUrls.sizeUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-pink-400 hover:underline">
                                        📏 Size verification →
                                      </a>
                                    )}
                                    {couple.hisProfile.visualUrls.cumUrl && (
                                      <a href={couple.hisProfile.visualUrls.cumUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-pink-400 hover:underline">
                                        💦 Cum verification →
                                      </a>
                                    )}
                                    {couple.hisProfile.visualUrls.precumUrl && (
                                      <a href={couple.hisProfile.visualUrls.precumUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-pink-400 hover:underline">
                                        💧 Precum verification →
                                      </a>
                                    )}
                                    {couple.hisProfile.visualUrls.otherUrl && (
                                      <a href={couple.hisProfile.visualUrls.otherUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-pink-400 hover:underline">
                                        🔗 Other visuals →
                                      </a>
                                    )}
                                  </div>
                                )}
                                {couple.hisProfile.visualSharingNotes && (
                                  <p className="text-zinc-400 text-xs mt-1 italic">"{couple.hisProfile.visualSharingNotes}"</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Her Profile Stats */}
                        {couple.herProfile && (couple.herProfile.typicalWetness || couple.herProfile.squirtingAbility || couple.herProfile.sensitivity || couple.herProfile.orgasmEase) && (
                          <div className="bg-gradient-to-r from-pink-500/10 to-rose-500/10 rounded-lg p-2 border border-pink-500/20">
                            <p className="text-zinc-400 text-xs mb-2">🌸 Her Stats</p>
                            <div className="flex flex-wrap gap-3 text-center">
                              {couple.herProfile.typicalWetness && (
                                <div>
                                  <span className="text-xl">{HER_WETNESS_SCALE.find(s => s.value === couple.herProfile?.typicalWetness)?.emoji}</span>
                                  <p className="text-xs text-zinc-400">{HER_WETNESS_SCALE.find(s => s.value === couple.herProfile?.typicalWetness)?.label}</p>
                                </div>
                              )}
                              {couple.herProfile.squirtingAbility !== undefined && couple.herProfile.squirtingAbility !== null && (
                                <div>
                                  <span className="text-xl">{HER_SQUIRT_SCALE.find(s => s.value === couple.herProfile?.squirtingAbility)?.emoji}</span>
                                  <p className="text-xs text-zinc-400">{HER_SQUIRT_SCALE.find(s => s.value === couple.herProfile?.squirtingAbility)?.label}</p>
                                </div>
                              )}
                              {couple.herProfile.sensitivity && (
                                <div>
                                  <span className="text-xl">{HER_SENSITIVITY_SCALE.find(s => s.value === couple.herProfile?.sensitivity)?.emoji}</span>
                                  <p className="text-xs text-zinc-400">{HER_SENSITIVITY_SCALE.find(s => s.value === couple.herProfile?.sensitivity)?.label}</p>
                                </div>
                              )}
                              {couple.herProfile.orgasmEase && (
                                <div>
                                  <span className="text-xl">{HER_ORGASM_EASE_SCALE.find(s => s.value === couple.herProfile?.orgasmEase)?.emoji}</span>
                                  <p className="text-xs text-zinc-400">{HER_ORGASM_EASE_SCALE.find(s => s.value === couple.herProfile?.orgasmEase)?.label}</p>
                                </div>
                              )}
                            </div>
                            {(couple.herProfile.wetnessNotes || couple.herProfile.squirtingNotes || couple.herProfile.sensitivityNotes || couple.herProfile.orgasmNotes) && (
                              <div className="mt-2 pt-2 border-t border-pink-500/20 space-y-1">
                                {couple.herProfile.wetnessNotes && (
                                  <p className="text-zinc-400 text-xs"><span className="text-pink-400">Wetness:</span> {couple.herProfile.wetnessNotes}</p>
                                )}
                                {couple.herProfile.squirtingNotes && (
                                  <p className="text-zinc-400 text-xs"><span className="text-violet-400">Squirting:</span> {couple.herProfile.squirtingNotes}</p>
                                )}
                                {couple.herProfile.sensitivityNotes && (
                                  <p className="text-zinc-400 text-xs"><span className="text-fuchsia-400">Sensitivity:</span> {couple.herProfile.sensitivityNotes}</p>
                                )}
                                {couple.herProfile.orgasmNotes && (
                                  <p className="text-zinc-400 text-xs"><span className="text-rose-400">Orgasm:</span> {couple.herProfile.orgasmNotes}</p>
                                )}
                              </div>
                            )}

                            {/* Visual Sharing Info */}
                            {couple.herProfile.visualSharingOpen && (
                              <div className="mt-2 pt-2 border-t border-pink-500/20">
                                <div className="flex items-center gap-2 mb-2">
                                  <span>📸</span>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    couple.herProfile.visualSharingOpen === 'yes' ? 'bg-green-500/20 text-green-400' :
                                    couple.herProfile.visualSharingOpen === 'maybe' ? 'bg-amber-500/20 text-amber-400' :
                                    'bg-zinc-500/20 text-zinc-400'
                                  }`}>
                                    Visuals: {couple.herProfile.visualSharingOpen === 'yes' ? 'Yes!' : couple.herProfile.visualSharingOpen === 'maybe' ? 'Maybe' : 'No'}
                                  </span>
                                </div>
                                {couple.herProfile.visualUrls && (
                                  <div className="space-y-1 text-xs">
                                    {couple.herProfile.visualUrls.wetnessUrl && (
                                      <a href={couple.herProfile.visualUrls.wetnessUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-pink-400 hover:underline">
                                        💦 Wetness verification →
                                      </a>
                                    )}
                                    {couple.herProfile.visualUrls.squirtUrl && (
                                      <a href={couple.herProfile.visualUrls.squirtUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-pink-400 hover:underline">
                                        🌊 Squirt verification →
                                      </a>
                                    )}
                                    {couple.herProfile.visualUrls.otherUrl && (
                                      <a href={couple.herProfile.visualUrls.otherUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-pink-400 hover:underline">
                                        🔗 Other visuals →
                                      </a>
                                    )}
                                  </div>
                                )}
                                {couple.herProfile.visualSharingNotes && (
                                  <p className="text-zinc-400 text-xs mt-1 italic">"{couple.herProfile.visualSharingNotes}"</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Linked Reviewer */}
                        {couple.managedByReviewerId && (
                          <div className="bg-lime-500/10 border border-lime-500/30 rounded-lg p-2">
                            <p className="text-lime-400 text-xs mb-1 font-semibold">👤 Linked Reviewer</p>
                            <p className="text-white text-sm">{couple.managedByReviewerName || couple.managedByReviewerId}</p>
                            {couple.managedByAcceptedAt && (
                              <p className="text-zinc-500 text-xs">Connected {new Date(couple.managedByAcceptedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                            )}
                          </div>
                        )}

                        {/* Wishlist */}
                        {(() => {
                          const coupleWishlist = wishlist.filter(w => w.coupleId === couple.coupleId);
                          if (coupleWishlist.length === 0) return null;
                          return (
                            <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-2">
                              <p className="text-rose-400 text-xs mb-2 font-semibold">⭐ Wishlist ({coupleWishlist.length})</p>
                              <div className="space-y-1 max-h-32 overflow-y-auto">
                                {coupleWishlist.map(item => {
                                  const clip = dateNiteClips.find(c => c.id === item.clipId);
                                  return (
                                    <div key={item.id} className="flex items-center justify-between bg-zinc-800/50 rounded p-1.5 text-xs">
                                      <span className="text-zinc-300 truncate">{clip?.title || item.clipId}</span>
                                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                                        item.status === 'want-to-try' ? 'bg-amber-500/20 text-amber-400' :
                                        item.status === 'tried' ? 'bg-green-500/20 text-green-400' :
                                        'bg-zinc-600 text-zinc-400'
                                      }`}>{item.status}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Favorites with Notes */}
                        {couple.favorites && couple.favorites.length > 0 && (
                          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2">
                            <p className="text-yellow-400 text-xs mb-2 font-semibold">💛 Favorites ({(couple.favorites || []).length})</p>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {(couple.favorites || []).map(favId => {
                                const clip = dateNiteClips.find(c => c.id === favId);
                                const note = couple.favoriteNotes?.[favId];
                                return (
                                  <div key={favId} className="bg-zinc-800/50 rounded p-1.5 text-xs">
                                    <p className="text-zinc-300 truncate">{clip?.title || favId}</p>
                                    {note && <p className="text-zinc-500 italic truncate">"{note}"</p>}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Available Days */}
                        {couple.availableDays && couple.availableDays.length > 0 && (
                          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2">
                            <p className="text-blue-400 text-xs mb-1 font-semibold">📅 Available Days</p>
                            <div className="flex gap-1">
                              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                                <span
                                  key={day}
                                  className={`px-2 py-1 rounded text-xs ${
                                    couple.availableDays?.includes(i)
                                      ? 'bg-blue-500/30 text-blue-400'
                                      : 'bg-zinc-700 text-zinc-500'
                                  }`}
                                >
                                  {day}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Profile Notes */}
                        {couple.profileNotes && (
                          <div className="bg-zinc-800/50 rounded-lg p-2">
                            <p className="text-zinc-400 text-xs mb-1">📝 Notes</p>
                            <p className="text-zinc-300 text-xs">{couple.profileNotes}</p>
                          </div>
                        )}

                        {/* Couple Feedback */}
                        {(couple.wouldSubmitClips || couple.productExcitement || couple.generalFeedback) && (
                          <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-lg p-2 border border-amber-500/20">
                            <p className="text-zinc-400 text-xs mb-2 flex items-center gap-1">
                              💬 Feedback
                              {couple.feedbackSubmittedAt && (
                                <span className="text-zinc-500">
                                  ({new Date(couple.feedbackSubmittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})
                                </span>
                              )}
                            </p>
                            <div className="flex flex-wrap gap-2 items-center">
                              {couple.wouldSubmitClips && (
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  couple.wouldSubmitClips === 'yes' ? 'bg-green-500/20 text-green-400' :
                                  couple.wouldSubmitClips === 'maybe' ? 'bg-amber-500/20 text-amber-400' :
                                  'bg-zinc-500/20 text-zinc-400'
                                }`}>
                                  {couple.wouldSubmitClips === 'yes' ? '📹 Yes, would submit clips' : couple.wouldSubmitClips === 'maybe' ? '📹 Maybe submit clips' : '📹 No, won\'t submit clips'}
                                </span>
                              )}
                              {couple.productExcitement && (
                                <span className="text-xs flex items-center gap-1">
                                  <span className="text-zinc-500">Excitement:</span>
                                  <span className="text-amber-400">{'★'.repeat(couple.productExcitement)}</span>
                                  <span className="text-zinc-600">{'★'.repeat(5 - couple.productExcitement)}</span>
                                </span>
                              )}
                            </div>
                            {couple.generalFeedback && (
                              <p className="text-zinc-300 text-xs mt-2 italic truncate">"{couple.generalFeedback}"</p>
                            )}
                          </div>
                        )}
                          </>
                          )}

                          {/* ===== INBOX TAB ===== */}
                          {(coupleCardTab[couple.coupleId] || 'inbox') === 'inbox' && (
                          <>
                        {/* Reviewer Questions to Couple (Threaded) */}
                        {(() => {
                          const questionsForCouple = coupleQuestions.filter(q => q.coupleId === couple.coupleId);
                          if (questionsForCouple.length === 0) return null;

                          // Separate root questions from replies
                          const rootQuestions = questionsForCouple.filter(q => !q.parentId);
                          const getReplies = (parentId: string) => questionsForCouple.filter(q => q.parentId === parentId);

                          const renderQuestion = (q: typeof questionsForCouple[0], isReply = false): React.ReactNode => (
                            <div key={q.id} className={`bg-zinc-800/50 rounded p-2 ${isReply ? 'ml-3 border-l-2 border-purple-500/30' : ''}`}>
                              <div className="flex justify-between text-xs mb-1">
                                <span className="text-purple-400">{q.fromReviewerName}</span>
                                <div className="flex items-center gap-2">
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                                    q.status === 'answered' ? 'bg-green-500/20 text-green-400' :
                                    q.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                                    'bg-zinc-500/20 text-zinc-400'
                                  }`}>
                                    {q.status}
                                  </span>
                                  <button
                                    onClick={() => deleteCoupleQuestion(q.id)}
                                    className="text-red-400 hover:text-red-300"
                                    title="Delete question"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                              <p className="text-white text-xs">Q: {q.question}</p>
                              {/* Show attached clips if exist */}
                              {(() => {
                                const clipIdsToShow = q.clipIds || (q.clipId ? [q.clipId] : []);
                                if (clipIdsToShow.length === 0) return null;
                                return (
                                  <div className="mt-1 space-y-1">
                                    {clipIdsToShow.map(clipId => {
                                      const attachedClip = dateNiteClips.find(c => c.id === clipId);
                                      if (!attachedClip) return null;
                                      return (
                                        <div key={clipId} className="flex items-center gap-2 bg-pink-500/10 border border-pink-500/30 rounded px-2 py-1">
                                          <Film className="w-3 h-3 text-pink-400" />
                                          <span className="text-pink-300 text-xs">{attachedClip.title}</span>
                                          {attachedClip.link && (
                                            <a
                                              href={attachedClip.link}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-pink-400 hover:text-pink-300 text-xs underline ml-auto"
                                            >
                                              Watch
                                            </a>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                              {q.category && (
                                <span className="text-zinc-500 text-xs">Topic: {q.category}</span>
                              )}
                              {q.answer && (
                                <div className="mt-1 pt-1 border-t border-purple-500/20">
                                  <p className="text-lime-400 text-xs">A: {q.answer}</p>
                                  {q.answeredBy && (
                                    <span className="text-zinc-500 text-xs">— {q.answeredBy}</span>
                                  )}
                                </div>
                              )}

                              {/* Action buttons - Reply and Edit */}
                              <div className="flex items-center gap-2 mt-1">
                                <button
                                  onClick={() => {
                                    setReplyingToCoupleQuestionId(q.id);
                                    setNewCoupleQuestionReply('');
                                    setReplyClipIds([]);
                                  }}
                                  className="text-purple-400 hover:text-purple-300 text-xs flex items-center gap-1"
                                >
                                  <MessageSquare className="w-3 h-3" /> Reply
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingQuestionId(q.id);
                                    setEditQuestionText(q.question);
                                    setEditQuestionClipIds(q.clipIds || (q.clipId ? [q.clipId] : []));
                                  }}
                                  className="text-amber-400 hover:text-amber-300 text-xs flex items-center gap-1"
                                >
                                  <Edit2 className="w-3 h-3" /> Edit
                                </button>
                              </div>

                              {/* Edit mode */}
                              {editingQuestionId === q.id && (
                                <div className="mt-2 space-y-2 bg-amber-500/10 border border-amber-500/30 rounded p-2">
                                  <textarea
                                    value={editQuestionText}
                                    onChange={e => setEditQuestionText(e.target.value)}
                                    className="w-full bg-zinc-700 border border-amber-500/30 rounded px-2 py-1 text-white text-xs h-16 resize-none"
                                    autoFocus
                                  />
                                  {/* Selected clips preview */}
                                  {editQuestionClipIds.length > 0 && (
                                    <div className="space-y-1">
                                      {editQuestionClipIds.map(clipId => {
                                        const clip = dateNiteClips.find(c => c.id === clipId);
                                        return clip ? (
                                          <div key={clipId} className="flex items-center gap-2 bg-pink-500/10 border border-pink-500/30 rounded px-2 py-1">
                                            <Film className="w-3 h-3 text-pink-400" />
                                            <span className="text-pink-300 text-xs flex-1 truncate">{clip.title}</span>
                                            <button onClick={() => setEditQuestionClipIds(prev => prev.filter(id => id !== clipId))} className="text-pink-400 hover:text-pink-300">
                                              <X className="w-3 h-3" />
                                            </button>
                                          </div>
                                        ) : null;
                                      })}
                                    </div>
                                  )}
                                  {/* Clip picker for edit */}
                                  <button
                                    onClick={() => setShowReplyClipPicker(!showReplyClipPicker)}
                                    className="px-2 py-1 bg-zinc-600 text-zinc-400 hover:bg-zinc-500 rounded text-xs flex items-center gap-1"
                                  >
                                    <Film className="w-3 h-3" /> Add Clip
                                  </button>
                                  {showReplyClipPicker && (
                                    <div className="bg-zinc-800 border border-zinc-600 rounded-lg p-2 max-h-32 overflow-y-auto">
                                      <input
                                        type="text"
                                        value={replyClipSearch}
                                        onChange={e => setReplyClipSearch(e.target.value)}
                                        placeholder="Search clips..."
                                        className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-xs mb-2"
                                      />
                                      <div className="space-y-1">
                                        {dateNiteClips
                                          .filter(c => !replyClipSearch || c.title.toLowerCase().includes(replyClipSearch.toLowerCase()))
                                          .filter(c => !editQuestionClipIds.includes(c.id))
                                          .slice(0, 8)
                                          .map(clip => (
                                            <button
                                              key={clip.id}
                                              onClick={() => {
                                                setEditQuestionClipIds(prev => [...prev, clip.id]);
                                                setReplyClipSearch('');
                                              }}
                                              className="w-full text-left px-2 py-1 rounded text-xs hover:bg-zinc-700 flex items-center gap-2"
                                            >
                                              <span>{CATEGORIES.find(cat => cat.id === clip.categoryId)?.emoji}</span>
                                              <span className="text-white truncate">{clip.title}</span>
                                            </button>
                                          ))}
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => saveQuestionEdit(q.id)}
                                      disabled={!editQuestionText.trim()}
                                      className="flex-1 px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs hover:bg-amber-500/30 disabled:opacity-50"
                                    >
                                      Save Edit
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingQuestionId(null);
                                        setEditQuestionText('');
                                        setEditQuestionClipIds([]);
                                        setShowReplyClipPicker(false);
                                      }}
                                      className="px-2 py-1 bg-zinc-700 text-zinc-400 rounded text-xs hover:bg-zinc-600"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Reply input */}
                              {replyingToCoupleQuestionId === q.id && (
                                <div className="mt-2 space-y-2">
                                  <div className="flex gap-2">
                                    <textarea
                                      value={newCoupleQuestionReply}
                                      onChange={e => setNewCoupleQuestionReply(e.target.value)}
                                      placeholder="Type your follow-up question..."
                                      className="flex-1 bg-zinc-700 border border-purple-500/30 rounded px-2 py-1 text-white placeholder-zinc-500 text-xs h-16 resize-none"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() => setShowReplyClipPicker(!showReplyClipPicker)}
                                      className={`px-2 h-16 rounded text-xs ${replyClipIds.length > 0 ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30' : 'bg-zinc-600 text-zinc-400 hover:bg-zinc-500'}`}
                                      title="Attach clips"
                                    >
                                      <Film className="w-4 h-4" />
                                      {replyClipIds.length > 0 && <span className="ml-1">{replyClipIds.length}</span>}
                                    </button>
                                  </div>
                                  {/* Selected clips preview */}
                                  {replyClipIds.length > 0 && (
                                    <div className="space-y-1">
                                      {replyClipIds.map(clipId => {
                                        const clip = dateNiteClips.find(c => c.id === clipId);
                                        return clip ? (
                                          <div key={clipId} className="flex items-center gap-2 bg-pink-500/10 border border-pink-500/30 rounded px-2 py-1">
                                            <Film className="w-3 h-3 text-pink-400" />
                                            <span className="text-pink-300 text-xs flex-1 truncate">{clip.title}</span>
                                            <button onClick={() => setReplyClipIds(prev => prev.filter(id => id !== clipId))} className="text-pink-400 hover:text-pink-300">
                                              <X className="w-3 h-3" />
                                            </button>
                                          </div>
                                        ) : null;
                                      })}
                                    </div>
                                  )}
                                  {/* Clip picker dropdown */}
                                  {showReplyClipPicker && (
                                    <div className="bg-zinc-800 border border-zinc-600 rounded-lg p-2 max-h-48 overflow-y-auto">
                                      <input
                                        type="text"
                                        value={replyClipSearch}
                                        onChange={e => setReplyClipSearch(e.target.value)}
                                        placeholder="Search clips..."
                                        className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-xs mb-2"
                                      />
                                      <div className="space-y-1">
                                        {dateNiteClips
                                          .filter(c => !replyClipSearch || c.title.toLowerCase().includes(replyClipSearch.toLowerCase()))
                                          .filter(c => !replyClipIds.includes(c.id))
                                          .slice(0, 10)
                                          .map(clip => (
                                            <button
                                              key={clip.id}
                                              onClick={() => {
                                                setReplyClipIds(prev => [...prev, clip.id]);
                                                setReplyClipSearch('');
                                              }}
                                              className="w-full text-left px-2 py-1 rounded text-xs hover:bg-zinc-700 flex items-center gap-2"
                                            >
                                              <span>{CATEGORIES.find(cat => cat.id === clip.categoryId)?.emoji}</span>
                                              <span className="text-white truncate">{clip.title}</span>
                                            </button>
                                          ))}
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => sendCoupleQuestionReply(couple.coupleId, q.id, 'Admin')}
                                      disabled={!newCoupleQuestionReply.trim()}
                                      className="flex-1 px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs hover:bg-purple-500/30 disabled:opacity-50"
                                    >
                                      Send Reply {replyClipIds.length > 0 && `+ ${replyClipIds.length} Clip${replyClipIds.length > 1 ? 's' : ''}`}
                                    </button>
                                    <button
                                      onClick={() => {
                                        setReplyingToCoupleQuestionId(null);
                                        setReplyClipIds([]);
                                        setShowReplyClipPicker(false);
                                      }}
                                      className="px-2 py-1 bg-zinc-700 text-zinc-400 rounded text-xs hover:bg-zinc-600"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Render threaded replies */}
                              {getReplies(q.id).length > 0 && (
                                <div className="mt-2 space-y-2">
                                  {getReplies(q.id).map(reply => renderQuestion(reply, true))}
                                </div>
                              )}
                            </div>
                          );

                          return (
                            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg p-2 border border-purple-500/20">
                              <p className="text-zinc-400 text-xs mb-2 flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" /> Questions from Reviewers ({questionsForCouple.length})
                              </p>
                              <div className="space-y-2 max-h-60 overflow-y-auto">
                                {rootQuestions.map(q => renderQuestion(q))}
                              </div>
                            </div>
                          );
                        })()}

                        {/* AI Follow-up Questions */}
                        {(() => {
                          const followUpsForCouple = coupleFollowUps.filter(fu => fu.coupleId === couple.coupleId);
                          const pendingFollowUps = followUpsForCouple.filter(fu => fu.status === 'pending');

                          // Generate suggested follow-ups based on couple activity
                          const suggestedFollowUps: { label: string; question: string; context: string; clipId?: string }[] = [];

                          // ========== HIS PROFILE-BASED QUESTIONS ==========
                          const hp = couple.hisProfile;
                          if (hp) {
                            // Cum Intensity
                            if (hp.cumIntensity === 1 || hp.cumIntensity === 2) {
                              suggestedFollowUps.push({
                                label: '💧 Low volume tips',
                                question: `You noted his load volume is lower - a few easy things make a big difference: staying well hydrated (2-3L water daily), edging for 15-20 mins before finishing, and 3-4 days abstinence before a big session. Has he tried any of these? Load Boost supplements can help too if you want extra boost.`,
                                context: 'Based on his cum intensity rating'
                              });
                            }
                            if (hp.cumIntensity === 5) {
                              suggestedFollowUps.push({
                                label: '🌊 High volume',
                                question: `Impressive volume! Does he use any supplements or have a specific diet/routine? We'd love to know what works for him.`,
                                context: 'Based on his high cum intensity'
                              });
                            }

                            // Cum Consistency
                            if (hp.cumConsistency === 1 || hp.cumConsistency === 2) {
                              suggestedFollowUps.push({
                                label: '💦 Watery consistency',
                                question: `You mentioned his consistency is on the watery side - Load Boost can help with thicker, more satisfying loads. Has he tried supplements or dietary changes?`,
                                context: 'Based on consistency rating'
                              });
                            }
                            if (hp.cumConsistency === 5) {
                              suggestedFollowUps.push({
                                label: '🥛 Thick consistency',
                                question: `Nice thick consistency! Does she prefer it this way for specific activities (swallowing, facials, etc.)? What makes it work for you both?`,
                                context: 'Based on consistency rating'
                              });
                            }

                            // Cum Taste
                            if (hp.cumTaste && hp.cumTaste <= 2) {
                              suggestedFollowUps.push({
                                label: '😬 Taste improvement',
                                question: `You rated taste lower - pineapple juice, staying hydrated, and avoiding certain foods can help. Has he tried any dietary changes? Would taste improvement make oral more enjoyable for her?`,
                                context: 'Based on taste rating'
                              });
                            }
                            if (hp.cumTaste && hp.cumTaste >= 4) {
                              suggestedFollowUps.push({
                                label: '😋 Good taste',
                                question: `Great taste rating! Does he follow a specific diet or routine? This could help other couples - what's his secret?`,
                                context: 'Based on taste rating'
                              });
                            }

                            // Precum
                            if (hp.precumProduction === 1) {
                              suggestedFollowUps.push({
                                label: '🚫 No precum',
                                question: `You noted he doesn't produce much precum - is this something you'd like to change? Some supplements and hydration can help increase it.`,
                                context: 'Based on precum rating'
                              });
                            }
                            if (hp.precumProduction && hp.precumProduction >= 4) {
                              suggestedFollowUps.push({
                                label: '💧 Lots of precum',
                                question: `He produces a lot of precum - does she enjoy this during foreplay/oral? How do you incorporate it into your activities?`,
                                context: 'Based on precum rating'
                              });
                            }

                            // Size-related (sensitive but relevant)
                            if (hp.size === 1 || hp.size === 2) {
                              suggestedFollowUps.push({
                                label: '📏 Size advantages',
                                question: `Smaller sizes have real advantages - easier deep throat, more positions, less discomfort. Can she deep throat easily? What activities work especially well for you both?`,
                                context: 'Based on his profile'
                              });
                            }
                            if (hp.size === 3 || hp.size === 4) {
                              suggestedFollowUps.push({
                                label: '📏 Deep throat?',
                                question: `With his size, can she comfortably deep throat? Is that something you enjoy together or working on? Any tips that have helped?`,
                                context: 'Based on his profile'
                              });
                            }
                            if (hp.size && hp.size >= 5) {
                              suggestedFollowUps.push({
                                label: '📏 Larger size',
                                question: `With his size, deep throat might be challenging - is that something she can do or working towards? What positions/activities work best and which do you avoid?`,
                                context: 'Based on his profile'
                              });
                            }

                            // Girth-related - video matching
                            if (hp.girth && hp.girth >= 4) {
                              suggestedFollowUps.push({
                                label: '🎬 Video size match',
                                question: `Is there a video in our library where the guy looks like his size/girth? We want to recommend clips that feel relatable - seeing similar proportions can make the fantasy feel more real and achievable!`,
                                context: 'Based on his girth'
                              });
                            }
                            if (hp.size && hp.size >= 5) {
                              suggestedFollowUps.push({
                                label: '🎬 Video size match',
                                question: `Is there a video where the guy looks similar in size to him? Finding clips with relatable proportions makes them more fun - let us know if any match up!`,
                                context: 'Based on his size'
                              });
                            }

                            // Visual sharing
                            if (hp.visualSharingOpen === 'yes' && !hp.visualUrls?.cumUrl && !hp.visualUrls?.sizeUrl) {
                              suggestedFollowUps.push({
                                label: '📸 Visual sharing',
                                question: `You indicated openness to sharing visuals - would you like to share any verification content? It helps us understand your profile better and match recommendations.`,
                                context: 'Based on visual sharing preference'
                              });
                            }
                            if (hp.visualSharingOpen === 'maybe') {
                              suggestedFollowUps.push({
                                label: '🤔 Maybe share visuals?',
                                question: `You said "maybe" to visual sharing - what would make you more comfortable? Anonymous sharing? Specific types only? We respect all boundaries.`,
                                context: 'Based on visual sharing preference'
                              });
                            }
                          }

                          // ========== CATEGORY PREFERENCE QUESTIONS ==========
                          if (couple.preferences) {
                            Object.entries(couple.preferences).forEach(([catId, pref]) => {
                              const cat = CATEGORIES.find(c => c.id === catId);
                              if (!cat) return;

                              if (pref === 'love') {
                                suggestedFollowUps.push({
                                  label: `💕 Love ${cat.emoji}`,
                                  question: `You love ${cat.name} - what specifically appeals to you? The visual aspect, the sensation, the intimacy? This helps us find the perfect clips.`,
                                  context: `Based on "${cat.name}" preference`
                                });
                              }
                              if (pref === 'curious') {
                                suggestedFollowUps.push({
                                  label: `🤔 Curious: ${cat.emoji}`,
                                  question: `You're curious about ${cat.name} - have you tried anything similar? What draws you to it? What would help you feel ready to explore?`,
                                  context: `Based on "${cat.name}" curiosity`
                                });
                              }
                              if (pref === 'nope') {
                                suggestedFollowUps.push({
                                  label: `🚫 Nope: ${cat.emoji}`,
                                  question: `${cat.name} is marked as "nope" - is this a hard boundary or just not appealing right now? Understanding helps us avoid recommending similar content.`,
                                  context: `Based on "${cat.name}" preference`
                                });
                              }
                            });
                          }

                          // ========== COUPLE FEEDBACK TRIGGERS ==========
                          if (couple.wouldSubmitClips === 'yes' && !couple.generalFeedback?.includes('submit')) {
                            suggestedFollowUps.push({
                              label: '📹 Would submit clips',
                              question: `You said you'd be open to submitting your own clips - amazing! What type of content would you create? What would make you feel comfortable doing so?`,
                              context: 'Based on submission interest'
                            });
                          }
                          if (couple.wouldSubmitClips === 'maybe') {
                            suggestedFollowUps.push({
                              label: '🎬 Maybe submit?',
                              question: `You said "maybe" to submitting clips - what's holding you back? Privacy concerns? Not sure what to create? We'd love to help make it comfortable.`,
                              context: 'Based on submission interest'
                            });
                          }

                          if (couple.productExcitement && couple.productExcitement <= 2) {
                            suggestedFollowUps.push({
                              label: '😐 Low excitement',
                              question: `Your product excitement rating was lower - what would make this more exciting for you? Different content? Different features? We want to improve!`,
                              context: 'Based on excitement rating'
                            });
                          }
                          if (couple.productExcitement && couple.productExcitement >= 4) {
                            suggestedFollowUps.push({
                              label: '🎉 High excitement',
                              question: `You're excited about the product! What features do you love most? What would make it even better? Your feedback helps us prioritize.`,
                              context: 'Based on excitement rating'
                            });
                          }

                          // ========== TRIED CLIPS - COMPREHENSIVE ==========
                          if (couple.tried && couple.tried.length > 0) {
                            const recentTried = (couple.tried || []).slice(-5);
                            recentTried.forEach(tried => {
                              const clip = dateNiteClips.find(c => c.id === tried.clipId);
                              if (!clip) return;

                              // Hot rating variations
                              if (tried.rating === 'hot') {
                                if (!tried.wouldDoAgain) {
                                  suggestedFollowUps.push({
                                    label: `🔥 Loved "${clip.title.slice(0, 15)}..."`,
                                    question: `You really enjoyed "${clip.title}" - would you want to try it again? What made it so good for you both?`,
                                    context: 'Based on 🔥🔥🔥 rating',
                                    clipId: clip.id
                                  });
                                }
                                if (tried.wouldDoAgain === 'yes' && !tried.inspiresDifferent) {
                                  suggestedFollowUps.push({
                                    label: `🔁 Do again "${clip.title.slice(0, 12)}..."`,
                                    question: `You'd do "${clip.title}" again - did it inspire any variations? Would you try it with different elements next time?`,
                                    context: 'Based on wanting to repeat',
                                    clipId: clip.id
                                  });
                                }
                              }

                              // Good rating
                              if (tried.rating === 'good') {
                                suggestedFollowUps.push({
                                  label: `👍 Good: "${clip.title.slice(0, 15)}..."`,
                                  question: `"${clip.title}" was good but not amazing - what would have made it 🔥🔥🔥? Different pacing, scenario, or technique?`,
                                  context: 'Based on good rating',
                                  clipId: clip.id
                                });
                              }

                              // Meh rating
                              if (tried.rating === 'meh') {
                                suggestedFollowUps.push({
                                  label: `😐 Meh: "${clip.title.slice(0, 15)}..."`,
                                  question: `"${clip.title}" was just meh - was it the content type, the execution, or something else? This helps us recommend better.`,
                                  context: 'Based on meh rating',
                                  clipId: clip.id
                                });
                              }

                              // Nope rating
                              if (tried.rating === 'nope') {
                                suggestedFollowUps.push({
                                  label: `👎 Nope: "${clip.title.slice(0, 15)}..."`,
                                  question: `"${clip.title}" didn't work for you - was it a turn-off or just not your thing? Should we avoid similar content?`,
                                  context: 'Based on nope rating',
                                  clipId: clip.id
                                });
                              }

                              // Inspired different but no details
                              if (tried.inspiresDifferent === 'yes' && !tried.howDifferent) {
                                suggestedFollowUps.push({
                                  label: `💡 Inspired by "${clip.title.slice(0, 12)}..."`,
                                  question: `"${clip.title}" inspired you to try something different - what did you end up doing? How did it go?`,
                                  context: 'Based on inspiration',
                                  clipId: clip.id
                                });
                              }

                              // Has detailed thoughts - dig deeper
                              if (tried.hisThoughts && tried.hisThoughts.length > 20 && !tried.herThoughts) {
                                suggestedFollowUps.push({
                                  label: `👩 Her thoughts on "${clip.title.slice(0, 10)}..."`,
                                  question: `He shared thoughts on "${clip.title}" - what about her perspective? Did she enjoy it the same way?`,
                                  context: 'Want her perspective',
                                  clipId: clip.id
                                });
                              }
                              if (tried.herThoughts && tried.herThoughts.length > 20 && !tried.hisThoughts) {
                                suggestedFollowUps.push({
                                  label: `👨 His thoughts on "${clip.title.slice(0, 10)}..."`,
                                  question: `She shared thoughts on "${clip.title}" - what about his perspective? Did he enjoy it the same way?`,
                                  context: 'Want his perspective',
                                  clipId: clip.id
                                });
                              }
                            });
                          }

                          // ========== FAVORITES ==========
                          if (couple.favorites && couple.favorites.length > 0) {
                            // Recent favorite
                            const recentFav = (couple.favorites || [])[(couple.favorites || []).length - 1];
                            const clip = dateNiteClips.find(c => c.id === recentFav);
                            if (clip) {
                              suggestedFollowUps.push({
                                label: `❤️ Fav: "${clip.title.slice(0, 18)}..."`,
                                question: `You favorited "${clip.title}" - what specifically drew you to it? The scenario, the chemistry, or something else?`,
                                context: 'Based on recent favorite',
                                clipId: clip.id
                              });
                            }

                            // Multiple favorites in same category
                            const favClips = (couple.favorites || []).map(fid => dateNiteClips.find(c => c.id === fid)).filter(Boolean);
                            const catCounts: Record<string, number> = {};
                            favClips.forEach(fc => {
                              if (fc) catCounts[fc.categoryId] = (catCounts[fc.categoryId] || 0) + 1;
                            });
                            Object.entries(catCounts).forEach(([catId, count]) => {
                              if (count >= 2) {
                                const cat = CATEGORIES.find(c => c.id === catId);
                                if (cat) {
                                  suggestedFollowUps.push({
                                    label: `📂 ${count}x ${cat.emoji} favorites`,
                                    question: `You've favorited ${count} clips in ${cat.name} - this is clearly your thing! What specifically about ${cat.name} appeals to you both?`,
                                    context: `Based on multiple ${cat.name} favorites`
                                  });
                                }
                              }
                            });
                          }

                          // ========== ENGAGEMENT GAPS ==========
                          if (!couple.tried || couple.tried.length === 0) {
                            suggestedFollowUps.push({
                              label: '🆕 Haven\'t tried yet',
                              question: `You haven't marked any clips as "tried" yet - have you recreated any activities from the clips? We'd love to hear how it went!`,
                              context: 'No tried activities'
                            });
                          }
                          if (!couple.favorites || couple.favorites.length === 0) {
                            suggestedFollowUps.push({
                              label: '⭐ No favorites',
                              question: `You haven't favorited any clips yet - are there any that caught your eye? Favorites help us learn what you like!`,
                              context: 'No favorites yet'
                            });
                          }
                          if (!couple.preferences || Object.keys(couple.preferences).length < 3) {
                            suggestedFollowUps.push({
                              label: '🎯 Set preferences',
                              question: `You haven't set many category preferences - knowing what you love, like, or want to avoid helps us recommend perfect clips. Want to explore categories?`,
                              context: 'Few preferences set'
                            });
                          }

                          // Filter out already-sent questions (check both coupleFollowUps AND coupleQuestions)
                          const sentFollowUpTexts = followUpsForCouple.map(fu => fu.question.toLowerCase());
                          const questionsForThisCouple = coupleQuestions.filter(q => q.coupleId === couple.coupleId);
                          const sentQuestionTexts = questionsForThisCouple.map(q => q.question.toLowerCase());
                          const allSentTexts = [...sentFollowUpTexts, ...sentQuestionTexts];

                          const availableFollowUps = suggestedFollowUps.filter(s => {
                            const questionLower = s.question.toLowerCase();
                            // Check if any sent question is similar (first 40 chars or key phrases)
                            const keyPhrase = questionLower.slice(0, 40);
                            const isAlreadySent = allSentTexts.some(sent =>
                              sent.includes(keyPhrase) ||
                              keyPhrase.includes(sent.slice(0, 40)) ||
                              // Also check for semantic similarity on key topics
                              (questionLower.includes('deep throat') && sent.includes('deep throat')) ||
                              (questionLower.includes('load volume') && sent.includes('load volume')) ||
                              (questionLower.includes('consistency') && sent.includes('consistency')) ||
                              (questionLower.includes('precum') && sent.includes('precum')) ||
                              (questionLower.includes('visual') && sent.includes('visual')) ||
                              (questionLower.includes('excitement') && sent.includes('excitement')) ||
                              (questionLower.includes('haven\'t tried') && sent.includes('haven\'t tried')) ||
                              (questionLower.includes('favorited') && sent.includes('favorited')) ||
                              // Additional semantic checks
                              (questionLower.includes('video') && questionLower.includes('size') && sent.includes('video') && sent.includes('size')) ||
                              (questionLower.includes('girth') && sent.includes('girth')) ||
                              (questionLower.includes('lube') && sent.includes('lube')) ||
                              (questionLower.includes('taste') && sent.includes('taste')) ||
                              (questionLower.includes('swallow') && sent.includes('swallow')) ||
                              (questionLower.includes('wetness') && sent.includes('wetness')) ||
                              (questionLower.includes('squirt') && sent.includes('squirt')) ||
                              (questionLower.includes('sensitivity') && sent.includes('sensitivity')) ||
                              (questionLower.includes('orgasm') && sent.includes('orgasm')) ||
                              (questionLower.includes('submit') && questionLower.includes('clip') && sent.includes('submit')) ||
                              (questionLower.includes('preferences') && sent.includes('preferences'))
                            );
                            return !isAlreadySent;
                          });

                          return (
                            <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-lg p-2 border border-amber-500/20">
                              <p className="text-zinc-400 text-xs mb-2 flex items-center gap-1">
                                🤖 AI Follow-up Questions
                                {pendingFollowUps.length > 0 && (
                                  <span className="px-1.5 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400">
                                    {pendingFollowUps.length} pending
                                  </span>
                                )}
                              </p>

                              {/* Categorized quick send buttons */}
                              {availableFollowUps.length > 0 && saveCoupleFollowUps && (
                                <div className="space-y-2 mb-2">
                                  <p className="text-amber-400 text-xs flex items-center gap-2">
                                    Quick send
                                    <span className="px-1.5 py-0.5 bg-amber-500/20 rounded text-xs">
                                      {availableFollowUps.length} available
                                    </span>
                                  </p>

                                  {/* Group by context type */}
                                  {(() => {
                                    const profileQs = availableFollowUps.filter(f =>
                                      f.context.includes('profile') || f.context.includes('rating') || f.context.includes('intensity') ||
                                      f.context.includes('consistency') || f.context.includes('taste') || f.context.includes('precum') ||
                                      f.context.includes('visual')
                                    );
                                    const prefQs = availableFollowUps.filter(f =>
                                      f.context.includes('preference') || f.context.includes('curiosity')
                                    );
                                    const clipQs = availableFollowUps.filter(f => f.clipId);
                                    const feedbackQs = availableFollowUps.filter(f =>
                                      f.context.includes('submission') || f.context.includes('excitement')
                                    );
                                    const engagementQs = availableFollowUps.filter(f =>
                                      f.context.includes('No ') || f.context.includes('Few ')
                                    );

                                    return (
                                      <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {/* His Profile Questions */}
                                        {profileQs.length > 0 && (
                                          <div>
                                            <p className="text-blue-400 text-xs mb-1">🍆 His Profile ({profileQs.length})</p>
                                            <div className="flex flex-wrap gap-1">
                                              {profileQs.slice(0, 6).map((fu, i) => (
                                                <button
                                                  key={`profile-${i}`}
                                                  onClick={() => queueCoupleFollowUp(couple.coupleId, `${couple.hisName} & ${couple.herName}`, fu.question, fu.context, fu.clipId)}
                                                  className="px-2 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded text-xs flex items-center gap-1"
                                                  title={fu.question}
                                                >
                                                  <Send className="w-3 h-3" /> {fu.label.slice(0, 20)}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* Category Preferences */}
                                        {prefQs.length > 0 && (
                                          <div>
                                            <p className="text-purple-400 text-xs mb-1">🎯 Preferences ({prefQs.length})</p>
                                            <div className="flex flex-wrap gap-1">
                                              {prefQs.slice(0, 6).map((fu, i) => (
                                                <button
                                                  key={`pref-${i}`}
                                                  onClick={() => queueCoupleFollowUp(couple.coupleId, `${couple.hisName} & ${couple.herName}`, fu.question, fu.context, fu.clipId)}
                                                  className="px-2 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded text-xs flex items-center gap-1"
                                                  title={fu.question}
                                                >
                                                  <Send className="w-3 h-3" /> {fu.label.slice(0, 20)}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* Clip-based Questions */}
                                        {clipQs.length > 0 && (
                                          <div>
                                            <p className="text-green-400 text-xs mb-1">🎬 Clips ({clipQs.length})</p>
                                            <div className="flex flex-wrap gap-1">
                                              {clipQs.slice(0, 6).map((fu, i) => (
                                                <button
                                                  key={`clip-${i}`}
                                                  onClick={() => queueCoupleFollowUp(couple.coupleId, `${couple.hisName} & ${couple.herName}`, fu.question, fu.context, fu.clipId)}
                                                  className="px-2 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded text-xs flex items-center gap-1"
                                                  title={fu.question}
                                                >
                                                  <Send className="w-3 h-3" /> {fu.label.slice(0, 20)}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* Feedback Questions */}
                                        {feedbackQs.length > 0 && (
                                          <div>
                                            <p className="text-pink-400 text-xs mb-1">💬 Feedback ({feedbackQs.length})</p>
                                            <div className="flex flex-wrap gap-1">
                                              {feedbackQs.map((fu, i) => (
                                                <button
                                                  key={`feedback-${i}`}
                                                  onClick={() => queueCoupleFollowUp(couple.coupleId, `${couple.hisName} & ${couple.herName}`, fu.question, fu.context, fu.clipId)}
                                                  className="px-2 py-1 bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 rounded text-xs flex items-center gap-1"
                                                  title={fu.question}
                                                >
                                                  <Send className="w-3 h-3" /> {fu.label.slice(0, 20)}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* Engagement Prompts */}
                                        {engagementQs.length > 0 && (
                                          <div>
                                            <p className="text-amber-400 text-xs mb-1">📊 Engagement ({engagementQs.length})</p>
                                            <div className="flex flex-wrap gap-1">
                                              {engagementQs.map((fu, i) => (
                                                <button
                                                  key={`engage-${i}`}
                                                  onClick={() => queueCoupleFollowUp(couple.coupleId, `${couple.hisName} & ${couple.herName}`, fu.question, fu.context, fu.clipId)}
                                                  className="px-2 py-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded text-xs flex items-center gap-1"
                                                  title={fu.question}
                                                >
                                                  <Send className="w-3 h-3" /> {fu.label.slice(0, 20)}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}

                              {/* Research Questions - Same templates as reviewers */}
                              {(() => {
                                const alreadySent = followUpsForCouple.map(fu => fu.question);
                                const activeQuestions = researchQuestions.filter(rq => rq.active);
                                // Filter out already asked questions
                                const availableResearch = activeQuestions.filter(rq =>
                                  !alreadySent.some(sent => sent.includes(rq.question.slice(0, 50)))
                                );

                                if (availableResearch.length === 0) return null;

                                return (
                                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 mb-2">
                                    <h4 className="text-purple-400 font-semibold text-xs flex items-center gap-2 mb-2">
                                      🤖 Research Questions
                                      <span className="text-purple-400/60 text-xs font-normal">Universal feedback prompts</span>
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                      {availableResearch.map(rq => (
                                        <button
                                          key={rq.id}
                                          onClick={() => queueCoupleFollowUp(couple.coupleId, `${couple.hisName} & ${couple.herName}`, rq.question, rq.context || rq.label)}
                                          className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-full text-xs transition-colors flex items-center gap-1"
                                          title={rq.question}
                                        >
                                          {rq.label}
                                          <Send className="w-3 h-3 opacity-60" />
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Quick Actions */}
                              {saveCoupleFollowUps && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  <button
                                    onClick={() => queueCoupleFollowUp(
                                      couple.coupleId,
                                      `${couple.hisName} & ${couple.herName}`,
                                      `Great news! Your clips have been getting amazing feedback from reviewers who'd love to see more from you two. They're especially interested in more couple content and some solo clips from ${couple.hisName}. If you have more to share, just paste the links below — RedGifs or Google Drive links both work!`,
                                      'Reviewer clip request'
                                    )}
                                    className="px-2 py-1 bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 rounded text-xs flex items-center gap-1"
                                    title="Ask couple to share more clips"
                                  >
                                    <Film className="w-3 h-3" /> Request More Clips
                                  </button>
                                </div>
                              )}

                              {/* Custom question input */}
                              {saveCoupleFollowUps && (
                                <div className="flex gap-1 mb-2">
                                  <input
                                    type="text"
                                    placeholder="Custom question..."
                                    className="flex-1 bg-zinc-800 border border-zinc-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-amber-500"
                                    id={`couple-followup-custom-${couple.coupleId}`}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        const input = e.target as HTMLInputElement;
                                        const question = input.value.trim();
                                        if (!question) return;
                                        queueCoupleFollowUp(couple.coupleId, `${couple.hisName} & ${couple.herName}`, question, 'Custom question from admin');
                                        input.value = '';
                                      }
                                    }}
                                  />
                                  <button
                                    onClick={() => {
                                      const input = document.getElementById(`couple-followup-custom-${couple.coupleId}`) as HTMLInputElement;
                                      const question = input?.value?.trim();
                                      if (!question) return;
                                      queueCoupleFollowUp(couple.coupleId, `${couple.hisName} & ${couple.herName}`, question, 'Custom question from admin');
                                      input.value = '';
                                    }}
                                    className="px-2 py-1 bg-amber-500 hover:bg-amber-400 text-zinc-900 rounded text-xs font-medium"
                                  >
                                    Send
                                  </button>
                                </div>
                              )}

                              {/* Show existing follow-ups with threading */}
                              {followUpsForCouple.length > 0 && (() => {
                                // Separate root questions from replies for threaded display
                                const rootFollowUps = followUpsForCouple.filter(fu => !fu.parentId);
                                const getReplies = (parentId: string) => followUpsForCouple.filter(fu => fu.parentId === parentId);

                                const renderCoupleQuestion = (fu: typeof followUpsForCouple[0], isReply = false): React.ReactNode => (
                                  <div key={fu.id} className={`bg-zinc-800/50 rounded p-2 ${isReply ? 'ml-3 border-l-2 border-amber-500/30' : ''}`}>
                                    <div className="flex justify-between text-xs mb-1">
                                      <span className="text-amber-400">{fu.context || 'Question'}</span>
                                      <div className="flex items-center gap-1">
                                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                                          fu.status === 'answered' ? 'bg-green-500/20 text-green-400' :
                                          fu.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                                          'bg-zinc-500/20 text-zinc-400'
                                        }`}>
                                          {fu.status}
                                        </span>
                                        {deleteConfirm?.id === fu.id ? (
                                          <div className="flex items-center gap-1">
                                            <button
                                              onClick={() => deleteCoupleFollowUp(fu.id)}
                                              className="px-1.5 py-0.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs rounded transition-colors"
                                            >
                                              Yes
                                            </button>
                                            <button
                                              onClick={() => setDeleteConfirm(null)}
                                              className="px-1.5 py-0.5 bg-zinc-600 hover:bg-zinc-500 text-zinc-300 text-xs rounded transition-colors"
                                            >
                                              No
                                            </button>
                                          </div>
                                        ) : (
                                          <button
                                            onClick={() => setDeleteConfirm({ id: fu.id, type: 'couple' })}
                                            className="p-0.5 text-red-400/50 hover:text-red-400 hover:bg-red-500/20 rounded transition-colors"
                                            title="Delete question"
                                          >
                                            <X className="w-3 h-3" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <p className="text-white text-xs">Q: {fu.question}</p>
                                    {/* Show attached clips if exist */}
                                    {(() => {
                                      const clipIdsToShow = fu.clipIds || (fu.clipId ? [fu.clipId] : []);
                                      if (clipIdsToShow.length === 0) return null;
                                      return (
                                        <div className="mt-1 space-y-1">
                                          {clipIdsToShow.map(clipId => {
                                            const dnClip = dateNiteClips.find(c => c.id === clipId);
                                            const subClip = !dnClip ? clips.find(s => s.id === clipId) : null;
                                            const attachedClip = dnClip
                                              ? { title: dnClip.title, link: dnClip.link }
                                              : subClip
                                              ? { title: subClip.clipTitle, link: subClip.contentLink }
                                              : null;
                                            if (!attachedClip) return null;
                                            return (
                                              <button
                                                key={clipId}
                                                onClick={() => setPreviewClip({ title: attachedClip.title, link: attachedClip.link })}
                                                className="w-full flex items-center gap-2 bg-pink-500/10 border border-pink-500/30 rounded px-2 py-1 hover:bg-pink-500/20 transition-colors text-left"
                                              >
                                                <Film className="w-3 h-3 text-pink-400" />
                                                <span className="text-pink-300 text-xs flex-1">{attachedClip.title}</span>
                                                <Play className="w-3 h-3 text-pink-400 ml-auto" />
                                              </button>
                                            );
                                          })}
                                        </div>
                                      );
                                    })()}
                                    {/* Answer display */}
                                    {fu.answer && (
                                      <div className="mt-1 pt-1 border-t border-amber-500/20">
                                        <p className="text-lime-400 text-xs">A: {fu.answer}</p>
                                        {fu.answeredBy && (
                                          <span className="text-zinc-500 text-xs">— {fu.answeredBy}</span>
                                        )}
                                      </div>
                                    )}
                                    {/* Shared links from couple */}
                                    {fu.sharedLinks && fu.sharedLinks.length > 0 && (
                                      <div className="mt-1 pt-1 border-t border-lime-500/20 space-y-1">
                                        <p className="text-lime-400 text-xs font-medium">Shared links:</p>
                                        {fu.sharedLinks.map((link, i) => (
                                          <a
                                            key={i}
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-1 text-xs text-pink-400 hover:text-pink-300 transition-colors"
                                          >
                                            <Film className="w-3 h-3" />
                                            {link.label || link.url}
                                            <Eye className="w-3 h-3 ml-auto opacity-50" />
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                    {/* Action buttons - always visible */}
                                    <div className="flex items-center gap-1 mt-1">
                                      <button
                                        onClick={() => {
                                          setReplyingToCoupleFollowUpId(fu.id);
                                          setNewCoupleFollowUpReply('');
                                        }}
                                        className="px-1.5 py-0.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs rounded flex items-center gap-1 transition-colors"
                                      >
                                        <MessageSquare className="w-2.5 h-2.5" /> Reply
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditingQuestionId(fu.id);
                                          setEditQuestionText(fu.question);
                                          setEditQuestionClipIds(fu.clipIds || (fu.clipId ? [fu.clipId] : []));
                                        }}
                                        className="px-1.5 py-0.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs rounded flex items-center gap-1 transition-colors"
                                      >
                                        <Edit2 className="w-2.5 h-2.5" /> Edit
                                      </button>
                                      {fu.answer && (
                                        <button
                                          onClick={() => {
                                            setCreatingClipSuggestion({ coupleId: couple.coupleId });
                                            setClipSuggestionContext(`Following up on your answer: "${fu.answer?.slice(0, 80)}${(fu.answer?.length || 0) > 80 ? '...' : ''}" - here are some clips we think you might enjoy:`);
                                            setClipSuggestionLinkedQuestion(fu.id);
                                          }}
                                          className="px-1.5 py-0.5 bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 text-xs rounded flex items-center gap-1 transition-colors"
                                        >
                                          <Film className="w-2.5 h-2.5" /> Send Clips
                                        </button>
                                      )}
                                      {clipSuggestions.filter(cs => cs.linkedQuestionId === fu.id).length > 0 && (
                                        <span className="px-1.5 py-0.5 bg-pink-500/10 text-pink-400 text-xs rounded flex items-center gap-1">
                                          <Film className="w-2.5 h-2.5" />
                                          {clipSuggestions.filter(cs => cs.linkedQuestionId === fu.id).reduce((sum, cs) => sum + (cs.clips || []).length, 0)} sent
                                          {clipSuggestions.filter(cs => cs.linkedQuestionId === fu.id).some(cs => cs.status === 'completed') && ' ✓'}
                                        </span>
                                      )}
                                    </div>

                                    {/* Edit mode for CoupleFollowUp */}
                                    {editingQuestionId === fu.id && (
                                      <div className="mt-2 space-y-2 bg-amber-500/10 border border-amber-500/30 rounded p-2">
                                        <textarea
                                          value={editQuestionText}
                                          onChange={e => setEditQuestionText(e.target.value)}
                                          className="w-full bg-zinc-700 border border-amber-500/30 rounded px-2 py-1 text-white text-xs h-16 resize-none"
                                          autoFocus
                                        />
                                        {/* Selected clips preview */}
                                        {editQuestionClipIds.length > 0 && (
                                          <div className="space-y-1">
                                            {editQuestionClipIds.map(clipId => {
                                              const clip = dateNiteClips.find(c => c.id === clipId);
                                              return clip ? (
                                                <div key={clipId} className="flex items-center gap-2 bg-pink-500/10 border border-pink-500/30 rounded px-2 py-1">
                                                  <Film className="w-3 h-3 text-pink-400" />
                                                  <span className="text-pink-300 text-xs flex-1 truncate">{clip.title}</span>
                                                  <button onClick={() => setEditQuestionClipIds(prev => prev.filter(id => id !== clipId))} className="text-pink-400 hover:text-pink-300">
                                                    <X className="w-3 h-3" />
                                                  </button>
                                                </div>
                                              ) : null;
                                            })}
                                          </div>
                                        )}
                                        {/* Clip picker for edit */}
                                        <button
                                          onClick={() => setShowReplyClipPicker(!showReplyClipPicker)}
                                          className="px-2 py-1 bg-zinc-600 text-zinc-400 hover:bg-zinc-500 rounded text-xs flex items-center gap-1"
                                        >
                                          <Film className="w-3 h-3" /> Add Clip
                                        </button>
                                        {showReplyClipPicker && (
                                          <div className="bg-zinc-800 border border-zinc-600 rounded-lg p-2 max-h-32 overflow-y-auto">
                                            <input
                                              type="text"
                                              value={replyClipSearch}
                                              onChange={e => setReplyClipSearch(e.target.value)}
                                              placeholder="Search clips..."
                                              className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-xs mb-2"
                                            />
                                            <div className="space-y-1">
                                              {dateNiteClips
                                                .filter(c => !replyClipSearch || c.title.toLowerCase().includes(replyClipSearch.toLowerCase()))
                                                .filter(c => !editQuestionClipIds.includes(c.id))
                                                .slice(0, 8)
                                                .map(clip => (
                                                  <button
                                                    key={clip.id}
                                                    onClick={() => {
                                                      setEditQuestionClipIds(prev => [...prev, clip.id]);
                                                      setReplyClipSearch('');
                                                    }}
                                                    className="w-full text-left px-2 py-1 rounded text-xs hover:bg-zinc-700 flex items-center gap-2"
                                                  >
                                                    <span>{CATEGORIES.find(cat => cat.id === clip.categoryId)?.emoji}</span>
                                                    <span className="text-white truncate">{clip.title}</span>
                                                  </button>
                                                ))}
                                            </div>
                                          </div>
                                        )}
                                        <div className="flex gap-2">
                                          <button
                                            onClick={() => saveFollowUpEdit(fu.id)}
                                            disabled={!editQuestionText.trim()}
                                            className="flex-1 px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs hover:bg-amber-500/30 disabled:opacity-50"
                                          >
                                            Save Edit
                                          </button>
                                          <button
                                            onClick={() => {
                                              setEditingQuestionId(null);
                                              setEditQuestionText('');
                                              setEditQuestionClipIds([]);
                                              setShowReplyClipPicker(false);
                                            }}
                                            className="px-2 py-1 bg-zinc-700 text-zinc-400 rounded text-xs hover:bg-zinc-600"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    {/* Reply input for CoupleFollowUp */}
                                    {replyingToCoupleFollowUpId === fu.id && (
                                      <div className="mt-2 space-y-2 bg-zinc-900/50 rounded p-2 border border-amber-500/20">
                                        <div className="flex gap-2">
                                          <textarea
                                            value={newCoupleFollowUpReply}
                                            onChange={e => setNewCoupleFollowUpReply(e.target.value)}
                                            placeholder="Type your reply or follow-up..."
                                            className="flex-1 bg-zinc-700 border border-amber-500/30 rounded px-2 py-1 text-white placeholder-zinc-500 text-xs h-16 resize-none"
                                            autoFocus
                                          />
                                          <button
                                            onClick={() => setShowReplyClipPicker(!showReplyClipPicker)}
                                            className={`px-2 h-16 rounded text-xs ${replyClipIds.length > 0 ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30' : 'bg-zinc-600 text-zinc-400 hover:bg-zinc-500'}`}
                                            title="Attach clips"
                                          >
                                            <Film className="w-4 h-4" />
                                            {replyClipIds.length > 0 && <span className="ml-1">{replyClipIds.length}</span>}
                                          </button>
                                        </div>
                                        {replyClipIds.length > 0 && (
                                          <div className="space-y-1">
                                            {replyClipIds.map(clipId => {
                                              const clip = dateNiteClips.find(c => c.id === clipId);
                                              return clip ? (
                                                <div key={clipId} className="flex items-center gap-2 bg-pink-500/10 border border-pink-500/30 rounded px-2 py-1">
                                                  <Film className="w-3 h-3 text-pink-400" />
                                                  <span className="text-pink-300 text-xs flex-1 truncate">{clip.title}</span>
                                                  <button onClick={() => setReplyClipIds(prev => prev.filter(id => id !== clipId))} className="text-pink-400 hover:text-pink-300">
                                                    <X className="w-3 h-3" />
                                                  </button>
                                                </div>
                                              ) : null;
                                            })}
                                          </div>
                                        )}
                                        {showReplyClipPicker && (
                                          <div className="bg-zinc-800 border border-zinc-600 rounded-lg p-2 max-h-48 overflow-y-auto">
                                            <input
                                              type="text"
                                              value={replyClipSearch}
                                              onChange={e => setReplyClipSearch(e.target.value)}
                                              placeholder="Search clips..."
                                              className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-xs mb-2"
                                            />
                                            <div className="space-y-1">
                                              {dateNiteClips
                                                .filter(c => !replyClipSearch || c.title.toLowerCase().includes(replyClipSearch.toLowerCase()))
                                                .filter(c => !replyClipIds.includes(c.id))
                                                .slice(0, 10)
                                                .map(clip => (
                                                  <button
                                                    key={clip.id}
                                                    onClick={() => {
                                                      setReplyClipIds(prev => [...prev, clip.id]);
                                                      setReplyClipSearch('');
                                                    }}
                                                    className="w-full text-left px-2 py-1 rounded text-xs hover:bg-zinc-700 flex items-center gap-2"
                                                  >
                                                    <span>{CATEGORIES.find(cat => cat.id === clip.categoryId)?.emoji}</span>
                                                    <span className="text-white truncate">{clip.title}</span>
                                                  </button>
                                                ))}
                                            </div>
                                          </div>
                                        )}
                                        <div className="flex gap-2">
                                          <button
                                            onClick={async () => {
                                              if (!saveCoupleFollowUps || !newCoupleFollowUpReply.trim()) return;
                                              const newFollowUp: CoupleFollowUp = {
                                                id: `cfu-${Date.now()}`,
                                                coupleId: couple.coupleId,
                                                question: newCoupleFollowUpReply.trim(),
                                                context: 'Admin reply',
                                                sentAt: now(),
                                                status: 'answered', // Admin replies are not pending questions
                                                answer: newCoupleFollowUpReply.trim(), // Treat as answered (it's a comment)
                                                reviewerResponderName: 'Admin', // Mark as admin response
                                                parentId: fu.id,
                                                ...(replyClipIds.length > 0 && { clipIds: replyClipIds })
                                              };
                                              await saveCoupleFollowUps([...coupleFollowUps, newFollowUp]);
                                              setNewCoupleFollowUpReply('');
                                              setReplyingToCoupleFollowUpId(null);
                                              setReplyClipIds([]);
                                              setShowReplyClipPicker(false);
                                            }}
                                            disabled={!newCoupleFollowUpReply.trim()}
                                            className="flex-1 px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs hover:bg-amber-500/30 disabled:opacity-50"
                                          >
                                            Send Reply {replyClipIds.length > 0 && `+ ${replyClipIds.length} Clip${replyClipIds.length > 1 ? 's' : ''}`}
                                          </button>
                                          <button
                                            onClick={() => {
                                              setReplyingToCoupleFollowUpId(null);
                                              setReplyClipIds([]);
                                              setShowReplyClipPicker(false);
                                            }}
                                            className="px-2 py-1 bg-zinc-700 text-zinc-400 rounded text-xs hover:bg-zinc-600"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    {/* Render threaded replies */}
                                    {getReplies(fu.id).length > 0 && (
                                      <div className="mt-2 space-y-1">
                                        {getReplies(fu.id).map(reply => renderCoupleQuestion(reply, true))}
                                      </div>
                                    )}
                                  </div>
                                );

                                return (
                                  <div className="space-y-1 max-h-48 overflow-y-auto">
                                    {rootFollowUps.slice().reverse().map(fu => renderCoupleQuestion(fu))}
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })()}

                        {/* Clip Suggestions - Curated clips for couple to rate */}
                        {(() => {
                          const coupleSuggestions = clipSuggestions.filter(cs => cs.coupleId === couple.coupleId);

                          return (
                            <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-3">
                              <h4 className="text-pink-400 font-semibold text-sm flex items-center gap-2 mb-2">
                                <Film className="w-4 h-4" /> Clip Suggestions
                                {coupleSuggestions.filter(cs => cs.status === 'pending').length > 0 && (
                                  <span className="bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded text-xs">
                                    {coupleSuggestions.filter(cs => cs.status === 'pending').length} pending
                                  </span>
                                )}
                              </h4>

                              {/* Existing clip suggestions */}
                              {coupleSuggestions.length > 0 && (
                                <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                                  {coupleSuggestions.map(cs => (
                                    <div key={cs.id} className={`rounded-lg p-2 ${
                                      cs.status === 'completed' ? 'bg-green-500/10 border border-green-500/30' :
                                      cs.status === 'partial' ? 'bg-amber-500/10 border border-amber-500/30' :
                                      'bg-zinc-800/50'
                                    }`}>
                                      <div className="flex justify-between text-xs mb-1">
                                        <span className="text-pink-400">{cs.context || 'Clip suggestions'}</span>
                                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                                          cs.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                          cs.status === 'partial' ? 'bg-amber-500/20 text-amber-400' :
                                          'bg-zinc-500/20 text-zinc-400'
                                        }`}>
                                          {cs.status} ({(cs.clips || []).filter(c => c.rating).length}/{(cs.clips || []).length})
                                        </span>
                                      </div>
                                      <div className="space-y-1">
                                        {(cs.clips || []).map((clip, idx) => (
                                          <div key={clip.id} className="flex items-start gap-2 bg-zinc-900/50 rounded p-1.5">
                                            <span className="text-zinc-500 text-xs w-4">{idx + 1}.</span>
                                            <div className="flex-1 min-w-0">
                                              <a href={clip.url} target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:text-pink-300 text-xs truncate block">
                                                {clip.title}
                                              </a>
                                              {clip.rating && (
                                                <div className="flex items-center gap-2 mt-0.5">
                                                  <div className="flex">
                                                    {[1,2,3,4,5].map(s => (
                                                      <Star key={s} className={`w-3 h-3 ${s <= clip.rating! ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'}`} />
                                                    ))}
                                                  </div>
                                                  {clip.interested !== undefined && (
                                                    <span className={`text-xs ${clip.interested ? 'text-lime-400' : 'text-zinc-500'}`}>
                                                      {clip.interested ? '✓ Interested' : '✗ Not interested'}
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                              {clip.comment && (
                                                <p className="text-zinc-400 text-xs mt-0.5 italic">"{clip.comment}"</p>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      <div className="flex items-center justify-between mt-1">
                                        <p className="text-zinc-500 text-xs">Sent: {new Date(cs.sentAt).toLocaleDateString()}</p>
                                        <div className="flex items-center gap-3">
                                          <button
                                            onClick={() => startEditingClipSuggestion(cs)}
                                            className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1"
                                          >
                                            <Edit2 className="w-3 h-3" /> Edit
                                          </button>
                                          <button
                                            onClick={async () => {
                                              if (!saveClipSuggestions) return;
                                              const updated = clipSuggestions.filter(s => s.id !== cs.id);
                                              await saveClipSuggestions(updated);
                                            }}
                                            className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1"
                                          >
                                            <Trash2 className="w-3 h-3" /> Delete
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Create new clip suggestion form */}
                              {creatingClipSuggestion?.coupleId === couple.coupleId ? (
                                <div className="space-y-2 bg-zinc-800/50 rounded-lg p-2">
                                  <textarea
                                    value={clipSuggestionContext}
                                    onChange={e => setClipSuggestionContext(e.target.value)}
                                    placeholder="Context (e.g., 'Based on your interest in MFM...')"
                                    className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1.5 text-white placeholder-zinc-500 text-xs h-12 resize-none"
                                  />

                                  {/* Added clips */}
                                  {clipSuggestionItems.length > 0 && (
                                    <div className="space-y-1">
                                      {clipSuggestionItems.map((item, idx) => (
                                        <div key={idx} className="flex items-center gap-2 bg-zinc-900/50 rounded p-1.5">
                                          <span className="text-pink-400 text-xs flex-1 truncate">{item.title}</span>
                                          <button
                                            onClick={() => removeClipFromSuggestion(idx)}
                                            className="text-red-400/50 hover:text-red-400"
                                          >
                                            <X className="w-3 h-3" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Add new clip */}
                                  <div className="flex gap-1">
                                    <input
                                      type="text"
                                      value={newClipUrl}
                                      onChange={e => setNewClipUrl(e.target.value)}
                                      placeholder="Redgif URL..."
                                      className="flex-1 bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white placeholder-zinc-500 text-xs"
                                    />
                                    <input
                                      type="text"
                                      value={newClipTitle}
                                      onChange={e => setNewClipTitle(e.target.value)}
                                      placeholder="Title..."
                                      className="flex-1 bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white placeholder-zinc-500 text-xs"
                                    />
                                    <button
                                      onClick={addClipToSuggestion}
                                      disabled={!newClipUrl.trim() || !newClipTitle.trim()}
                                      className="px-2 py-1 bg-pink-500/20 text-pink-400 rounded text-xs hover:bg-pink-500/30 disabled:opacity-50"
                                    >
                                      <Plus className="w-3 h-3" />
                                    </button>
                                  </div>

                                  {/* Actions */}
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => {
                                        setCreatingClipSuggestion(null);
                                        setClipSuggestionContext('');
                                        setClipSuggestionItems([]);
                                        setNewClipUrl('');
                                        setNewClipTitle('');
                                        setClipSuggestionLinkedQuestion(null);
                                      }}
                                      className="flex-1 px-2 py-1.5 bg-zinc-700 text-zinc-300 rounded text-xs hover:bg-zinc-600"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => sendClipSuggestion()}
                                      disabled={clipSuggestionItems.length === 0}
                                      className="flex-1 px-2 py-1.5 bg-pink-500/20 text-pink-400 rounded text-xs hover:bg-pink-500/30 disabled:opacity-50 flex items-center justify-center gap-1"
                                    >
                                      <Send className="w-3 h-3" /> Send ({clipSuggestionItems.length} clips)
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setCreatingClipSuggestion({ coupleId: couple.coupleId })}
                                  className="w-full flex items-center justify-center gap-2 px-2 py-1.5 bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 rounded text-xs font-medium transition-colors"
                                >
                                  <Plus className="w-3 h-3" /> Send Clips for Rating
                                </button>
                              )}
                            </div>
                          );
                        })()}

                        {/* Stats */}
                        <div className="bg-zinc-800/50 rounded-lg p-2">
                          <div className="flex gap-4 text-xs">
                            <span className="text-zinc-400">
                              Favorites: <span className="text-white">{couple.favorites?.length || 0}</span>
                            </span>
                            <span className="text-zinc-400">
                              Tried: <span className="text-white">{couple.tried?.length || 0}</span>
                            </span>
                            <span className="text-zinc-400">
                              Prefs: <span className="text-white">{Object.keys(couple.preferences || {}).length}</span>
                            </span>
                          </div>
                        </div>

                        {/* Available Days */}
                        {couple.availableDays && couple.availableDays.length > 0 && (
                          <div className="bg-lime-500/10 border border-lime-500/20 rounded-lg p-2">
                            <p className="text-zinc-400 text-xs mb-1">📅 Available Days</p>
                            <div className="flex gap-1">
                              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                                <span
                                  key={day}
                                  className={`px-2 py-1 rounded text-xs ${
                                    couple.availableDays?.includes(i)
                                      ? 'bg-lime-500/30 text-lime-400'
                                      : 'bg-zinc-800 text-zinc-600'
                                  }`}
                                >
                                  {day}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                          </>
                          )}

                        {/* Linked Reviewer Info - shown for all tabs */}
                        {linkedReviewer && (
                          <div className="text-xs text-zinc-500">
                            Reviewer: <span className="font-mono text-zinc-400">{linkedReviewer.code}</span>
                          </div>
                        )}

                        {/* Action Buttons - shown for all tabs */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => setSelectedCoupleSchedule({
                                coupleId: couple.coupleId,
                                partnerName: couple.herName,
                                reviewerId: linkedReviewer?.id
                              })}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 rounded-lg text-sm font-medium transition-colors"
                            >
                              <Calendar className="w-4 h-4" /> Schedule
                            </button>
                            <button
                              onClick={() => duplicateCouple(couple)}
                              className="px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 rounded-lg transition-colors"
                              title="Duplicate couple with new codes"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteCouple(couple.coupleId)}
                              className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg transition-colors"
                              title="Delete couple"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Also show reviewer-only couples that aren't in global storage yet */}
                {reviewers.filter(r => r.coupleId && !allCouples.find(c => c.coupleId === r.coupleId)).map(r => (
                  <div
                    key={r.id}
                    className="bg-zinc-900 rounded-xl border border-zinc-700 overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-zinc-700/50 to-zinc-600/50 p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Heart className="w-5 h-5 text-zinc-400" />
                        <span className="font-mono text-zinc-400 font-semibold">{r.coupleId}</span>
                      </div>
                      <span className="text-xs text-zinc-500">
                        {new Date(r.registeredAt).toLocaleDateString()}
                      </span>
                    </div>

                    <div className="p-4 space-y-3">
                      {/* Reviewer Info */}
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-medium">{r.id}</p>
                          <p className="text-zinc-500 text-sm">
                            {r.gender} &bull; {r.ageRange}
                          </p>
                        </div>
                        {r.partnerName && (
                          <div className="text-right">
                            <p className="text-zinc-400 text-xs">Partner</p>
                            <p className="text-white text-sm">{r.partnerName}</p>
                          </div>
                        )}
                      </div>

                      {/* Note about reviewer-only couple + Fix button */}
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
                        <p className="text-amber-400 text-xs mb-2">
                          Reviewer couple - no DateNite setup yet
                        </p>
                        <button
                          onClick={async () => {
                            if (!r.coupleId) return;
                            // Create the missing couple data
                            const coupleData = {
                              coupleId: r.coupleId,
                              hisName: r.partnerName || 'Partner A',
                              herName: 'Partner B',
                              setupAt: now(),
                              preferences: {},
                              tried: [],
                              favorites: [],
                            };
                            await d1Upsert('couples', coupleData);
                            alert(`Couple data created for ${r.coupleId}. They can now login with this code.`);
                            window.location.reload();
                          }}
                          className="w-full px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded text-xs font-medium transition-colors"
                        >
                          Create DateNite Profile
                        </button>
                      </div>

                      {/* Reviewer Code */}
                      <div className="text-xs text-zinc-500">
                        Code: <span className="font-mono text-zinc-400">{r.code}</span>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedCoupleSchedule({
                            coupleId: r.coupleId!,
                            partnerName: r.partnerName,
                            reviewerId: r.id
                          })}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded-lg text-sm font-medium transition-colors"
                        >
                          <Calendar className="w-4 h-4" /> Schedule
                        </button>
                        <button
                          onClick={() => r.coupleId && deleteCouple(r.coupleId)}
                          className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg transition-colors"
                          title="Delete couple"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Questions Tab - Manage AI Research Questions */}
        {tab === 'questions' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-white">AI Research Question Templates</h3>
                <p className="text-zinc-400 text-sm">Manage the questions that can be sent to reviewers</p>
              </div>
              <button
                onClick={() => {
                  setIsAddingQuestion(true);
                  setEditingQuestion({
                    id: '',
                    label: '',
                    question: '',
                    context: 'Research: ',
                    active: true,
                    createdAt: ''
                  });
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-zinc-900"
                style={{ backgroundColor: COLORS.lime }}
              >
                <Plus className="w-4 h-4" /> Add Question
              </button>
            </div>

            {/* Question List */}
            <div className="space-y-3">
              {researchQuestions.map(q => (
                <div
                  key={q.id}
                  className={`rounded-xl p-4 border ${q.active ? 'bg-purple-500/10 border-purple-500/30' : 'bg-zinc-800/50 border-zinc-700'}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${q.active ? 'bg-purple-500/30 text-purple-300' : 'bg-zinc-700 text-zinc-400'}`}>
                          {q.label}
                        </span>
                        <span className="text-zinc-500 text-xs">{q.context}</span>
                        {!q.active && (
                          <span className="px-2 py-0.5 bg-zinc-700 text-zinc-500 rounded text-xs">Inactive</span>
                        )}
                      </div>
                      <p className="text-white text-sm leading-relaxed">{q.question}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setEditingQuestion(q);
                          setIsAddingQuestion(false);
                        }}
                        className="p-2 hover:bg-blue-500/20 rounded-lg text-zinc-400 hover:text-blue-400 transition-colors"
                        title="Edit question"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          const updated = researchQuestions.map(rq =>
                            rq.id === q.id ? { ...rq, active: !rq.active, updatedAt: now() } : rq
                          );
                          saveResearchQuestions(updated);
                        }}
                        className={`p-2 rounded-lg transition-colors ${q.active ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : 'bg-zinc-700 text-zinc-500 hover:bg-zinc-600'}`}
                        title={q.active ? 'Active - click to deactivate' : 'Inactive - click to activate'}
                      >
                        {q.active ? '✓' : '○'}
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Delete this question template?')) {
                            const updated = researchQuestions.filter(rq => rq.id !== q.id);
                            saveResearchQuestions(updated);
                          }
                        }}
                        className="p-2 hover:bg-red-500/20 rounded-lg text-zinc-400 hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {researchQuestions.length === 0 && (
                <div className="text-center py-12 text-zinc-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No research questions yet</p>
                  <p className="text-sm">Click "Add Question" to create one</p>
                </div>
              )}
            </div>

            {/* Edit/Add Question Modal */}
            {editingQuestion && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4">
                <div className="bg-zinc-800 rounded-2xl w-full max-w-2xl border border-zinc-700 shadow-2xl">
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-4">
                      {isAddingQuestion ? 'Add New Question' : 'Edit Question'}
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-zinc-400 text-sm mb-1">Label (short, for button)</label>
                        <input
                          type="text"
                          value={editingQuestion.label}
                          onChange={e => setEditingQuestion({ ...editingQuestion, label: e.target.value })}
                          placeholder="e.g., Group content (MFM)"
                          className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-4 py-2 text-white"
                        />
                      </div>

                      <div>
                        <label className="block text-zinc-400 text-sm mb-1">Context (shown with question)</label>
                        <input
                          type="text"
                          value={editingQuestion.context}
                          onChange={e => setEditingQuestion({ ...editingQuestion, context: e.target.value })}
                          placeholder="e.g., Research: Group content interest"
                          className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-4 py-2 text-white"
                        />
                      </div>

                      <div>
                        <label className="block text-zinc-400 text-sm mb-1">Question (full text sent to reviewer)</label>
                        <textarea
                          value={editingQuestion.question}
                          onChange={e => setEditingQuestion({ ...editingQuestion, question: e.target.value })}
                          placeholder="Write the full question here..."
                          rows={6}
                          className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-4 py-3 text-white resize-none"
                        />
                        <p className="text-zinc-500 text-xs mt-1">{editingQuestion.question.length} characters</p>
                      </div>

                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingQuestion.active}
                            onChange={e => setEditingQuestion({ ...editingQuestion, active: e.target.checked })}
                            className="w-4 h-4 rounded"
                          />
                          <span className="text-zinc-300 text-sm">Active (available to send)</span>
                        </label>
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => {
                          setEditingQuestion(null);
                          setIsAddingQuestion(false);
                        }}
                        className="flex-1 px-4 py-3 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded-lg text-sm font-medium transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          if (!editingQuestion.label.trim() || !editingQuestion.question.trim()) {
                            alert('Label and question are required');
                            return;
                          }
                          if (isAddingQuestion) {
                            // Add new
                            const newQ: ResearchQuestionTemplate = {
                              ...editingQuestion,
                              id: `rq-${Date.now()}`,
                              createdAt: now()
                            };
                            await saveResearchQuestions([...researchQuestions, newQ]);
                          } else {
                            // Update existing
                            const updated = researchQuestions.map(q =>
                              q.id === editingQuestion.id ? { ...editingQuestion, updatedAt: now() } : q
                            );
                            await saveResearchQuestions(updated);
                          }
                          setEditingQuestion(null);
                          setIsAddingQuestion(false);
                        }}
                        className="flex-1 px-4 py-3 rounded-lg text-sm font-medium text-zinc-900 transition-colors"
                        style={{ backgroundColor: COLORS.lime }}
                      >
                        {isAddingQuestion ? 'Add Question' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Survey Tab - Configure Female Visual Survey */}
        {tab === 'survey' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  💧 Her Pleasure Survey
                </h3>
                <p className="text-zinc-400 text-sm mt-1">
                  Configure clips for each wetness and squirting level. These help women identify where they are and set goals.
                </p>
              </div>
            </div>

            {/* Wetness Levels */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
              <h4 className="text-blue-400 font-semibold mb-4 flex items-center gap-2">
                <Droplets className="w-5 h-5" /> Wetness Levels
              </h4>
              <div className="space-y-3">
                {femaleSurveyConfig.wetness.map((level, idx) => (
                  <div key={level.level} className="bg-zinc-800/50 rounded-lg p-3">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold">
                        {level.level}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={level.label}
                            onChange={e => {
                              setFemaleSurveyConfig({
                                ...femaleSurveyConfig,
                                wetness: femaleSurveyConfig.wetness.map((l, i) =>
                                  i === idx ? { ...l, label: e.target.value } : l
                                )
                              });
                            }}
                            placeholder="Label (e.g., Light moisture)"
                            className="flex-1 bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-sm"
                          />
                        </div>
                        <input
                          type="text"
                          value={level.description}
                          onChange={e => {
                            setFemaleSurveyConfig({
                              ...femaleSurveyConfig,
                              wetness: femaleSurveyConfig.wetness.map((l, i) =>
                                i === idx ? { ...l, description: e.target.value } : l
                              )
                            });
                          }}
                          placeholder="Description"
                          className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-sm"
                        />
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={level.clipUrl || ''}
                            onChange={e => {
                              setFemaleSurveyConfig({
                                ...femaleSurveyConfig,
                                wetness: femaleSurveyConfig.wetness.map((l, i) =>
                                  i === idx ? { ...l, clipUrl: e.target.value || undefined } : l
                                )
                              });
                            }}
                            placeholder="Clip URL (e.g., https://redgifs.com/watch/...)"
                            className="flex-1 bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-sm"
                          />
                          {level.clipUrl && (
                            <a
                              href={level.clipUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-sm hover:bg-blue-500/30"
                            >
                              Preview
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Squirting Levels */}
            <div className="bg-pink-500/10 border border-pink-500/30 rounded-xl p-4">
              <h4 className="text-pink-400 font-semibold mb-4 flex items-center gap-2">
                ✨ Squirting Levels
              </h4>
              <div className="space-y-3">
                {femaleSurveyConfig.squirting.map((level, idx) => (
                  <div key={level.level} className="bg-zinc-800/50 rounded-lg p-3">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center text-pink-400 font-bold">
                        {level.level}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={level.label}
                            onChange={e => {
                              setFemaleSurveyConfig({
                                ...femaleSurveyConfig,
                                squirting: femaleSurveyConfig.squirting.map((l, i) =>
                                  i === idx ? { ...l, label: e.target.value } : l
                                )
                              });
                            }}
                            placeholder="Label (e.g., Small release)"
                            className="flex-1 bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-sm"
                          />
                        </div>
                        <input
                          type="text"
                          value={level.description}
                          onChange={e => {
                            setFemaleSurveyConfig({
                              ...femaleSurveyConfig,
                              squirting: femaleSurveyConfig.squirting.map((l, i) =>
                                i === idx ? { ...l, description: e.target.value } : l
                              )
                            });
                          }}
                          placeholder="Description"
                          className="w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-sm"
                        />
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={level.clipUrl || ''}
                            onChange={e => {
                              setFemaleSurveyConfig({
                                ...femaleSurveyConfig,
                                squirting: femaleSurveyConfig.squirting.map((l, i) =>
                                  i === idx ? { ...l, clipUrl: e.target.value || undefined } : l
                                )
                              });
                            }}
                            placeholder="Clip URL (e.g., https://redgifs.com/watch/...)"
                            className="flex-1 bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-sm"
                          />
                          {level.clipUrl && (
                            <a
                              href={level.clipUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2 py-1 bg-pink-500/20 text-pink-400 rounded text-sm hover:bg-pink-500/30"
                            >
                              Preview
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={() => saveFemaleSurveyConfig(femaleSurveyConfig)}
                className="px-6 py-3 rounded-lg text-sm font-medium text-zinc-900"
                style={{ backgroundColor: COLORS.lime }}
              >
                Save Survey Configuration
              </button>
            </div>
          </div>
        )}

        {/* Edits Tab - Suggested Edit Approval */}
        {tab === 'edits' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                📝 Pending Edit Suggestions
                {suggestedEdits.filter(e => e.status === 'pending').length > 0 && (
                  <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-sm">
                    {suggestedEdits.filter(e => e.status === 'pending').length} pending
                  </span>
                )}
              </h3>
            </div>

            {suggestedEdits.filter(e => e.status === 'pending').length === 0 ? (
              <div className="bg-zinc-800 rounded-xl p-8 text-center">
                <p className="text-zinc-400">No pending edit suggestions</p>
              </div>
            ) : (
              <div className="space-y-4">
                {suggestedEdits.filter(e => e.status === 'pending').map(suggestion => {
                  const clip = dateNiteClips.find(c => c.id === suggestion.clipId);
                  if (!clip) return null;

                  // Check if clip was modified since suggestion (stale detection)
                  const isStale = false; // Simplified - could compare timestamps if we track clip.updatedAt

                  return (
                    <div key={suggestion.id} className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
                      {/* Header */}
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <span className="text-white font-medium">{clip.title}</span>
                          <p className="text-zinc-400 text-sm">
                            Suggested by {suggestion.reviewerName} • {new Date(suggestion.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </p>
                        </div>
                        <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs">pending</span>
                      </div>

                      {/* Side-by-side diff - show all suggested changes */}
                      {(() => {
                        const changes = suggestion.suggestedChanges;
                        const fields = Object.keys(changes).filter(k => changes[k as keyof typeof changes] !== undefined);

                        if (fields.length === 0) {
                          return (
                            <div className="bg-zinc-700/30 border border-zinc-600 rounded-lg p-3 mb-4">
                              <p className="text-zinc-400 text-sm">No changes in this suggestion</p>
                            </div>
                          );
                        }

                        return (
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-zinc-700/50 border border-zinc-600 rounded-lg p-3">
                              <p className="text-zinc-400 text-xs mb-2 font-medium">Current</p>
                              <div className="space-y-2 text-sm">
                                {changes.title !== undefined && (
                                  <p><span className="text-zinc-500">Title:</span> <span className="text-zinc-300">{clip.title}</span></p>
                                )}
                                {changes.description !== undefined && (
                                  <p><span className="text-zinc-500">Description:</span> <span className="text-zinc-300">{clip.description || '(none)'}</span></p>
                                )}
                                {changes.tags !== undefined && (
                                  <p><span className="text-zinc-500">Tags:</span> <span className="text-zinc-300">{(clip.tags || []).join(', ') || '(none)'}</span></p>
                                )}
                                {changes.categoryId !== undefined && (
                                  <p><span className="text-zinc-500">Category:</span> <span className="text-zinc-300">{CATEGORIES.find(c => c.id === clip.categoryId)?.name || clip.categoryId}</span></p>
                                )}
                                {changes.link !== undefined && (
                                  <p><span className="text-zinc-500">Link:</span> <span className="text-zinc-300 text-xs break-all">{clip.link}</span></p>
                                )}
                              </div>
                            </div>
                            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                              <p className="text-green-400 text-xs mb-2 font-medium">Suggested</p>
                              <div className="space-y-2 text-sm">
                                {changes.title !== undefined && (
                                  <p><span className="text-green-600">Title:</span> <span className="text-green-300">{changes.title}</span></p>
                                )}
                                {changes.description !== undefined && (
                                  <p><span className="text-green-600">Description:</span> <span className="text-green-300">{changes.description || '(none)'}</span></p>
                                )}
                                {changes.tags !== undefined && (
                                  <p><span className="text-green-600">Tags:</span> <span className="text-green-300">{(changes.tags || []).join(', ') || '(none)'}</span></p>
                                )}
                                {changes.categoryId !== undefined && (
                                  <p><span className="text-green-600">Category:</span> <span className="text-green-300">{CATEGORIES.find(c => c.id === changes.categoryId)?.name || changes.categoryId}</span></p>
                                )}
                                {changes.link !== undefined && (
                                  <p><span className="text-green-600">Link:</span> <span className="text-green-300 text-xs break-all">{changes.link}</span></p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Reviewer's reason if provided */}
                      {suggestion.reason && (
                        <p className="text-zinc-400 text-sm italic mb-4 bg-zinc-700/30 rounded-lg px-3 py-2">
                          "{suggestion.reason}"
                        </p>
                      )}

                      {/* Stale warning if clip changed */}
                      {isStale && (
                        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-2 mb-4">
                          <p className="text-amber-400 text-sm">
                            ⚠️ Clip has been modified since this suggestion was submitted
                          </p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            // Apply changes to clip
                            const updatedClips = dateNiteClips.map(c => {
                              if (c.id !== suggestion.clipId) return c;
                              return {
                                ...c,
                                ...(suggestion.suggestedChanges.title !== undefined && { title: suggestion.suggestedChanges.title }),
                                ...(suggestion.suggestedChanges.description !== undefined && { description: suggestion.suggestedChanges.description }),
                                ...(suggestion.suggestedChanges.tags !== undefined && { tags: suggestion.suggestedChanges.tags }),
                                ...(suggestion.suggestedChanges.categoryId !== undefined && { categoryId: suggestion.suggestedChanges.categoryId }),
                                ...(suggestion.suggestedChanges.additionalCategoryIds !== undefined && { additionalCategoryIds: suggestion.suggestedChanges.additionalCategoryIds }),
                                ...(suggestion.suggestedChanges.link !== undefined && { link: suggestion.suggestedChanges.link }),
                              };
                            });
                            await saveDateNiteClips(updatedClips);

                            // Update suggestion status
                            const updatedSuggestions = suggestedEdits.map(s =>
                              s.id === suggestion.id
                                ? { ...s, status: 'approved' as const, reviewedAt: now(), reviewedBy: 'admin' }
                                : s
                            );
                            if (saveSuggestedEdits) await saveSuggestedEdits(updatedSuggestions);

                            // Create notification for reviewer (add to reviewerFollowUps with editOutcome)
                            const notification: ReviewerFollowUp = {
                              id: generateId('edn'),
                              reviewerId: suggestion.reviewerId,
                              question: '', // Not a question, just a notification
                              sentAt: now(),
                              status: 'pending',
                              editOutcome: {
                                suggestedEditId: suggestion.id,
                                clipId: suggestion.clipId,
                                clipTitle: clip.title,
                                outcome: 'approved',
                              },
                            };
                            if (saveReviewerFollowUps) await saveReviewerFollowUps([...reviewerFollowUps, notification]);
                          }}
                          className="flex-1 px-4 py-2 rounded-lg font-medium text-sm text-white bg-green-600 hover:bg-green-500 transition-colors"
                        >
                          ✓ Approve
                        </button>
                        <button
                          onClick={() => {
                            setRejectingEdit(suggestion);
                            setRejectionReason('');
                          }}
                          className="flex-1 px-4 py-2 rounded-lg font-medium text-sm text-white bg-red-600 hover:bg-red-500 transition-colors"
                        >
                          ✗ Reject
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* History of reviewed suggestions */}
            {suggestedEdits.filter(e => e.status !== 'pending').length > 0 && (
              <div className="border-t border-zinc-700 pt-6">
                <h4 className="text-zinc-400 text-sm font-medium mb-3">Previously reviewed</h4>
                <div className="space-y-2">
                  {suggestedEdits.filter(e => e.status !== 'pending').slice(0, 10).map(s => {
                    const clip = dateNiteClips.find(c => c.id === s.clipId);
                    return (
                      <div key={s.id} className={`p-3 rounded-lg ${s.status === 'approved' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                        <div className="flex justify-between items-center">
                          <div>
                            <span className={`text-sm ${s.status === 'approved' ? 'text-green-400' : 'text-red-400'}`}>
                              {s.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
                            </span>
                            <span className="text-zinc-400 text-sm ml-2">{clip?.title || s.clipId}</span>
                            <span className="text-zinc-500 text-xs ml-2">by {s.reviewerName}</span>
                          </div>
                          <span className="text-zinc-500 text-xs">
                            {s.reviewedAt && new Date(s.reviewedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                        {s.rejectionReason && (
                          <p className="text-red-400 text-xs mt-1 italic">Reason: {s.rejectionReason}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Submissions Tab */}
        {tab === 'submissions' && (
          <SubmissionsBoard
            submissions={clips}
            creators={creators}
            reviewers={reviewers}
            libraryClips={dateNiteClips}
            saveSubmissions={saveClips}
            saveLibraryClips={saveDateNiteClips}
          />
        )}

        {/* Data Tools Tab */}
        {tab === 'tools' && (
          <div className="space-y-6">
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <h3 className="text-red-400 font-semibold mb-2">Data Cleanup Tools</h3>
              <p className="text-zinc-400 text-sm mb-4">These actions modify live data. Use with care.</p>

              <div className="space-y-3">
                {/* Reset couple chat */}
                <div className="bg-zinc-900 rounded-lg p-4 border border-zinc-700">
                  <h4 className="text-white font-medium mb-1">Reset Couple Chat</h4>
                  <p className="text-zinc-400 text-xs mb-3">Clear all chat messages for a couple so they can re-match with a reviewer.</p>
                  <div className="flex flex-wrap gap-2">
                    {allCouples.map(couple => (
                      <button
                        key={couple.coupleId}
                        onClick={async () => {
                          if (toolConfirm === `reset-chat-${couple.coupleId}`) {
                            // Actually reset
                            await d1DeleteWhere('couple_messages', { coupleId: couple.coupleId });
                            // Also clear managedByReviewerId on the couple
                            const updated = { ...couple, managedByReviewerId: undefined, managedByReviewerName: undefined };
                            await d1Upsert('couples', updated);
                            // Update local state
                            setAllCouples(prev => prev.map(c => c.coupleId === couple.coupleId ? updated : c));
                            // Clear managedCoupleId from any reviewer that managed this couple
                            if (saveReviewers) {
                              const updatedReviewers = reviewers.map(r =>
                                r.managedCoupleId === couple.coupleId
                                  ? { ...r, managedCoupleId: undefined }
                                  : r
                              );
                              await saveReviewers(updatedReviewers);
                            }
                            setToolConfirm(null);
                            setToolMessage(`Chat reset for ${couple.coupleId}. Couple can now match with a new reviewer.`);
                          } else {
                            setToolConfirm(`reset-chat-${couple.coupleId}`);
                          }
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm ${
                          toolConfirm === `reset-chat-${couple.coupleId}`
                            ? 'bg-red-500 text-white'
                            : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                        }`}
                      >
                        {toolConfirm === `reset-chat-${couple.coupleId}`
                          ? `Confirm reset ${couple.coupleId}?`
                          : `${couple.coupleId} (${couple.hisName} & ${couple.herName})`
                        }
                      </button>
                    ))}
                  </div>
                </div>

                {/* Remove all couples except one */}
                <div className="bg-zinc-900 rounded-lg p-4 border border-zinc-700">
                  <h4 className="text-white font-medium mb-1">Remove All Couples Except One</h4>
                  <p className="text-zinc-400 text-xs mb-3">Keep only the selected couple, remove all others from the index and storage.</p>
                  <div className="flex flex-wrap gap-2">
                    {allCouples.map(couple => (
                      <button
                        key={couple.coupleId}
                        onClick={async () => {
                          if (toolConfirm === `keep-only-${couple.coupleId}`) {
                            const toRemove = allCouples.filter(c => c.coupleId !== couple.coupleId);
                            // Remove each couple's data from D1
                            for (const c of toRemove) {
                              await d1Delete('couples', c.coupleId);
                              await d1DeleteWhere('tried_clips', { coupleId: c.coupleId });
                              await d1DeleteWhere('couple_messages', { coupleId: c.coupleId });
                              await d1DeleteWhere('couple_recommendations', { coupleId: c.coupleId });
                              await d1DeleteWhere('scheduled_activities', { coupleId: c.coupleId });
                              await d1DeleteWhere('couple_submissions', { coupleId: c.coupleId });
                              await d1DeleteWhere('couple_requests', { coupleId: c.coupleId });
                              await d1DeleteWhere('wishlist_items', { coupleId: c.coupleId });
                              await d1DeleteWhere('activity_followups', { coupleId: c.coupleId });
                            }
                            // Build new access index from kept couple
                            const newAccessIndex: Record<string, string> = {};
                            if (couple.accessCode) {
                              newAccessIndex[couple.accessCode] = couple.coupleId;
                            }
                            setAllCouples([couple]);
                            setAccessCodeIndex(newAccessIndex);
                            // Clear managedCoupleId from reviewers that managed removed couples
                            if (saveReviewers) {
                              const updatedReviewers = reviewers.map(r => {
                                if (r.managedCoupleId && r.managedCoupleId !== couple.coupleId) {
                                  return { ...r, managedCoupleId: undefined };
                                }
                                return r;
                              });
                              await saveReviewers(updatedReviewers);
                            }
                            setToolConfirm(null);
                            setToolMessage(`Removed ${toRemove.length} couples. Only ${couple.coupleId} remains.`);
                          } else {
                            setToolConfirm(`keep-only-${couple.coupleId}`);
                          }
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm ${
                          toolConfirm === `keep-only-${couple.coupleId}`
                            ? 'bg-red-500 text-white'
                            : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                        }`}
                      >
                        {toolConfirm === `keep-only-${couple.coupleId}`
                          ? `Confirm keep ONLY ${couple.coupleId}?`
                          : `Keep ${couple.coupleId} (${couple.hisName} & ${couple.herName})`
                        }
                      </button>
                    ))}
                  </div>
                </div>

                {/* Remove all reviewers */}
                <div className="bg-zinc-900 rounded-lg p-4 border border-zinc-700">
                  <h4 className="text-white font-medium mb-1">Remove All Reviewers</h4>
                  <p className="text-zinc-400 text-xs mb-3">Removes all reviewer accounts. Submitted clips and their reviews are preserved.</p>
                  {reviewers.length === 0 ? (
                    <p className="text-zinc-500 text-sm">No reviewers to remove.</p>
                  ) : (
                    <button
                      onClick={async () => {
                        if (toolConfirm === 'remove-all-reviewers') {
                          if (saveReviewers) {
                            await saveReviewers([]);
                          }
                          // Clear managedByReviewerId from all couples
                          for (const couple of allCouples) {
                            if (couple.managedByReviewerId) {
                              const updated = { ...couple, managedByReviewerId: undefined, managedByReviewerName: undefined };
                              await d1Upsert('couples', updated);
                              setAllCouples(prev => prev.map(c => c.coupleId === couple.coupleId ? updated : c));
                            }
                          }
                          setToolConfirm(null);
                          setToolMessage(`Removed ${reviewers.length} reviewers. Clips and submissions preserved.`);
                        } else {
                          setToolConfirm('remove-all-reviewers');
                        }
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm ${
                        toolConfirm === 'remove-all-reviewers'
                          ? 'bg-red-500 text-white'
                          : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                      }`}
                    >
                      {toolConfirm === 'remove-all-reviewers'
                        ? `Confirm remove ALL ${reviewers.length} reviewers?`
                        : `Remove all ${reviewers.length} reviewers`
                      }
                    </button>
                  )}
                </div>
              </div>

              {toolMessage && (
                <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-between">
                  <p className="text-green-400 text-sm">{toolMessage}</p>
                  <button onClick={() => setToolMessage(null)} className="text-green-400 hover:text-green-300">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rejection Reason Modal */}
        {rejectingEdit && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-60 p-4">
            <div className="bg-zinc-800 rounded-2xl w-full max-w-md border border-zinc-700 p-6">
              <h3 className="text-lg font-bold text-white mb-4">Reject Edit Suggestion</h3>
              <p className="text-zinc-400 text-sm mb-4">
                Please provide a reason for rejecting this edit. This will be shown to the reviewer.
              </p>
              <textarea
                value={rejectionReason}
                onChange={e => setRejectionReason(e.target.value)}
                placeholder="Why is this edit being rejected?"
                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm resize-none"
                rows={3}
                autoFocus
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => {
                    setRejectingEdit(null);
                    setRejectionReason('');
                  }}
                  className="flex-1 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded-lg text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!rejectionReason.trim()) {
                      alert('Please provide a reason for rejection');
                      return;
                    }
                    const clip = dateNiteClips.find(c => c.id === rejectingEdit.clipId);

                    // Update suggestion status
                    const updatedSuggestions = suggestedEdits.map(s =>
                      s.id === rejectingEdit.id
                        ? { ...s, status: 'rejected' as const, reviewedAt: now(), reviewedBy: 'admin', rejectionReason: rejectionReason.trim() }
                        : s
                    );
                    if (saveSuggestedEdits) await saveSuggestedEdits(updatedSuggestions);

                    // Create notification for reviewer with rejection reason
                    const notification: ReviewerFollowUp = {
                      id: generateId('edn'),
                      reviewerId: rejectingEdit.reviewerId,
                      question: '',
                      sentAt: now(),
                      status: 'pending',
                      editOutcome: {
                        suggestedEditId: rejectingEdit.id,
                        clipId: rejectingEdit.clipId,
                        clipTitle: clip?.title || rejectingEdit.clipId,
                        outcome: 'rejected',
                        rejectionReason: rejectionReason.trim(),
                      },
                    };
                    if (saveReviewerFollowUps) await saveReviewerFollowUps([...reviewerFollowUps, notification]);

                    setRejectingEdit(null);
                    setRejectionReason('');
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium"
                >
                  Reject Edit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Couple Schedule Modal */}
        {selectedCoupleSchedule && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-60 p-4 overflow-auto">
            <div className="bg-zinc-800 rounded-2xl w-full max-w-3xl border border-zinc-700 my-8 max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-zinc-800 border-b border-zinc-700 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSelectedCoupleSchedule(null)}
                    className="p-2 hover:bg-zinc-700 rounded-lg text-zinc-400 hover:text-white"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div>
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Calendar className="w-5 h-5" style={{ color: COLORS.lime }} />
                      Couple Schedule
                    </h3>
                    <p className="text-zinc-400 text-sm">
                      {selectedCoupleSchedule.coupleId}
                      {selectedCoupleSchedule.partnerName && ` (${selectedCoupleSchedule.partnerName})`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedCoupleSchedule(null)}
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-4 space-y-6">
                {/* Upcoming Activities Widget */}
                <UpcomingActivities
                  scheduledActivities={coupleScheduledActivities}
                  clips={dateNiteClips}
                  onViewClip={clip => window.open(clip.link, '_blank')}
                  onMarkComplete={handleAdminMarkComplete}
                  onReschedule={handleAdminRescheduleActivity}
                  onDelete={handleAdminDeleteActivity}
                />

                {/* Calendar View */}
                <CalendarView
                  scheduledActivities={coupleScheduledActivities}
                  clips={dateNiteClips}
                  onDateSelect={handleAdminCalendarDateSelect}
                  onScheduleNew={() => {
                    setSelectedCalendarDate(undefined);
                    setShowScheduleModal(true);
                  }}
                />

                {/* Admin Clip Search for Recommendations */}
                <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Gift className="w-4 h-4" style={{ color: COLORS.lime }} /> Quick Recommend a Clip
                  </h4>

                  {/* Search Input */}
                  <div className="flex gap-2 mb-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <input
                        type="text"
                        value={clipSearchTerm}
                        onChange={e => setClipSearchTerm(e.target.value)}
                        placeholder="Search clips..."
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white text-sm placeholder-zinc-400"
                      />
                    </div>
                    <select
                      value={clipCategoryFilter}
                      onChange={e => setClipCategoryFilter(e.target.value)}
                      className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                    >
                      <option value="all">All</option>
                      {CATEGORIES.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.emoji} {cat.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Search Results */}
                  {clipSearchTerm.trim() && (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {searchedClips.slice(0, 8).map(clip => {
                        const category = CATEGORIES.find(c => c.id === clip.categoryId);
                        return (
                          <div
                            key={clip.id}
                            className="flex items-center justify-between bg-zinc-800 rounded-lg p-2"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{category?.emoji}</span>
                              <span className="text-white text-sm">{clip.title}</span>
                            </div>
                            <button
                              onClick={async () => {
                                // Quick recommend from admin
                                const newRec: CoupleRecommendation = {
                                  id: generateId('rec'),
                                  coupleId: selectedCoupleSchedule.coupleId,
                                  clipId: clip.id,
                                  fromName: 'Admin',
                                  message: '',
                                  createdAt: now(),
                                  seen: false,
                                };
                                await d1Upsert('couple_recommendations', newRec);
                              }}
                              className="px-2 py-1 bg-pink-500/20 text-pink-400 border border-pink-500/30 rounded text-xs hover:bg-pink-500/30"
                            >
                              Recommend
                            </button>
                          </div>
                        );
                      })}
                      {searchedClips.length === 0 && (
                        <p className="text-zinc-500 text-center py-2 text-sm">No clips match</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Follow-up Question Confirmation Modal */}
        {pendingFollowUp && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4">
            <div className="bg-zinc-800 rounded-2xl w-full max-w-xl border border-zinc-700 shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 overflow-y-auto flex-1">
                <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                  <Send className="w-5 h-5 text-blue-400" />
                  {pendingFollowUp.questions.length === 1 ? 'Send Follow-up Question?' : `Send ${pendingFollowUp.questions.length} Questions (Queue)`}
                </h3>
                <p className="text-zinc-400 text-sm mb-4">
                  {pendingFollowUp.questions.length === 1 ? (
                    <>This will send the following question to <span className="text-white font-medium">{pendingFollowUp.reviewerName}</span>:</>
                  ) : (
                    <>These questions will be sent to <span className="text-white font-medium">{pendingFollowUp.reviewerName}</span> one at a time. After they answer the first, the next appears automatically.</>
                  )}
                </p>

                {/* Question(s) preview */}
                <div className="space-y-3 mb-4">
                  {pendingFollowUp.questions.map((q, idx) => (
                    <div key={idx} className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 relative group">
                      {/* Remove button */}
                      <button
                        onClick={() => removeFromQueue(idx)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove from queue"
                      >
                        <X className="w-3 h-3" />
                      </button>

                      {/* Order badge for sequences */}
                      {pendingFollowUp.questions.length > 1 && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            idx === 0
                              ? 'bg-blue-500 text-white'
                              : 'bg-zinc-700 text-zinc-400'
                          }`}>
                            {idx === 0 ? '1st (sends now)' : `${idx + 1}${idx === 1 ? 'nd' : idx === 2 ? 'rd' : 'th'} (queued)`}
                          </span>
                          {q.context && (
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">
                              {q.context}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Context for single question */}
                      {pendingFollowUp.questions.length === 1 && q.context && (
                        <div className="mb-2">
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">
                            {q.context}
                          </span>
                        </div>
                      )}

                      <textarea
                        value={q.question}
                        onChange={(e) => {
                          setPendingFollowUp(prev => {
                            if (!prev) return null;
                            const newQuestions = [...prev.questions];
                            newQuestions[idx] = { ...newQuestions[idx], question: e.target.value };
                            return { ...prev, questions: newQuestions };
                          });
                        }}
                        className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm leading-relaxed resize-none focus:border-blue-500 focus:outline-none"
                        rows={Math.max(3, q.question.split('\n').length + 1)}
                      />
                    </div>
                  ))}
                </div>

                {/* Tip for adding more */}
                {pendingFollowUp.questions.length === 1 && (
                  <p className="text-zinc-500 text-xs mb-4 flex items-center gap-1">
                    💡 Edit the question above, or click more follow-up buttons to add to queue
                  </p>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setPendingFollowUp(null)}
                    className="flex-1 px-4 py-3 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded-lg text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmAndSendFollowUp}
                    className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    {pendingFollowUp.questions.length === 1
                      ? `Send to ${pendingFollowUp.reviewerName}`
                      : `Send Queue (${pendingFollowUp.questions.length})`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Couple Follow-up Question Confirmation Modal */}
        {pendingCoupleFollowUp && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4">
            <div className="bg-zinc-800 rounded-2xl w-full max-w-xl border border-zinc-700 shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 overflow-y-auto flex-1">
                <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                  <Send className="w-5 h-5 text-amber-400" />
                  {pendingCoupleFollowUp.questions.length === 1 ? 'Send Follow-up Question?' : `Send ${pendingCoupleFollowUp.questions.length} Questions (Queue)`}
                </h3>
                <p className="text-zinc-400 text-sm mb-4">
                  {pendingCoupleFollowUp.questions.length === 1 ? (
                    <>This will send the following question to <span className="text-white font-medium">{pendingCoupleFollowUp.coupleName}</span>:</>
                  ) : (
                    <>These questions will be sent to <span className="text-white font-medium">{pendingCoupleFollowUp.coupleName}</span> one at a time. After they answer the first, the next appears automatically.</>
                  )}
                </p>

                {/* Question(s) preview with edit */}
                <div className="space-y-3 mb-4">
                  {pendingCoupleFollowUp.questions.map((q, idx) => (
                    <div key={idx} className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 relative group">
                      {/* Remove button */}
                      <button
                        onClick={() => removePendingCoupleQuestion(idx)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove from queue"
                      >
                        ×
                      </button>

                      {/* Order badge for sequences */}
                      {pendingCoupleFollowUp.questions.length > 1 && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            idx === 0
                              ? 'bg-amber-500/20 text-amber-400'
                              : 'bg-zinc-700 text-zinc-400'
                          }`}>
                            {idx === 0 ? '1st - Sent Now' : `${idx + 1}${idx === 1 ? 'nd' : idx === 2 ? 'rd' : 'th'} - Queued`}
                          </span>
                          {q.context && (
                            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">
                              {q.context}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Context for single question */}
                      {pendingCoupleFollowUp.questions.length === 1 && q.context && (
                        <div className="mb-2">
                          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">
                            {q.context}
                          </span>
                        </div>
                      )}

                      {/* Editable question text */}
                      <textarea
                        value={q.question}
                        onChange={(e) => {
                          setPendingCoupleFollowUp(prev => {
                            if (!prev) return null;
                            const newQuestions = [...prev.questions];
                            newQuestions[idx] = { ...newQuestions[idx], question: e.target.value };
                            return { ...prev, questions: newQuestions };
                          });
                        }}
                        className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm leading-relaxed resize-none focus:border-amber-500 focus:outline-none"
                        rows={Math.max(3, q.question.split('\n').length + 1)}
                      />
                    </div>
                  ))}
                </div>

                {/* Tip for adding more */}
                {pendingCoupleFollowUp.questions.length === 1 && (
                  <p className="text-zinc-500 text-xs mb-4 flex items-center gap-1">
                    💡 Edit the question above, or click more follow-up buttons to add to queue
                  </p>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setPendingCoupleFollowUp(null)}
                    className="flex-1 px-4 py-3 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded-lg text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmAndSendCoupleFollowUp}
                    className="flex-1 px-4 py-3 bg-amber-500 hover:bg-amber-600 text-zinc-900 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    {pendingCoupleFollowUp.questions.length === 1
                      ? `Send to ${pendingCoupleFollowUp.coupleName}`
                      : `Send Queue (${pendingCoupleFollowUp.questions.length})`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Admin Schedule Activity Modal */}
        {selectedCoupleSchedule && (
          <ScheduleActivityModal
            isOpen={showScheduleModal}
            onClose={() => {
              setShowScheduleModal(false);
              setSelectedCalendarDate(undefined);
              setRescheduleActivityId(null);
            }}
            clips={dateNiteClips}
            initialDate={selectedCalendarDate}
            onSchedule={handleAdminScheduleActivity}
            context={getAdminContext()}
          />
        )}

        {/* Edit Clip Modal */}
        {editingClip && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4">
            <div className="bg-zinc-800 rounded-2xl w-full max-w-lg border border-zinc-700 shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <Edit2 className="w-5 h-5 text-blue-400" />
                    Edit Clip
                  </h3>
                  <button onClick={() => setEditingClip(null)} className="text-zinc-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <label className="block text-zinc-400 text-sm mb-2">Title</label>
                    <input
                      type="text"
                      value={editClipForm.title}
                      onChange={e => setEditClipForm({ ...editClipForm, title: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-zinc-400 text-sm mb-2">Description</label>
                    <textarea
                      value={editClipForm.description}
                      onChange={e => setEditClipForm({ ...editClipForm, description: e.target.value })}
                      rows={2}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white resize-none"
                    />
                  </div>

                  {/* Link */}
                  <div>
                    <label className="block text-zinc-400 text-sm mb-2">Clip Link</label>
                    <input
                      type="text"
                      value={editClipForm.link}
                      onChange={e => setEditClipForm({ ...editClipForm, link: e.target.value })}
                      placeholder="https://redgifs.com/watch/..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    />
                  </div>

                  {/* Categories */}
                  <div>
                    <label className="block text-zinc-400 text-sm mb-2">
                      Categories <span className="text-zinc-500">(first is primary)</span>
                    </label>
                    <div className="flex flex-wrap gap-2 p-3 bg-zinc-900 rounded-xl border border-zinc-700">
                      {CATEGORIES.map(cat => {
                        const isSelected = editClipForm.categoryIds.includes(cat.id);
                        const isPrimary = editClipForm.categoryIds[0] === cat.id;
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                if (editClipForm.categoryIds.length > 1) {
                                  setEditClipForm({
                                    ...editClipForm,
                                    categoryIds: editClipForm.categoryIds.filter(id => id !== cat.id)
                                  });
                                }
                              } else {
                                setEditClipForm({
                                  ...editClipForm,
                                  categoryIds: [...editClipForm.categoryIds, cat.id]
                                });
                              }
                            }}
                            className={`px-2 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                              isSelected
                                ? isPrimary
                                  ? 'bg-emerald-500 text-white ring-2 ring-emerald-300'
                                  : 'bg-blue-500/30 text-blue-300 ring-1 ring-blue-400'
                                : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                            }`}
                          >
                            <span>{cat.emoji}</span>
                            <span>{cat.name}</span>
                            {isPrimary && <span className="text-[10px] bg-emerald-600 px-1 rounded">1st</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-zinc-400 text-sm mb-2">Tags</label>
                    <div className="flex flex-wrap gap-2 p-3 bg-zinc-900 rounded-xl border border-zinc-700">
                      {TAGS.map(tag => {
                        const isSelected = editClipForm.tags.includes(tag.id);
                        return (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setEditClipForm({
                                  ...editClipForm,
                                  tags: editClipForm.tags.filter(t => t !== tag.id)
                                });
                              } else {
                                setEditClipForm({
                                  ...editClipForm,
                                  tags: [...editClipForm.tags, tag.id]
                                });
                              }
                            }}
                            className={`px-2 py-1 rounded-lg text-xs font-medium transition-all border ${
                              isSelected
                                ? tag.color
                                : 'bg-zinc-700 text-zinc-400 border-zinc-600 hover:bg-zinc-600'
                            }`}
                          >
                            {tag.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setEditingClip(null)}
                      className="flex-1 px-4 py-3 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded-lg text-sm font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveEditClip}
                      disabled={editClipForm.categoryIds.length === 0}
                      className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-zinc-600 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Floating Clip Suggestion Modal - appears when triggered from anywhere */}
        {creatingClipSuggestion && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
            <div className="bg-zinc-800 rounded-2xl p-6 w-full max-w-lg border border-pink-500/30 shadow-xl max-h-[80vh] overflow-y-auto">
              <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                <Film className="w-5 h-5 text-pink-400" />
                {editingClipSuggestionId
                  ? (creatingClipSuggestion.reviewerId ? 'Edit Clips for Review' : 'Edit Clips for Rating')
                  : (creatingClipSuggestion.reviewerId ? 'Send Clips for Review' : 'Send Clips for Rating')
                }
              </h3>
              <p className="text-zinc-400 text-sm mb-4">
                {editingClipSuggestionId
                  ? "Update the context or clips already sent. Existing ratings and comments are kept where possible."
                  : creatingClipSuggestion.reviewerId
                    ? "Add clips for the reviewer to review. They'll see these in their inbox."
                    : "Add clips for the couple to rate. They'll see these in their inbox."
                }
              </p>

              <div className="space-y-4">
                {/* Context */}
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Context (shown to them)</label>
                  <textarea
                    value={clipSuggestionContext}
                    onChange={e => setClipSuggestionContext(e.target.value)}
                    placeholder="e.g., Following up on your interest in MFM..."
                    className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white placeholder-zinc-500 text-sm resize-none h-16"
                  />
                </div>

                {/* Added clips */}
                {clipSuggestionItems.length > 0 && (
                  <div>
                    <label className="text-xs text-zinc-500 block mb-2">Clips to send ({clipSuggestionItems.length})</label>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {clipSuggestionItems.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-zinc-900/50 rounded-lg p-2">
                          <span className="text-pink-400 font-bold">{idx + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm truncate">{item.title}</p>
                            <p className="text-zinc-500 text-xs truncate">{item.url}</p>
                          </div>
                          <button
                            onClick={() => removeClipFromSuggestion(idx)}
                            className="p-1 text-red-400/50 hover:text-red-400 hover:bg-red-500/20 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add new clip */}
                <div className="bg-zinc-700/50 rounded-lg p-3">
                  <label className="text-xs text-zinc-400 block mb-2">Add a clip</label>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newClipUrl}
                      onChange={e => setNewClipUrl(e.target.value)}
                      placeholder="Redgif URL (e.g., https://redgifs.com/watch/...)"
                      className="w-full bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white placeholder-zinc-500 text-sm"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newClipTitle}
                        onChange={e => setNewClipTitle(e.target.value)}
                        placeholder="Title (e.g., MFM example 1)"
                        className="flex-1 bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white placeholder-zinc-500 text-sm"
                      />
                      <button
                        onClick={addClipToSuggestion}
                        disabled={!newClipUrl.trim() || !newClipTitle.trim()}
                        className="px-4 py-2 bg-pink-500/20 text-pink-400 rounded text-sm hover:bg-pink-500/30 disabled:opacity-50 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" /> Add
                      </button>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={resetClipSuggestionForm}
                    className="flex-1 px-4 py-3 bg-zinc-700 text-zinc-300 rounded-lg text-sm font-medium hover:bg-zinc-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => sendClipSuggestion()}
                    disabled={clipSuggestionItems.length === 0}
                    className="flex-1 px-4 py-3 bg-pink-500 hover:bg-pink-600 disabled:bg-zinc-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {editingClipSuggestionId ? (
                      <>
                        <Check className="w-4 h-4" /> Save Changes
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" /> Send {clipSuggestionItems.length > 0 && `(${clipSuggestionItems.length} clips)`}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Duplicate Couple Result Modal */}
        {duplicatedCoupleResult && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4">
            <div className="bg-zinc-800 rounded-2xl w-full max-w-md border border-zinc-700 shadow-2xl">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Couple Duplicated!</h3>
                    <p className="text-zinc-400 text-sm">{duplicatedCoupleResult.hisName} & {duplicatedCoupleResult.herName}</p>
                  </div>
                </div>

                <p className="text-zinc-400 text-sm mb-4">
                  A new couple profile has been created with fresh codes. The reviewer link has been removed.
                </p>

                {/* New Codes */}
                <div className="space-y-3 mb-6">
                  <div className="bg-zinc-900 rounded-lg p-3 border border-zinc-700">
                    <p className="text-zinc-500 text-xs mb-1">Couple Code (shareable)</p>
                    <div className="flex items-center justify-between">
                      <code className="text-lime-400 font-mono text-lg">{duplicatedCoupleResult.coupleId}</code>
                      <button
                        onClick={() => navigator.clipboard.writeText(duplicatedCoupleResult.coupleId)}
                        className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded text-xs flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                  </div>
                  <div className="bg-zinc-900 rounded-lg p-3 border border-zinc-700">
                    <p className="text-zinc-500 text-xs mb-1">Access Code (private login)</p>
                    <div className="flex items-center justify-between">
                      <code className="text-pink-400 font-mono text-lg">{duplicatedCoupleResult.accessCode}</code>
                      <button
                        onClick={() => navigator.clipboard.writeText(duplicatedCoupleResult.accessCode)}
                        className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded text-xs flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setDuplicatedCoupleResult(null)}
                  className="w-full px-4 py-3 bg-lime-500 hover:bg-lime-600 text-zinc-900 rounded-lg font-medium transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Consolidated Q&A Slide-over */}
        {consolidatedQAReviewerId && (() => {
          const reviewer = reviewers.find(r => r.id === consolidatedQAReviewerId);
          if (!reviewer) return null;

          // Build CoupleTabData for managed couple (if exists)
          const managedCoupleData = reviewer.managedCoupleId ? allCouples.find(c => c.coupleId === reviewer.managedCoupleId) : null;
          const managedCouple: CoupleTabData | null = managedCoupleData ? {
            coupleData: managedCoupleData,
            coupleFollowUps: coupleFollowUps.filter(fu => fu.coupleId === managedCoupleData.coupleId),
            coupleQuestions: coupleQuestions.filter(q => q.coupleId === managedCoupleData.coupleId),
            clipSuggestions: clipSuggestions.filter(cs => cs.coupleId === managedCoupleData.coupleId && (cs.reviewerId === reviewer.id || cs.reviewerId === reviewer.code)),
          } : null;

          // Build CoupleTabData for own couple (if exists)
          const ownCoupleData = reviewer.coupleId ? allCouples.find(c => c.coupleId === reviewer.coupleId) : null;
          const ownCouple: CoupleTabData | null = ownCoupleData ? {
            coupleData: ownCoupleData,
            coupleFollowUps: coupleFollowUps.filter(fu => fu.coupleId === ownCoupleData.coupleId),
            coupleQuestions: coupleQuestions.filter(q => q.coupleId === ownCoupleData.coupleId),
            clipSuggestions: clipSuggestions.filter(cs => cs.coupleId === ownCoupleData.coupleId && (cs.reviewerId === reviewer.id || cs.reviewerId === reviewer.code)),
          } : null;

          const viewData: ConsolidatedQAViewData = {
            reviewer,
            reviewerFollowUps: reviewerFollowUps.filter(fu => fu.reviewerId === reviewer.id),
            clips: dateNiteClips,
            managedCouple,
            ownCouple,
          };

          // Callback: send reviewer follow-up (new question or reply)
          const handleSendReviewerFollowUp = async (question: string, parentId?: string) => {
            if (!saveReviewerFollowUps || !question.trim()) return;
            const followUp: ReviewerFollowUp = {
              id: generateId('rfu'),
              reviewerId: reviewer.id,
              question: question.trim(),
              sentAt: new Date().toISOString(),
              status: 'pending',
              parentId,
            };
            await saveReviewerFollowUps([...reviewerFollowUps, followUp]);
          };

          // Callback: send couple follow-up (new question or threaded reply)
          const handleSendCoupleFollowUp = async (coupleId: string, question: string, parentId?: string) => {
            if (!saveCoupleFollowUps || !question.trim()) return;
            const followUp: CoupleFollowUp = {
              id: generateId('cfu'),
              coupleId,
              question: question.trim(),
              sentAt: new Date().toISOString(),
              status: 'pending',
              parentId,
            };
            await saveCoupleFollowUps([...coupleFollowUps, followUp]);
          };

          // Callback: send couple question reply (threaded)
          const handleSendCoupleQuestionReply = async (coupleId: string, parentId: string, replyText: string) => {
            if (!saveCoupleQuestions || !replyText.trim()) return;
            const reply: CoupleQuestion = {
              id: generateId('cq'),
              coupleId,
              fromReviewerId: 'admin',
              fromReviewerName: 'Admin',
              question: replyText.trim(),
              sentAt: now(),
              status: 'pending',
              parentId,
            };
            await saveCoupleQuestions([...coupleQuestions, reply]);
          };

          // Callback: send clip suggestion
          const handleSendClipSuggestion = async (coupleId: string, reviewerId: string, context: string, clips: { url: string; title: string }[]) => {
            if (!saveClipSuggestions || clips.length === 0) return;
            const request: ClipSuggestionRequest = {
              id: generateId('csr'),
              coupleId,
              reviewerId,
              context,
              clips: clips.map((item, i) => ({
                id: `${generateId('csi')}-${i}`,
                url: item.url,
                title: item.title,
              })),
              sentAt: now(),
              status: 'pending',
            };
            await saveClipSuggestions([...clipSuggestions, request]);
          };

          return (
            <ConsolidatedQAView
              data={viewData}
              onClose={() => setConsolidatedQAReviewerId(null)}
              onSendReviewerFollowUp={handleSendReviewerFollowUp}
              onSendCoupleFollowUp={handleSendCoupleFollowUp}
              onSendCoupleQuestionReply={handleSendCoupleQuestionReply}
              onSendClipSuggestion={handleSendClipSuggestion}
            />
          );
        })()}

        {/* Clip Preview Modal */}
        {previewClip && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4" onClick={() => setPreviewClip(null)}>
            <div className="bg-zinc-800 rounded-2xl w-full max-w-3xl border border-zinc-700 overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-zinc-700">
                <h3 className="text-white font-semibold truncate">{previewClip.title}</h3>
                <button onClick={() => setPreviewClip(null)} className="text-zinc-400 hover:text-white flex-shrink-0 ml-3">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4">
                <VideoPlayer url={previewClip.link} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
